import { portalZH } from '../portal/zh';
import { privacyPolicyBodyZh } from '@/shared/i18n/locales/privacy-policy-bodies/zh';
import { restaurantOrdersZh } from '@/shared/i18n/locales/restaurant-orders/zh';
import type { TranslationSchema } from '@/shared/i18n/types';

const paymentPolicyTemplate = `付款、银行卡与取消政策

1. 商户信息
预订及相应付款由 [公司名称] 处理，地址为 [地址]，联系方式为 [邮箱/电话]。Stripe 仅作为支付服务提供方。

2. 银行卡验证
仅需验证时，不会扣款也不会冻结任何金额。系统通过 Stripe 验证银行卡是否有效。

3. 预先付款
需要立即付款时，确认前显示的总金额将在预订时扣款，并记为 [定金/全额付款]。

4. 预授权
设置预授权时，显示的金额会被临时冻结。商户可在本政策所述情形下扣取该金额。若未扣取，解冻时间取决于客户的开户行。

5. 取消与修改
在 [期限] 前可免费取消。超过该期限，商户可保留或扣取 [金额/百分比]。修改需视可用情况而定。

6. 未到店与迟到
客户未到店或迟到超过 [分钟] 时，商户可保留或扣取 [金额/百分比]。

7. 退款
退款将在 [天数] 天内退回至原支付方式；最终到账时间取决于卡组织与银行。

8. 银行卡的保存与后续使用
Stripe 可以令牌化形式保存银行卡，且仅在客户就所述扣款给予明确同意时才可再次使用。PRO20 与商户均不存储完整卡号。

9. 争议与支持
如需咨询、取消或提出异议，请通过 [邮箱/电话] 联系我们，并提供预订编号。

版本与效力
所接受的版本、金额、付款方式及接受日期将与预订一并记录。`;

export const zh: TranslationSchema = {
  collaboratorRoles: {
    inherited:
      'This role is not configured yet and retains its current access. Save to define explicit permissions.',
    title: 'App permissions',
    description:
      'Choose which app operations each collaborator role can perform. Portal access is unchanged.',
    role: 'Collaborator role',
    enabled: 'enabled',
    wholeCategory: 'Entire category',
    search: 'Search operations',
    saved: 'Permissions saved',
    saveError:
      'Could not save. Reload the role to check for changes by another manager.',
    versionError: 'The permission catalog has changed. Reload before saving.',
    unsaved: 'Unsaved changes',
    appOnly: 'These permissions apply to the app only.',
    reset: 'Discard changes',
    save: 'Save permissions',
    discard: 'Discard unsaved changes and load the selected role?',
    selectBranch: 'Select a branch to configure permissions.',
    loading: 'Loading permissions…',
    unavailable:
      'Role permissions could not be loaded. Check your access and that the service supports this configuration. No permission changes have been applied.',
    retry: 'Retry',
  },

  portal: portalZH,
  restaurantOrders: restaurantOrdersZh,
  giftCards: {
    openRecap: '打开概览',
    recapError: '无法获取礼品卡专属链接。Wallet 服务可能尚不可用。',

    select: '选择',
    card: '卡',
    invalidDates: '请选择有效的日期范围。',

    title: '礼品卡',
    description: '已发行的卡、二维码和余额',
    create: '创建礼品卡',
    search: '搜索客户或卡号…',
    empty: '未找到礼品卡',
    loadError: '无法加载礼品卡。',
    noBranch: '请选择门店。',
    customer: '客户',
    amount: '金额 (€)',
    chooseCustomer: '从列表中选择客户',
    selected: '已选客户',
    createSuccess: '礼品卡已创建。',
    createError: '无法创建礼品卡。',
    amountError: '请输入大于零且最多两位小数的金额。',
    details: '礼品卡详情',
    initial: '初始金额',
    balance: '余额',
    status: '状态',
    created: '创建日期',
    expires: '到期日期',
    operations: '交易记录',
    operation: '交易',
    note: '备注',
    noOperations: '暂无交易记录。',
    email: '发送邮件',
    emailSuccess: '已请求发送邮件。',
    emailError: '无法请求发送邮件。',
    active: '有效',
    used: '已使用',
    expired: '已过期',
    cancelled: '已取消',
    allStates: '所有状态',
    from: '从',
    to: '至',
    dateRange: '日期范围',
    clearDateRange: '清除日期范围',
    requestPending: '保留相同的客户和金额以重试，避免重复发卡。',
  },
  common: {
    retry: '重试',
    loading: '加载中',
    save: '保存',
    delete: '是，删除',
    deleting: '删除中...',
    createLoading: '创建中...',
    cancel: '取消',
    yes: '是',
    no: '否',
    back: '返回',
    search: '搜索',
    noData: '暂无数据',
    noDataDesc: '没有符合所选筛选条件的数据。',
    previous: '上一页',
    next: '下一页',
    actions: '操作',
    serialNumber: 'Sr No.',
    itemsPerPage: '每页条数',
    range: '第 {{start}} - {{end}} 条，共 {{total}} 条',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: '网址已复制到剪贴板。',
    },
  },
  nav: {
    collaboratorRoles: 'Collaborator roles',
    deliverySection: 'Delivery',
    drivers: '配送员',
    orders: '订单',
    delivery: 'Delivery',
    deliveryTakeaway: '外送 / 自取',
    orderingConfiguration: '配置',
    takeaway: '自取',
    operations: '订单',
    suppliers: 'Suppliers and catalogs',
    supplierOrderHistory: 'Order history',
    supplierOrders: 'Supplier orders',
    ingredients: '食材',
    menuList: '您的菜单',
    home: '首页',
    menu: '菜单',
    settingsGroup: '设置',
    dashboard: '仪表板',
    calendar: '日历',
    calendarDesc: '预订和表单',
    marketing: '营销',
    marketingDesc: '消息和促销',
    menuItem: '菜单',
    staff: '员工',
    settings: '配置',
    payments: '付款',
    settingsDesc: '常规设置',
    users: '用户',
    reports: '报告',
    notifications: '通知',
    bookingHistory: '预订',
    forms: '预订表单',
    formHistory: 'Booking',
    formMenu: '菜单',
    others: '其他',
    extraServiceAndProduct: '附加服务',
    events: '活动',
    bulkMessage: '群发消息',
    surveys: 'Questionnaires',
    automationConfiguration: '配置',
    automationDashboard: '成效仪表板',
    automation: 'Automation',
    dataCollectionForm: '表单数据收集',
    promotion: '促销活动',
    fidelity: '会员忠诚度',
    discountCards: 'Discount cards',
    cards: 'Cards',
    experienceCards: 'Experience',
    prizes: '奖品',
    loyalty: '客户忠诚度',
    landingPage: '落地页',
    tag: '标签',
    publicRelation: '公共关系',
    collaborator: '协作者',
    collaboratorReport: '协作者报告',
    myCustomers: '我的客户',
    localConfiguration: '本地',
    stripeConfiguration: 'Stripe 配置',
    stripeTransactions: 'Stripe 交易',
    configureWhatsApp: '非官方 WhatsApp',
    messages: '消息',
    officialWhatsAppChannel: '官方 WhatsApp 频道',
    templates: '模板',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: '配置',
    whatsAppOfficialTemplates: '模板',
    whatsAppOfficialInbox: '收件箱',
  },
  header: {
    fullscreen: '全屏',
    language: '语言',
    theme: '主题',
    lightMode: '浅色',
    darkMode: '深色',
    profile: '个人资料',
    signOut: '退出登录',
    userCode: '用户代码',
    branchList: '分店列表',
    searchBranches: '搜索分店...',
    noBranchesFound: '未找到分店',
    switchingBranch: '正在切换分店…',
  },
  dashboard: {
    aiCredits: 'AI 积分',
    smsAvailable: '剩余短信',
    smsLoading: '正在加载短信余额…',
    smsError: '短信余额错误',
    smsErrorHint: '短信余额不可用，请重试。',
    smsRefresh: '刷新短信余额',
    smsEmpty: '短信已用完',
    title: '仪表板',
    subtitle: '以下是您业务的最新动态。',
    headerMeta: '{{dateRange}} · {{days}} 天表现窗口',
    customRange: '自定义',
    customRangePlaceholder: '选择日期范围',
    refresh: '刷新',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: '到店',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: '无法加载此板块。',
    bookingsFlow: 'Bookings flow',
    forms: '表单',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: '待处理',
    waitingActionSubtitle: '处于 IN_ATTESA 状态、待处理的预订。',
    waitingBadge: '等待中',
    people: '人',
    openBooking: '打开预订',
    copyContact: '复制联系方式',
    copySuccess: '已复制到剪贴板。',
    emptyWaiting: '没有待处理的预订。',
    emptyChart: '该时间段暂无数据。',
    emptyCustomers: '暂无客户可显示。',
    emptyForms: '暂无表单可显示。',
    guests: '客人',
    children: '儿童',
    accept: '接受',
    reject: '拒绝',
    acceptSuccess: '预订已接受。',
    rejectSuccess: '预订已拒绝。',
    actionError: '无法更新预订状态。',
    busiestSlots: '最忙时段',
    busiestSlotsSubtitle: '服务最密集的时段。',
    customerMix: '客户构成',
    customerRisk: '客户风险',
    customerRiskSubtitle: '未到店与拒绝的集中情况。',
    bestCustomers: '最佳客户',
    bestCustomersSubtitle: '高价值回头客。',
    lastBooking: '最近预订',
    messageDelivery: '消息送达',
    messageDeliverySubtitle: '自动通信健康状况。',
    dailyOperations: '每日运营',
    dailyOperationsSubtitle: '每日预订与客流负载。',
    bookingsFlowSubtitle: '所选时间段内的每日预订。',
    formsSubtitle: '预订来源。',
    statusDistributionSubtitle: '预订的最终状态。',
    customerMixSubtitle: '本时间段内的客户构成。',
    pulse: {
      today: '今日',
      upcoming: '即将到来',
      pending: '待处理',
      waitList: '候补名单',
      refusalRate: '拒绝率',
      google: 'Google 预订',
      googleOn: '已启用',
      googleOff: '未启用',
      googleMissing: '缺少 {{count}} 项要求',
    },
    waitingTabs: {
      all: '全部',
      today: '今天',
      future: '未来',
      large: '大桌',
    },
    flow: {
      volume: '数量',
      people: '人数',
      outcome: '结果',
    },
    kpis: {
      reservations: '预订',
      reservationsHint: '{{count}} 人',
      arrivalRate: '到店率',
      customers: '客户',
      arrivedOfTotal: '{{total}} 中 {{arrived}} 已到店',
      customersHint: '{{active}} 活跃 · {{new}} 新增',
      messageErrors: '{{count}} 条发送错误',
      noShowRate: '未到店率',
      arrivedVsNoShow: '{{arrived}} 到店 · {{noShow}} 未到店',
      peakDay: '高峰日',
      peakSlot: '高峰时段',
      dailyAverage: '日均',
      dailyAverageHint: '每日预订数',
      messagesWaiting: '待发送消息',
      scheduledTotal: '共 {{count}} 条定时消息',
      bookingsGuests: '{{bookings}} 预订，{{guests}} 客人',
      slotStats: '{{bookings}} 预订 · {{arrived}} 到店 ({{rate}}%)',
      totalBookings: '总预订数',
      activeUsers: '活跃用户',
      newUsers: '新用户',
      peakDate: '高峰日期',
      managedBookings: '已管理预订',
    },
    series: {
      bookings: '预订',
      confirmed: '已确认',
      pending: '待处理',
      refused: '已拒绝',
      noShow: '未到店',
      risk: '风险',
    },
    mix: {
      active: '活跃',
      new: '新增',
      marketingOptIn: '营销订阅',
      blacklisted: '黑名单',
      ofBase: '占总数 {{percent}}%',
      total: '总计',
    },
    comm: {
      bookingNotifications: '预订通知',
      scheduledMessages: '定时消息',
      successRate: '成功率',
      total: '总计',
      processed: '已处理',
      waiting: '等待中',
      errors: '错误',
      centralino: '中控状态',
      centralinoActive: '中控已启用',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: '短信',
      email: '邮件',
      enabled: '开',
      disabled: '关',
    },
    ops: {
      byDay: '按日',
      byWeekday: '按星期',
      avgPeople: '平均人数',
      avgBookings: '平均预订数',
      avgPartySize: '平均每单人数',
      busiestWeekday: '最繁忙的星期',
      totalPeople: '总人数',
      daysCount: '{{count}} 天',
    },
    days: {
      mon: '周一',
      tue: '周二',
      wed: '周三',
      thu: '周四',
      fri: '周五',
      sat: '周六',
      sun: '周日',
    },
  },
  auth: {
    welcomeBack: '欢迎回来！',
    signIn: '登录',
    email: '邮箱',
    password: '密码',
    enterPassword: '请输入您的密码',
    userCode: '用户代码',
    enterUserCode: '请输入您的用户代码',
    branchCode: '分店代码',
    enterBranchCode: '请输入分店代码',
    employeeId: '员工ID',
    enterEmployeeId: '请输入员工ID',
    rememberMe: '记住我',
    rememberCredentials: '记住凭据',
    forgotPassword: '忘记密码？',
    signInToContinue: '登录以继续使用 PRO20 管理您的餐厅业务。',
    signInToAccount: '登录您的账户',
    chooseSignInMethod: '选择您偏好的登录方式',
    or: '或',
    loginWithEmployeeId: '使用员工ID登录',
    loginWithUserCode: '使用用户代码登录',
    loginWithAdminCode: '使用管理员代码登录',
    invalidCredentials: '凭据无效，请重试。',
    enterpriseTagline: '企业级管理，化繁为简。',
    enterpriseDescription: '从一个强大的仪表板管理用户、监控分析并配置工作区。',
    trustedBy: '深受全球餐厅信赖',
    trustSecurityTitle: '企业级安全',
    trustSecurityDesc: '银行级数据保护与合规。',
    trustUptimeTitle: '99.9% 可用性',
    trustUptimeDesc: '在您需要时提供可靠性能。',
    trustSupportTitle: '全天候支持',
    trustSupportDesc: '随时为您提供帮助。',
    secureSignIn: '安全登录 | 您的连接已加密。',
    copyright: '© {{year}} PRO20。保留所有权利。',
    dashboardPreviewAlt: 'PRO20 仪表板预览',
    demoCredentials: '演示凭据：admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: '订阅已过期',
      title: '您的订阅已过期',
      description: '如果您已经续订，请联系支持团队。',
      infoTitle: '需要续订或恢复访问？',
      infoDescription: '请联系我们的支持团队，我们将协助您恢复使用。',
      footerHelp: '仍需帮助？',
      footerHelpDesc: '我们的支持团队全天候为您提供协助。',
    },
  },
  automation: {
    disabled: {
      badge: '功能未启用',
      title: '此部分尚未启用',
      description: '此功能当前已针对您的账户禁用。请联系支持以了解更多信息。',
      infoTitle: '需要访问此部分？',
      infoDescription: '请联系我们的支持团队，我们将协助您开始使用。',
      footerHelp: '仍需要帮助？',
      footerHelpDesc: '我们的支持团队全天候 24/7 为您提供协助。',
    },
  },
  orders: {
    disabled: {
      badge: '功能未启用',
      title: '此部分尚未启用',
      description: '此功能当前已针对您的账户禁用。请联系支持以了解更多信息。',
      infoTitle: '需要访问此部分？',
      infoDescription: '请联系我们的支持团队，我们将协助您开始使用。',
      footerHelp: '仍需要帮助？',
      footerHelpDesc: '我们的支持团队全天候 24/7 为您提供协助。',
    },
  },
  delivery: {
    disabled: {
      badge: '功能未启用',
      title: '此部分尚未启用',
      description: '此功能当前已针对您的账户禁用。请联系支持以了解更多信息。',
      infoTitle: '需要访问此部分？',
      infoDescription: '请联系我们的支持团队，我们将协助您开始使用。',
      footerHelp: '仍需要帮助？',
      footerHelpDesc: '我们的支持团队全天候 24/7 为您提供协助。',
    },
  },
  users: {
    title: '用户',
    subtitle: '管理团队成员和权限',
    searchPlaceholder: '搜索用户...',
    role: '角色',
    status: '状态',
    joined: '加入时间',
    accountDetails: '账户详情',
    userId: '用户ID',
    created: '创建时间',
    lastUpdated: '最后更新',
    notFound: '未找到用户',
  },
  settings: {
    title: '设置',
    subtitle: '管理工作区偏好',
    comingSoon: '此部分即将推出。',
    general: '常规',
    theme: '主题',
    language: '语言',
    timezone: '时区',
    notifications: '通知',
    emailNotifications: '邮件通知',
    pushNotifications: '推送通知',
    enabled: '已启用',
    disabled: '已禁用',
  },
  userSettings: {
    title: '用户设置',
    subtitle: '管理账户密码和偏好设置',
    password: {
      sectionTitle: '密码管理',
      sectionSubtitle: '更改密码',
      passwordLabel: '密码',
      confirmLabel: '确认密码',
      submit: '更新密码',
      errors: {
        required: '请输入密码',
        minLength: '密码至少需要 4 个字符',
        confirmRequired: '请确认密码',
        mismatch: '两次输入的密码不一致',
      },
      toast: {
        success: '密码更新成功',
        failed: '无法更新密码，请重试。',
        missingUser: '找不到您的账户，请重新登录。',
      },
    },
  },
  localConfiguration: {
    subtitle: '配置餐厅的标志、营业时段、位置和追踪设置。',
    saveConfiguration: '保存配置',
    saveTooltip: '保存餐厅配置',
    noBranch: '请选择门店以配置本地设置。',
    loadError: '无法加载本地配置，请重试。',
    logoTitle: '餐厅标志',
    logoHint: '上传图片',
    logoDescription: '上传带透明背景的 PNG 标志。双击图片可打开预览。',
    uploadImage: '上传图片',
    removeLogo: '移除标志',
    logoUploading: '上传中…',
    logoRemoving: '移除中…',
    timezoneTitle: '房间时区',
    timezoneHint: '确定表单中显示的当前日期，并避免国外客户出现日期偏移。',
    timezoneLabel: '地理区域',
    timezoneHelper: '标准时间与夏令时之间的切换会自动处理。',
    generalTitle: '基本信息',
    generalHint: '配置门店容量、时间段和闭店日期。',
    scheduleTitle: '营业时间',
    scheduleHint: '选择客人可以预订的时间。',
    dayOverviewTitle: '客人可以预订的时间',
    dayOverviewHint: '打开即可查看全天。',
    dayOverviewShow: '显示当日预览',
    dayOverviewHide: '隐藏当日预览',
    guestsCanBook: '客人可以在这段时间预订。',
    guestsCannotBook: '客人不能在这段时间预订。',
    emptyServiceTimes:
      '还没有营业时段。添加早餐、午餐或晚餐，让客人知道何时可以预订。',
    timeStepHint: '请按 15 分钟选择时间。预订在结束时间停止。',
    switchOn: '开',
    switchOff: '关',
    editAction: '编辑',
    deleteAction: '删除',
    howItWorks: '使用说明',
    previewOnly: '仅预览 — 请在下方编辑时段',
    stepBadge: '步长：15 分钟 — 04:00 / 03:59',
    localDay: '本地日：{{time}}',
    tips: {
      turnOnTitle: '开启',
      turnOn: '只保留你正在使用的时间段为启用状态。',
      moveTitle: '调整',
      move: '使用滑块调整开始和结束时间。',
      saveTitle: '保存',
      save: '系统会自动避免时间重叠。',
    },
    shiftsTitle: '营业时段',
    shiftsHint:
      '添加接受预订的时间，例如午餐或晚餐。可以关闭某一段，而不必删除。',
    createMealBand: '添加营业时段',
    createMealBandAction: '添加',
    createMealBandHint: '起一个名字，然后选择开始和结束时间。',
    editMealBand: '编辑营业时段',
    editMealBandNamed: '编辑 {{name}}',
    updateMealBandAction: '保存',
    mealBandName: '名称',
    mealBandNamePlaceholder: '午餐',
    selectStartTime: '选择开始时间',
    selectEndTime: '选择结束时间',
    mealBandLimitReached: '最多可以添加 20 个营业时段。',
    customMealBand: '营业时段',
    deleteMealBand: '删除 {{name}}',
    deleteMealBandTitle: '删除这个营业时段？',
    deleteMealBandDescription: '确定要删除 {{name}} 吗？此操作无法撤销。',

    active: '启用',
    inactive: '停用',
    shiftLegendOff: '{{name}}：不接受预订',
    shiftLegendRange: '{{name}}：{{from}} - {{to}}',
    booking: '预订',
    activeBand: '接受预订',
    activeBandHint: '如果客人不能在这段时间预订，请关闭。',
    deactivate: '停用',
    deactivated: '已停用',
    activate: '启用',
    valueSaved: '已保存值',
    valueSavedHint: '顾客可在该时间段内进行预订。',
    selectBandTitle: '使用单个滑条选择时段',
    selectBandHint: '所选部分会遵循其他已启用时段。',
    fine: '结束',
    spaceAvailable: '{{name}} 可用时段：{{from}} - {{to}}',
    minStartValue: '最小开始',
    maxEndValue: '最大结束',
    bandTurnedOff: '该时段已关闭。请打开上方开关以编辑时间。',
    activityHours: '营业时间',
    start: '开始',
    end: '结束',
    closuresTitle: '餐厅关闭的日期',
    closuresHint: '客人不能在这些日期预订。',
    addClosure: '添加闭店',
    closuresModalHint: '请选择闭店周期的开始和结束日期。',
    closingPeriod: '闭店周期',
    chooseDateRange: '选择日期范围',
    fromDate: '开始日期',
    toDate: '结束日期',
    actions: '操作',
    noClosures: '还没有关闭日期。',
    deleteClosure: '删除闭店',
    locationTitle: '位置',
    trackingTitle: '追踪与分析',
    trackingHint: '配置谷歌代码管理器、Facebook 像素和追踪工具',
    googleTagManager: '谷歌代码管理器',
    facebookPixel: 'Facebook 像素',
    tabs: {
      tabListLabel: '本地配置分区',
      general: '常规',
      bookingTimeSlots: '营业时间',
      locationTracking: '位置与追踪',
    },
    shifts: {
      breakfast: '早餐',
      breakfastSubtitle: '早餐时段',
      lunch: '午餐',
      lunchSubtitle: '午餐时段',
      aperitivo: '欢乐时光',
      aperitivoSubtitle: '开胃酒时段',
      dinner: '晚餐',
      dinnerSubtitle: '晚餐时段',
    },
    fields: {
      localName: '门店名称',
      localCapacity: '门店容量',
      address: '地址',
      city: '城市',
      zip: '邮编',
      province: '省份',
      nation: '国家',
      state: '州/地区',
      latitude: '纬度',
      longitude: '经度',
      gtmId: '容器 ID',
      pixelId: '像素 ID',
    },
    validation: {
      nameRequired: '门店名称为必填项',
      mealBandNameRequired: '请填写名称',
      mealBandNameDuplicate: '已有同名营业时段',
      openingTimeRequired: '开始时间为必填项',
      closingTimeRequired: '结束时间为必填项',
    },
    toast: {
      saved: '配置已保存',
      saveError: '无法保存配置',
      logoUploadSuccess: '标志上传成功',
      logoUploadError: '无法上传标志',
      logoRemoveSuccess: '标志已成功移除',
      logoRemoveError: '无法移除标志',
      selectDateRange: '请选择开始日期和结束日期',
      closureAdded: '闭店周期已添加',
      closureAddError: '无法添加闭店周期',
      closureOverlap: '该时段与现有的关闭时段重叠',
      closureRemoved: '闭店周期已删除',
      closureDeleteError: '无法删除闭店周期',
      mealBandCreated: '已添加营业时段',
      mealBandCreateError: '无法添加这个营业时段',
      mealBandUpdated: '营业时段已更新',
      mealBandUpdateError: '无法更新这个营业时段',
      mealBandStatusUpdated: '预订状态已更新',
      mealBandStatusError: '无法更新预订状态',
      mealBandDeleted: '营业时段已删除',
      mealBandDeleteError: '无法删除这个营业时段',
      mealBandInvalidCode: '这个营业时段未能保存。请重试。',
    },
  },
  stripe: {
    common: {
      refresh: '从 Stripe 刷新',
      noBranch: '请选择门店以继续。',
      processing: '处理中…',
    },
    configuration: {
      title: 'Stripe 配置',
      subtitle: '已关联 Stripe 账户的实时数据。',
      subtitleWithBranch: '与 {{branch}} 关联账户的实时数据。',
      loadError: '无法直接从 Stripe 获取账户状态。',
      notConnectedTitle: '尚未配置 Stripe 账户',
      notConnectedDescription:
        '所选门店尚未关联 Stripe 账户。请在后台完成入驻流程。',
      accountSection: '账户信息',
      accountHeading: 'Stripe 账户',
      email: '邮箱',
      country: '国家/地区',
      currency: '货币',
      accountType: '账户类型',
      disabledReason: '停用原因',
      disabledReasonNone: '无',
      detailsSubmitted: '资料已提交',
      chargesEnabled: '收款',
      payoutsEnabled: '提现',
      requirements: {
        title: 'Stripe 要求',
        currentlyDue: '待补充',
        pastDue: '已逾期',
        pendingVerification: '验证中',
        none: '无',
      },
      policy: {
        title: '付款政策',
        description: '当预订需要验证银行卡、预授权或付款时，向客户展示的文本。',
        versionMeta: '版本 {{version}} · 更新于 {{date}}',
        useTemplate: '推荐一个政策结构',
        templateWarning:
          '模板仅为起点：请替换所有方括号中的字段，并请法律顾问审核文本。',
        placeholder: '请输入门店的付款政策…',
        charCount: '{{count}}/{{max}} 个字符',
        save: '保存并创建新版本',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: '付款政策已保存。',
        policyError: '无法保存付款政策。',
      },
      validation: {
        policyRequired: '请输入付款政策文本。',
        policyMax: '政策文本不能超过 20000 个字符。',
      },
    },
    transactions: {
      title: 'Stripe 交易',
      empty: '暂无交易',
      emptyDescription: '该门店尚未有付款或定金记录。',
      notConnectedTitle: '尚未关联 Stripe 账户。',
      notConnectedDescription: '该门店的所有历史交易记录仍可在此查看。',
      filters: {
        all: '全部',
        succeeded: '成功',
        uncaptured: '未扣款',
        failed: '失败',
      },
      columns: {
        date: '日期',
        booking: '预订',
        operation: '操作类型',
        requested: '请求金额',
        captured: '已扣金额',
        status: '状态',
        details: '详情',
      },
      mode: {
        payNow: '付款',
        authorizeOnly: '定金 / 预授权',
      },
      status: {
        linkCreated: '等待付款',
        processing: '已记录，等待 Stripe 确认',
        pending: '待处理',
        authorized: '未扣款',
        partiallyPaid: '部分扣款',
        paid: '成功',
        failed: '失败',
        canceled: '已取消',
        refundPending: '退款处理中',
        partiallyRefunded: '部分退款',
        refunded: '已退款',
        refundFailed: '退款失败',
      },
      actions: {
        capture: '扣取定金',
        release: '拒绝定金',
      },
      confirm: {
        captureTitle: '确认扣取定金？',
        captureDescription: '将为预订 {{booking}} 扣取 {{amount}}。',
        releaseTitle: '确认拒绝定金？',
        releaseDescription: '{{amount}} 的授权将被取消，金额将被释放。',
      },
      toast: {
        captureSuccess: 'Stripe 已确认扣款。',
        captureError: '扣款失败。请在 Stripe 检查状态后重试。',
        releaseSuccess: 'Stripe 已取消授权。',
        releaseError: '释放失败。请在 Stripe 检查状态后重试。',
      },
      technical: {
        title: '技术详情',
        description: 'PRO20 与 Stripe 用于此次操作的标识符。',
        open: '查看技术详情',
        close: '关闭',
        bookingCode: '预订编号',
        depositCode: 'PRO20 交易编号',
        paymentIntentId: 'Stripe PaymentIntent',
        paymentLinkId: 'Stripe Payment Link',
        connectedAccountId: 'Stripe 账户',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: '订单已收到',
      ACCEPTED: '订单已接受',
      PREPARING: '订单制作中',
      READY: '订单已就绪',
      DELIVERED: '订单已送达',
      COLLECTED: '订单已取走',
      CANCELLED: '订单已取消',
      FAILED: '配送未成功',
      PAYMENT_REQUIRED: '支付链接',
      PAYMENT_CONFIRMED: '已收到付款',
      PAYMENT_FAILED: '付款未成功',
      REFUNDED: '退款已确认',
      SCHEDULE_REMINDER: '订单提醒',
    },
    orderingFlows: {
      CREATED: '订单创建后。客户会得知餐厅尚未接受该订单。',
      ACCEPTED: '当你接受订单并选择通知客户时。',
      PREPARING: '当开始制作并选择通知客户时。',
      READY: '当订单就绪并选择通知客户时。对外卖而言，这并不表示骑手已出发。',
      DELIVERED:
        '当你单独将订单处理为已送达并选择通知客户时。批量操作和骑手完成不会发送消息。',
      COLLECTED:
        '当你单独将订单处理为已取走并选择通知客户时。批量操作不会发送消息。',
      CANCELLED: '当你取消订单并选择通知客户时。这并不确认退款。',
      FAILED: '当你将配送标记为未成功并选择通知客户时。',
      PAYMENT_REQUIRED: '仅在实际创建了有效支付链接之后。',
      PAYMENT_CONFIRMED:
        '在付款实际确认之后（自动或手动）。批量操作不会发送消息。',
      PAYMENT_FAILED: '如果付款尝试失败且订单仍未支付。',
      REFUNDED: '仅在退款实际完成后。',
      SCHEDULE_REMINDER:
        '在预约时间之前，仅针对已接受或制作中的订单。未完成的银行卡支付除外。',
    },
    ordering: {
      configurationHint: '为该分店的订单启用此消息。保存不会发送任何消息。',
      initialize: '创建外卖与自取消息',
      initialized: '消息已创建。未发送任何内容。',
      loadError: '无法加载外卖与自取消息。请重试。',
      missing: '创建缺失的消息。已有自定义内容会保留。新消息默认停用。',
      minutes: '订单预约时间前的分钟数',
      scheduleError: '请输入 1 到 10080 之间的整数分钟。',
      exclusive: '请选择非官方 WhatsApp 或官方模板，不能同时选择两者。',
      emailPreset: '默认邮件内容',
      paymentHint: '如果你已通过 WhatsApp 手动发送支付链接，请保持停用。',
    },

    searchPlaceholder: '按名称、代码、分类或渠道搜索…',
    allCategories: '所有分类',
    browseCategories: '消息分类',
    searchEmpty: '没有匹配项。请尝试其他关键词或选择所有分类。',

    giftCards: {
      scheduleTitle: '到期前提醒',
      daysBeforeExpiry: '到期前天数',
      scheduleHint:
        '按门店时区，在到期前指定天数和时间发送提醒。排除已使用、已取消或余额为零的卡。',
      scheduleError: '请输入正整数天数及 00:00 至 23:00 之间的时间。',

      creation: '礼品卡创建',
      expired: '礼品卡过期',
      walletHint:
        '后端在发送时生成 wallet_link。Wallet 按钮必须使用该卡专属的后缀，不能使用固定客户链接。',

      initialize: '创建缺失消息',
      initializeSuccess: '配置已创建。',
      initializeError: '后端无法创建配置。',
      missing: '以下配置缺失。创建后可关联官方 WhatsApp 和电子邮件模板。',
      notCreated: '尚未在后端创建',
      loadError: '无法加载营销配置。请重试。',
    },
    title: '消息中心',
    subtitle: '管理门店的所有自动 WhatsApp、短信和电子邮件消息。',
    configuredLabel: '已配置消息',
    refresh: '刷新',
    emptyCategory: '此分类暂无消息。',
    emptyTitle: '未找到消息',
    emptyDescription: '此门店尚未配置任何自动消息。',
    noBranchTitle: '未选择门店',
    noBranchDescription: '请选择门店以查看和管理自动消息。',
    loadErrorTitle: '无法加载消息',
    loadErrorDescription: '加载消息列表失败，请重试。',
    categories: {
      restaurantOrdering: '外卖与自取',
      supplierOrders: '供应商订单',
      giftCards: '礼品卡消息',
      standard: '标准消息',
      cta: 'CTA',
      marketing: '营销消息',
      preBooking: '预订前沟通',
      postBooking: '预订后沟通',
      switchboard: '总机消息',
    },
    categoryDescriptions: {
      marketing: '促销消息、忠诚计划及卡片自动操作。',
    },
    items: {
      ORDER_SENT: '订单已发送',
      ORDER_UPDATED: '订单已更新',
      ORDER_DELIVERY: '订单配送',
      ORDER_GENERIC: '供应商订单',

      ATT: '待确认消息',
      ARR: '到店确认',
      CONF: '预订确认',
      REF: '预订被拒绝',
      DEL: '预订已删除',
      FQ1: '排队管理 1',
      FQ2: '排队管理 2',
      EDTU: '预订更新（用户）',
      EDTC: '预订更新（客户）',
      EDTR: '预订更新（餐厅）',
      ANS1: '未到店提醒 1',
      ANS2: '未到店提醒 2',
      ANS3: '未到店提醒 3',
      RPR: '促销提醒',
      DOB: '生日促销',
      AAD: '评价请求',
      RRP: '想念你促销',
      CBP: '回访邀请',
      CENTRALINO1: '总机功能 1',
      CENTRALINO2: '总机功能 2',
      CENTRALINO3: '总机功能 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: '关闭',
      pageSubtitle: '为此模板配置自动消息渠道。',
      marketingTitle: '营销消息',
      enableConfiguration: '启用此配置',
      enableConfigurationHint: '更改将应用于所有使用 {{type}} 的计划。',
      enableWhatsApp: '启用 WhatsApp',
      enableTemplates: '启用模板',
      enableEmail: '启用电子邮件',
      emailObject: '主题',
      configuration: '配置',
      configurationHint: '选择消息发送方式',
      configurationHintPreBooking: '配置发送方式和时间',
      configurationHintPostBooking: '配置预订后消息的发送与排程',
      sendingMethods: '发送方式',
      sendingMode: '发送模式',
      sendingTiming: '发送时机',
      whenToSend: '何时发送',
      sendingNow: '发送时间',
      timingImmediate: '预订后立即发送消息 🕒',
      timingOneHourBefore: '提前 1 小时发送消息 🕒',
      timingHoursAdvance: '提前 {{count}} 小时发送消息 🕒',
      timingOneDayAdvance: '提前 1 天发送消息 🕒',
      timingDaysAdvance: '提前 {{count}} 天发送消息 🕒',
      whenToSendOneDayLater: '1 天后发送消息',
      whenToSendDaysLater: '{{count}} 天后发送消息',
      sendingWhatsAppOnly: '仅发送 WhatsApp',
      sendingWhatsAppAndEmail: '发送 WhatsApp 和电子邮件',
      whatsappMessage: 'WhatsApp 消息',
      whatsappMessageDesc: '配置发送给客户的 WhatsApp 消息',
      whatsappForeign: 'WhatsApp 国际客户',
      whatsappForeignDesc: '英文版本。向带有国际区号的客户自动发送英文消息。',
      whatsappBusinessTemplate: 'WhatsApp Business 模板',
      whatsappBusinessTemplateDesc:
        '从 Meta 批准的模板中选择，通过 WhatsApp Business API 发送。',
      smsMessage: '短信消息',
      smsMessageDesc: '配置发送给客户的短信消息',
      email: '电子邮件',
      emailDesc: '配置发送给客户的电子邮件消息',
      emailSubject: '邮件主题',
      emailSubjectPlaceholder: '例如：来自 @hotel-name 的通知',
      bodyPlaceholder: '在此编写消息...',
      inspireMe: '给我灵感',
      testSending: '测试发送',
      testSendPrompt: '输入要发送消息的号码',
      testSendAction: '发送',
      selectTemplate: '选择模板',
      selectTemplateTooltip: '从 Meta 批准的模板中选择',
      selectTemplateHint: '为此消息选择 Meta 批准的模板。',
      selectedTemplate: '已选模板',
      noTemplateSelected: '未选择模板',
      noTemplateHint:
        '未选择模板。请选择 Meta 批准的模板以设置通过 WhatsApp Business API 发送。',
      templateCategory: '类别',
      templateType: '类型',
      templateLanguages: '语言',
      templateLanguage: '语言',
      templateVariableMap: '变量映射',
      templateBody: '正文',
      templateBodyEmpty: '该语言暂无正文预览。',
      ctaButton: '行动号召',
      ctaDynamicPath: '动态路径',
      ctaDynamicPathPlaceholder:
        '动态路径，例如 /menu/xxxx0111 或 /booking/0000101111',
      ctaDynamicPathHint:
        '发送消息时会把这里的内容接在模板链接后面，替换 {{1}}，便于根据消息场景生成动态链接。',
      ctaDynamicPathRequired: '动态路径不能为空。',
      ctaUrlValid: '{{url}} 有效',
      ctaUrlInvalid: '链接无效，请检查格式。',
      ctaLinksTooltip: '我有哪些选项？',
      ctaLinksTitle: '在 Pro20 上创建的服务可用链接',
      ctaLinksDesc: '选择一个链接作为动态路径。',
      ctaLinksEmpty: '你还没有在 Pro20 上创建任何服务，暂时无法提供建议。',
      ctaLinkUnnamed: '未命名链接',
    },
    toast: {
      refreshed: '消息列表已刷新。',
      saveSuccess: '消息配置已保存。',
      saveError: '无法保存消息配置。',
      testSent: '测试消息发送成功。',
      sendError: '无法发送消息。',
      templateSelected: '已选择模板。',
      resetSuccess: '配置已恢复为默认值。',
      resetError: '无法重置配置，请重试。',
      applyDefaultsSuccess: '已应用默认文案并启用该渠道。',
      applyDefaultsError: '无法应用默认设置，请重试或取消。',
      detailUnavailable: '此类别的消息详情尚不可用。',
    },
    validation: {
      whatsappBodyRequired: '启用 WhatsApp 时请填写消息内容。',
      templateRequired: '启用模板时请选择 WhatsApp Business 模板。',
      emailSubjectRequired: '启用电子邮件时请填写主题。',
      emailBodyRequired: '启用电子邮件时请填写正文。',
    },
    reset: {
      tooltip: '恢复默认配置',
      title: '重置配置？',
      description: '现有配置将被默认配置替换。',
      confirm: '是，继续',
      cancel: '取消',
      resetting: '正在重置…',
    },
    applyDefaults: {
      whatsappTooltip: '应用默认文案并为该组启用 WhatsApp',
      emailTooltip: '应用默认主题与正文并为该组启用电子邮件',
      whatsappAria: '应用默认 WhatsApp 配置',
      emailAria: '应用默认电子邮件配置',
      whatsappTitle: '应用 WhatsApp 默认设置？',
      emailTitle: '应用电子邮件默认设置？',
      description:
        '所选渠道的文案将被替换，并在整个组中启用该渠道。消息激活状态、其他渠道和发送规则保持不变。',
      marketingWhatsappNote:
        'WhatsApp 将使用自由格式消息；官方模板数据将予以保留。',
      confirm: '应用并启用',
      cancel: '取消',
      applying: '正在应用…',
    },
  },
  configureWhatsApp: {
    title: '配置 WhatsApp',
    subtitle: '为此分店连接 WhatsApp Business 号码',
    disabled: {
      badge: '配置已禁用',
      title: 'WhatsApp 配置不可用',
      description: '请通过移动应用启用此功能。',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: '连接本地 WhatsApp 号码',
      description: '创建新实例，扫描二维码，并启用从 PRO20 平台发送自动消息。',
      create: '创建配置',
      guided: '引导式且可撤销的操作',
      featureSecureTitle: '安全可靠',
      featureSecureDesc: '企业级安全',
      featureQuickTitle: '快速设置',
      featureQuickDesc: '不到 2 分钟',
      featureReversibleTitle: '可撤销',
      featureReversibleDesc: '随时更改或断开连接',
      step1Title: '创建实例',
      step1Desc: '生成与所选分店关联的技术配置。',
      step1Time: '~ 30 秒',
      step2Title: '扫描二维码',
      step2Desc: '在手机上打开 WhatsApp，通过二维码连接设备。',
      step2Time: '~ 60 秒',
      step3Title: '尝试发送',
      step3Desc: '发送测试消息并确认一切正常运行。',
      step3Time: '~ 30 秒',
    },
    creating: {
      title: '正在配置…',
      description: '正在创建 WhatsApp 实例，请稍候。',
      starting: 'WhatsApp 正在启动。首次连接可能需要几分钟。',
    },
    success: {
      title: '已完成',
      description: '实例创建成功！',
      banner: '您现在可以使用二维码或手机配对码绑定设备。',
      continue: '继续',
    },
    configured: {
      heading: 'WhatsApp 实例',
      subtitle: '管理此分店的绑定、状态和测试消息。',
      statusInstanceCreated: '实例已创建',
      statusQr: '等待扫描二维码',
      statusReady: '已连接',
      statusCreating: '创建中…',
      statusFailed: '连接失败',
      restart: '重新连接',
      stillStarting:
        'WhatsApp 仍在启动。此页面会继续检查。二维码准备好后请使用扫描二维码，或重新连接。',
      failureHint: 'WhatsApp 引擎未能启动。请重新连接后再试。',
      scanQr: '扫描二维码',
      delete: '删除',
      deleting: '删除中...',
      statusDeleting: '删除中...',
      sessionName: '会话名称',
      instanceId: '实例 ID',
      connectedNumber: '已连接号码',
      operatingStatus: '运行状态',
      nextStepTitle: '下一步',
      nextStepQr: '使用手机 WhatsApp 扫描二维码以绑定设备。',
      notLinked: '尚未绑定',
    },
    link: {
      title: '绑定设备',
      description: '使用二维码或手机配对码连接 WhatsApp。',
      tabQr: '二维码',
      tabPhone: '手机配对码',
      qrInstructions:
        '打开手机 WhatsApp → 已关联的设备 → 关联设备，然后扫描此码。',
      waiting: '等待扫描…',
      phoneInstructions: '输入要绑定的手机号，然后获取配对码。',
      phoneLabel: '手机号码',
      phonePlaceholder: '333 123 4567',
      getCode: '获取配对码',
      pairingCode: '您的配对码',
      pairingHint: '在 WhatsApp → 已关联的设备 → 用手机号码关联 中输入此代码。',
    },
    delete: {
      title: '删除 WhatsApp 配置？',
      description: '这将移除此分店的实例。之后可以重新创建。',
      confirm: '是，删除',
    },
    test: {
      title: '发送测试消息',
      phone: '收件人号码',
      phonePlaceholder: '333 123 4567',
      message: '消息',
      defaultMessage: '你好！这是来自 Proventi 的测试消息。',
      send: '发送测试',
      success: '测试消息发送成功。',
      disabledHint: '发送测试消息前，请扫描二维码并绑定 WhatsApp。',
      notConnected: 'WhatsApp 会话未连接。请先扫描二维码以绑定您的设备。',
    },
    toast: {
      linked: 'WhatsApp 已成功连接。',
      stillStarting: 'WhatsApp 仍在启动。此页面会继续检查。',
      createFailed: '创建 WhatsApp 会话失败。',
      qrFailed: '准备 WhatsApp 二维码失败。',
    },
  },
  configureWhatsAppOfficial: {
    title: '配置 WhatsApp 官方版',
    subtitle: '为该门店连接您的 WhatsApp Business 官方号码（Meta）。',
    loading: '正在加载接入状态…',
    loadError: '加载接入状态失败。',
    retry: '重试',
    toast: {
      onboardSuccess: 'WhatsApp 接入已成功完成。',
      onboardError: '完成 WhatsApp 接入失败。',
      registerSuccess: '电话号码注册成功。',
      registerError: '注册电话号码失败。',
      businessDetailsSuccess: '商家详情加载成功。',
      businessDetailsError: '加载商家详情失败。',
      missingPhoneNumberId: '缺少电话号码 ID。',
      metaError: 'WhatsApp 注册错误：{{message}}',
      missingBranch: '缺少门店代码。',
      sdkNotReady: 'Facebook SDK 尚未就绪。',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business API',
      title: '连接 WhatsApp Business API',
      description:
        '在此部分，您可以创建并将号码连接到 WhatsApp Business 官方渠道。配置可让您将企业号码与平台关联，从而以专业、自动化且合规的方式开始发送和接收消息。',
      connect: '创建配置',
      connecting: '连接中…',
      howToConnect: '如何连接',
      featuresTitle: '通过 Meta，您可以',
      featureExisting: '连接现有的 WhatsApp Business 号码',
      featureNew: '在 Meta 上创建新账户',
      step1Title: '注册您的号码',
      step1Desc: '在 Meta 中添加并验证您的企业电话号码以激活该渠道。',
      step1Time: '约 2 分钟',
      step2Title: '创建模板并获得批准',
      step2Desc: '创建消息模板并提交给 Meta 进行审核和批准。',
      step2Time: '约 1 天',
      step3Title: '发送文本模板',
      step3Desc: '发送已批准的模板以开始新的对话。',
      step3Time: '约 30 秒',
      step4Title: '发送到电话号码',
      step4Desc: '向任何客户电话号码发送消息并开始聊天。',
      step4Time: '约 30 秒',
    },
    configured: {
      heading: 'WhatsApp 接入信息',
      subtitle: '您的 WhatsApp Business 官方账户已连接。',
      connectedBadge: '已连接',
      phoneNumberId: '电话号码 ID',
      wabaId: 'WhatsApp Business 账户 ID',
      businessId: '企业 ID',
      onboardingStatus: '接入状态',
      phoneRegistrationStatus: '号码注册状态',
      statusCompleted: '已完成',
      statusSuccess: '成功',
      statusPending: '待处理',
      statusRegistered: '已注册',
      statusNotRegistered: '未注册',
      registerPhone: '注册您的号码',
      registeringPhone: '注册中…',
      businessDetailsHeading: '商家详情',
      businessDetailsEmpty:
        '商家详情尚不可用。请使用电话号码 ID 从 Meta 获取。',
      fetchBusinessDetails: '获取商家详情',
      fetchingBusinessDetails: '获取中…',
      verifiedName: '已验证名称',
      displayPhoneNumber: '显示电话号码',
      qualityRating: '质量评级',
      codeVerificationStatus: '代码验证',
      platformType: '平台类型',
      throughputLevel: '吞吐量',
      phoneStatus: '电话状态',
      accountMode: '账户模式',
      nameStatus: '名称状态',
      messagingLimitTier: '消息限额',
      officialBusinessAccount: '官方商业账户',
      businessProfileHeading: '企业资料',
      email: '企业邮箱',
      vertical: '业务类别',
      description: '企业描述',
      about: '关于',
      address: '地址',
      websites: '网站',
      metaPhoneNumberId: 'Meta 电话号码 ID',
      webhookApplication: 'Webhook URL',
      reset: '重置配置',
      resetting: '重置中…',
    },
    reset: {
      title: '要重置配置吗？',
      description:
        '此操作仅重置本地视图，以便您可以重新运行配置。不会删除后端接入数据。',
      confirm: '是的，重置',
    },
    guide: {
      title: 'WhatsApp Business API 接入指南',
      intro:
        '欢迎！请按照以下步骤将您的 WhatsApp Business 账户连接到我们的平台。',
      prerequisitesTitle: '前提条件',
      prerequisitesIntro: '开始之前，请确保您具备以下条件：',
      prereq1: '拥有管理员权限的 Facebook 账户。',
      prereq2: '一个 Meta Business Manager 账户（或准备好创建一个）。',
      prereq3: '一个可接收短信或语音验证的有效手机号码。',
      prereq4:
        '该电话号码当前未在 WhatsApp Messenger、WhatsApp Business 应用或其他 WhatsApp Business API 提供商处注册。',
      prereq5: '准备好您的企业信息：企业名称、网站（可选）、电子邮件和类别。',
      prereq6: '一张有效的信用卡/借记卡，用于在 Meta 中添加为付款方式。',
      prereq7: '稳定的网络连接。',
      prereqNote:
        '重要提示：如果您的电话号码当前正在 WhatsApp Business 应用中使用，请先将其从应用中移除，然后再连接到 WhatsApp Business Platform。',
      stepsTitle: '接入步骤',
      step1Title: '步骤 1 – 登录我们的平台',
      step1Body: '登录您的账户，进入“WhatsApp 集成”，然后点击“连接 WhatsApp”。',
      step2Title: '步骤 2 – 使用 Facebook 登录',
      step2Body:
        '将打开一个安全的 Meta（Facebook）登录窗口。使用您的 Facebook 账户登录并点击“继续”。',
      step3Title: '步骤 3 – 选择或创建 Business Manager',
      step3Body:
        '选择您现有的 Meta Business Manager，或点击“创建企业账户”并输入您的企业名称、电子邮件、网站和国家/地区，然后点击“下一步”。',
      step4Title: '步骤 4 – 创建或选择 WhatsApp Business 账户',
      step4Body:
        '使用现有的 WhatsApp Business 账户，或提供账户名称、显示名称和企业类别以创建新账户，然后点击“继续”。',
      step5Title: '步骤 5 – 添加您的电话号码',
      step5Body:
        '选择您的国家/地区，输入您的电话号码，选择短信或语音通话验证方式，然后输入 6 位验证码。',
      step6Title: '步骤 6 – 授予权限',
      step6Body:
        'Meta 将请求必要的权限。点击“继续”和“完成”；我们的平台将自动完成设置。',
      step7Title: '步骤 7 – 连接完成',
      step7Body:
        '连接成功后，您将看到已连接的电话号码、WhatsApp Business 账户、连接状态，并可以发送和接收消息。',
      nextStepsTitle: '建议的后续步骤',
      nextStep1: '创建您的 WhatsApp 消息模板。',
      nextStep2: '在 Meta Business Manager 中添加有效的付款方式。',
      nextStep3: '完成 Meta 企业验证（推荐）。',
      nextStep4: '发送一条测试模板消息。',
      nextStep5: '配置您的聊天机器人或自动化（如适用）。',
      notesTitle: '重要说明',
      note1: '一个电话号码一次只能连接到一个 WhatsApp 账户。',
      note2: '接入后请勿从 Meta 中删除您的 WhatsApp Business 账户。',
      note3: '保持付款方式有效，以避免消息发送中断。',
      note4: '建议完成企业验证以获得更高的消息发送限额。',
      note5: '显示名称的批准由 Meta 处理，可能需要一些时间。',
      issuesTitle: '常见问题',
      issue1Title: '未收到验证码',
      issue1Body:
        '请确认电话号码正确，尝试语音通话选项，并在再次请求验证码之前等待几分钟。',
      issue2Title: '电话号码已注册',
      issue2Body:
        '该号码已连接到 WhatsApp Messenger、WhatsApp Business 应用或其他 WhatsApp Business API 提供商。请先将其从现有服务中移除，然后再进行接入。',
      issue3Title: '权限错误',
      issue3Body:
        '请确保您同时是 Facebook 账户和 Meta Business Manager 的管理员。',
      issue4Title: '需要付款',
      issue4Body:
        'Meta 要求在发起某些对话之前提供有效的付款方式。请在 Meta Business Manager 中添加您的付款方式。',
      issue5Title: '企业验证待处理',
      issue5Body:
        '您的账户仍可连接和使用，但强烈建议完成企业验证，以解锁更高的消息发送限额并提升账户信任度。',
      needHelpTitle: '需要帮助？',
      needHelpIntro:
        '如果您在接入过程中遇到任何问题，请联系我们的支持团队并提供：',
      help1: '错误的屏幕截图。',
      help2: '电话号码（包括国家/地区代码）。',
      help3: 'Meta Business Manager ID（如有）。',
      help4: 'WhatsApp Business 账户 ID（如有）。',
      needHelpOutro: '提供这些信息将帮助我们更快地解决您的问题。',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: '功能未启用',
      title: '此部分尚未启用',
      description: '此功能当前已针对您的账户禁用。请联系支持以了解更多信息。',
      infoTitle: '需要访问此部分？',
      infoDescription: '请联系我们的支持团队，我们将协助您开始使用。',
      footerHelp: '仍需要帮助？',
      footerHelpDesc: '我们的支持团队全天候 24/7 为您提供协助。',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: '发送中…',
      retry: '重试',
      retryConfirmTitle: '重试发送消息？',
      retryConfirmMessage: '确定要再次发送此消息吗？',
      billingFree: '免费',
      billingPaid: '付费',
    },
    delivery: {
      warningTooltip: '未送达 — 点击查看详情',
      detailsTitle: '送达问题',
      fallbackTitle: '消息未能送达',
      technicalDetails: '技术详情',
      noTechnicalDetails: '暂无技术详情。',
    },
    composer: {
      placeholder: '输入消息...',
      send: '发送',
      submitTemplate: '发送模板',
      templateOnlyTitle: '需要模板',
      templateOnlyDescription:
        '24 小时客服窗口未激活。请先发送已批准的模板以重新打开对话。',
      attach: '添加附件',
      attachImage: '图片',
      attachAudio: '音频',
      attachDocument: '文档',
      removeAttachment: '移除附件',
    },
    templateSend: {
      title: '发送模板',
      fillVariablesTitle: '填写变量',
      customerLabel: '客户',
      phoneLabel: '收件人电话',
      prefix: '区号',
      number: '号码',
      sendingTo: '发送至',
      phoneInvalid: '发送前请填写区号和电话号码。',
      enterValuePlaceholder: '输入值',
      fieldRequired: '此字段为必填',
      ctaUrlSuffix: 'CTA URL 后缀',
      ctaUrlSuffixPlaceholder: 'URL 后缀',
      ctaUrlSuffixHint: '当模板含动态链接时，会追加到按钮 URL。',
      noVariables: '此模板无需填写变量。',
      sendMessage: '发送消息',
      cancel: '取消',
      previewTitle: '预览',
      infoCategory: '类别',
      infoLanguage: '语言',
      infoType: '类型',
      infoSendingTo: '发送至',
    },
    toast: {
      sendSuccess: '消息已发送。',
      sendError: '无法发送消息。',
      templateSendSuccess: '模板已发送。',
      templateSendError: '无法发送模板。',
      loadConversationsError: '无法加载会话。',
      loadMessagesError: '无法加载消息。',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: '模板分组',
    allGroups: '所有模板',
    allStatuses: '所有状态',
    filterStatus: '按状态筛选',
    searchEmpty: '没有符合搜索或筛选条件的模板。',
    groups: {
      UTILITY: '服务通知',
      MARKETING: '营销',
      AUTHENTICATION: '身份验证',
      OTHER: '其他',
    },
    title: '模板',
    subtitle: '整理模板以便快速访问，并通过预定义消息扩展沟通。',
    learnMore: '了解更多',
    importGlobal: '导入全局模板',
    addTemplate: '添加模板',
    tableTitle: '模板',
    sync: '同步模板',
    searchPlaceholder: '搜索模板...',
    emptyTitle: '未找到模板',
    emptyDescription: '创建或同步模板以开始向客户发送消息。',
    detailsTitle: '模板详情',
    deleteTitle: '删除模板？',
    deleteDescription: '确定要删除“{{name}}”吗？此操作无法撤销。',
    columns: {
      statusDate: '状态 / 日期',
      nameType: '名称 / 类型',
      category: '类别',
      language: '语言',
      actions: '操作',
    },
    stats: {
      approved: '已批准模板',
      pending: '待处理模板',
      draft: '草稿模板',
      rejected: '已拒绝模板',
    },
    status: {
      APPROVED: 'APPROVED',
      PENDING: 'PENDING',
      DRAFT: 'DRAFT',
      REJECTED: 'REJECTED',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: '模板操作',
      edit: '编辑',
      delete: '删除',
    },
    toast: {
      learnMoreSoon: '文档即将推出。',
      importSoon: '全局导入即将推出。',
      createSoon: '模板创建即将推出。',
      syncSuccess: '模板同步成功。',
      syncError: '无法同步模板。',
      importSuccess: '全局模板已导入。',
      importError: '无法导入全局模板。',
      loadError: '无法加载模板。',
      deleteSuccess: '模板已删除。',
      deleteError: '无法删除模板。',
    },
    create: {
      title: '新建模板',
      subtitle: '根据需要轻松创建新模板。',
      backToList: '返回模板列表',
      formTitle: '添加新模板',
      editTitle: '编辑模板',
      editSubtitle: '更新被拒绝的模板并重新提交审核。',
      editFormTitle: '编辑模板',
      category: '类别',
      name: '模板名称',
      namePlaceholder: '例如 booking_confirmation_v1',
      language: '模板语言',
      typeLabel: '模板类型',
      types: {
        text: '文本',
        image: '图片',
        video: '视频',
        audio: '音频',
        document: '文档',
      },
      categories: {
        UTILITY: '实用',
        MARKETING: '营销',
        AUTHENTICATION: '身份验证',
      },
      uploadFile: '上传文件',
      browse: '浏览…',
      uploading: '上传中…',
      clearMedia: '清除',
      noFileChosen: '未选择文件',
      orPasteUrl: '或粘贴句柄 / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder: '页眉媒体已上传（无法预览）',
      mediaHintImage: '仅 JPEG 或 PNG，最大 5 MB。',
      mediaHintVideo: '仅 MP4，最大 16 MB，最长 60 秒。',
      mediaHintAudio: 'MP3、OGG 或 M4A，最大 16 MB。',
      mediaHintDocument: 'PDF 或 Office 文档，最大 100 MB。',
      heading: '标题（可选）',
      headingHint: '显示在消息正文上方。',
      body: '正文',
      footer: '页脚',
      variablesTitle: '变量',
      variables: {
        requested_amount: '请求的定金',
        balance_due: '剩余余额',
        paid_total: '已收总额',
        deposit_percentage: '定金比例',
        payment_link: '定金支付链接',

        service_name: '服务',
        order_number: '订单号',
        ordering_channel: '自取 / 外卖',
        scheduled_label: '订单日期与时间',
        total_amount: '订单总额',
        order_link: '订单摘要链接',
        delivery_address: '配送地址',
        pickup_address: '取餐地址',
        paid_amount: '已付金额',
        refund_amount: '退款金额',
        cancellation_reason: '取消原因',
        failure_reason: '配送失败原因',
        amount_due: '应付金额',

        customerName: '客户姓名',
        branchName: '分店名称',
        bookingId: '预订 ID',
        discountCode: '折扣码',
        promoCode: '促销码',
        bookingDate: '预订日期',
        bookingTime: '预订时间',
        bookingUpdateLink: '预订更新链接',
        branchCode: '分店代码',
        guestCount: '成人数',
        childrenCount: '儿童数',
        creditCardRequestUrl: '信用卡请求 URL',
        depositRequestLink: '定金请求链接',
        depositAmount: '定金金额',
        expectedLeaveTime: '预计离店时间',
        qrCodeImageUrl: '二维码图片 URL',
      },
      buttonsTitle: '按钮',
      buttonType: '按钮类型',
      buttonText: '按钮文本',
      buttonUrl: '按钮 URL',
      quickReply: '快速回复',
      ctaUrl: 'CTA URL',
      addQuickReply: '快速回复',
      addCta: 'CTA',
      removeButton: '移除',
      buttonsHint: '每个模板最多可添加 3 个按钮。',
      submit: '发送',
      previewTitle: 'WhatsApp 预览',
      previewEmpty: '模板预览将显示在此处。',
      validation: {
        categoryRequired: '请选择类别。',
        nameMin: '名称至少 3 个字符。',
        nameMax: '名称最多 60 个字符。',
        nameFormat: '仅可使用小写字母、数字和下划线。',
        languageRequired: '请选择语言。',
        bodyRequired: '正文为必填项。',
        bodyMax: '正文最多 1024 个字符。',
        mediaRequired: '图片和视频模板必须提供媒体。',
        invalidImageType: '图片必须为 JPEG 或 PNG。',
        invalidVideoType: '视频必须为 MP4。',
        imageTooLarge: '图片不能超过 5 MB。',
        videoTooLarge: '视频不能超过 16 MB。',
        videoDurationMax: '视频时长不能超过 60 秒。',
        videoDurationUnreadable: '无法读取视频时长。',
        buttonLabelRequired: '按钮文本为必填项。',
        buttonUrlRequired: 'CTA 按钮需要 URL。',
        qrPlaceholder: '快速回复文本不能包含变量。',
        qrMax: '快速回复文本最多 25 个字符。',
        urlInvalid: 'CTA URL 必须是有效的 http(s) 地址。',
      },
      toast: {
        success: '模板创建成功。',
        error: '无法创建模板。',
        updateSuccess: '模板更新成功。',
        updateError: '无法更新模板。',
        uploadSuccess: '页眉媒体已上传。',
        uploadFailed: '无法上传页眉媒体。',
      },
    },
  },
  notifications: {
    title: '通知',
    allCaughtUp: '全部已读',
    unread: '条未读通知',
    unreadPlural: '条未读通知',
    markAllRead: '全部标为已读',
    noNotifications: '暂无通知',
    noNotificationsDesc: '一切正常，新通知将显示在这里。',
    viewAll: '查看所有通知',
  },
  bookings: {
    history: {
      title: '预订历史',
      subtitle: '{{branch}} 的预订',
      subtitleDefault: '查看和管理预订历史',
      totalBookings: '{{count}} 条预订',
      bookingsForDateBefore: '',
      bookingsForDateAfter: ' 共 {{count}} 条预订',
      previousDay: '前一天',
      nextDay: '后一天',
      calendar: {
        title: '选择预订日期',
        openCalendar: '打开预订日历',
        previousMonth: '上个月',
        nextMonth: '下个月',
        bookings: '{{count}} 个预订',
        arrived: '{{count}} 已到店',
        refused: '{{count}} 已拒绝',
        legendBookings: '预订',
        legendArrived: '已到店',
        legendRefused: '已拒绝',
      },
      calendarConfig: {
        eyebrow: '预订日历',
        title: '配置当天',
        subtitle: '仅编辑日历中选中的日期。',
        branchLabel: '分店',
        singleDay: '仅当天',
        updateDate: '更新 {{date}}',
        dateRange: '日期范围',
        dateRangeHint: '创建或覆盖多天',
        eventLabel: '重复或活动',
        eventPlaceholder: '添加重复或活动名称',
        daysSelected: '已选择 {{count}} 天',
        daysSelectedPlural: '已选择 {{count}} 天',
        rangePlaceholder: '选择日期范围',
        dateRangeRequired: '请选择开始和结束日期。',
        rulesTitle: '预订规则',
        manual: '手动',
        acceptTo: '接受至',
        acceptToValue: '数量',
        acceptToRequired: '此字段为必填项',
        acceptAll: '全部接受',
        refuseAll: '全部拒绝',
        manualHint: '手动模式由应用/门户的正常流程决定。',
        close: '关闭',
        saveDay: '保存当天',
        saveRange: '保存范围',
        loadError: '无法加载当天配置。',
        saveSuccess: '当天配置已保存。',
        saveError: '无法保存当天配置。',
      },
      exportPdf: '下载今日预订 PDF',
      exportPdfGeneratingTitle: '正在生成 PDF',
      exportPdfGeneratingDescription: '正在准备预订文件...',
      exportPdfReadyTitle: 'PDF 已就绪',
      exportPdfReadyDescription: '文件已成功生成。',
      exportPdfOpen: '打开 PDF',
      exportPdfClose: '关闭',
      exportPdfError: '生成预订 PDF 失败，请重试。',
      addBooking: '添加预订',
      shifts: {
        all: '全部',
        filterLabel: '服务',
        breakfast: '早餐',
        daily: '午餐',
        happyhour: '欢乐时光',
        night: '晚餐',
      },
      searchPlaceholder: '搜索客户、邮箱、手机或预订代码',
      filterStatus: '筛选状态',
      callCustomer: '呼叫 {{name}}',
      moreActions: '更多操作',
      snapshot: {
        bookings: '预订',
        confirmed: '已确认',
        arrived: '已到店',
        waiting: '等待中',
        issues: '未到 / 拒绝',
        configureShifts: '配置当日预订规则',
        filterShift: '筛选{{shift}}',
        shiftSelected: '已选择{{shift}} — 点击清除',
        shiftUnavailable: '此门店未配置{{shift}}',
      },
      itemsPerPage: '每页条数',
      range: '第 {{start}} - {{end}} 条，共 {{total}} 条',
      date: '日期',
      time: '时间',
      timeAndGuests: '时间与人数',
      customer: '客户',
      guests: '客人数',
      guestCount: '{{count}} 位客人',
      childrenCount: '+ {{count}} 位儿童',
      event: '活动',
      noEvent: 'no event',
      contacts: '联系方式',
      details: '详情',
      openImage: '打开图片',
      openPdf: '打开预订 PDF',
      googleReservation: '来自 Google Reservation 的预订',
      walkIn: 'Walk in',
      associatedServicesProducts: '关联服务/产品',
      generatedBooking: '生成的预订 {{score}}/100',
      extraServices: '附加服务 {{count}}',
      extraServicesOnly: '附加服务',
      tables: '已分配桌位',
      noTables: '未分配桌位',
      notes: '私人备注',
      noNotes: '无私人备注',
      manageBooking: '管理预订',
      eventDetails: {
        title: '活动详情',
        eventName: '活动名称',
        eventCode: '活动代码',
        startDate: '开始',
        maxCapacity: '人数上限',
        eventDescription: '描述',
        eventImage: '活动图片',
        formCodes: '表单',
        noImage: '无图片',
      },
      menuDetails: {
        title: '菜单详情',
        menuName: '菜单名称',
        peopleCount: '人数',
      },
      viewBookingImage: '查看预订图片',
      bookingImage: '预订图片',
      users: 'users',
      status: '状态',
      processedBy: '处理人',
      source: '来源',
      noBranch: '请选择分店以查看预订历史',
      empty: '未找到预订',
      emptyDesc: '{{date}} 当天没有预订。',
      updateStatus: '更新预订状态',
      confirmOperationTitle: '确认操作？',
      confirmStatusChange: '将预订状态更新为“{{status}}”？',
      notifyCustomerTitle: '通知客户？',
      notifyCustomerMessage: '是否要将此变更通知客户？',
      statusUpdateSuccess: '预订状态更新成功。',
      statusUpdateError: '预订状态更新失败，请重试。',
      updatingStatus: '正在更新状态...',
      associatedServices: {
        title: '关联服务/产品',
        reference: '预订 {{code}}',
        quantity: '×{{count}}',
        empty: '此预订没有关联服务。',
        paid: '已付款',
        unpaid: '未付款',
      },
      messageHistory: {
        open: '消息记录',
        communicationError: '消息发送失败',
        title: '消息记录',
        reference: '参考 {{code}}',
        refresh: '刷新',
        empty: '此预订尚未发送任何消息。',
        loadError: '加载消息记录失败。',
        endpoint: '端点',
        eventCode: '事件代码',
        payload: '载荷',
        response: '响应',
        statusProcessed: '已发送',
        statusPending: '待处理',
        messageText: '消息',
        messageTextUnavailable: '消息文本不可用。',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} 次尝试',
        resubmit: '重新发送消息',
        resubmitSuccess: '消息重新发送成功。',
        resubmitError: '消息重新发送失败，请重试。',
        resubmitConfirmTitle: '重新发送消息？',
        resubmitConfirmMessage: '确定要重新发送此消息吗？',
      },
      statusActions: {
        confirm: '确认',
        refuse: '拒绝',
        eliminate: '取消',
        arrived: '已到店',
        notArrived: '未到店',
        notifyUser: '通知用户前往门店',
        bookingAlreadyProcessed: '预订已处理',
        moveToConfirm: '移至已确认',
      },
      statuses: {
        IN_ATTESA: '待处理',
        MODIFICATO_DA_UTENTE: '用户已修改',
        ELIMINATO_DA_UTENTE: '用户已删除',
        MODIFICA_CONFERMATA: '修改已确认',
        MODIFICA_RIFIUTATA: '修改已拒绝',
        PRE_CONFERMA_PAGAMENTO: '付款预确认',
        CONFERMATO: '已确认',
        AVVISATO_LISTA_ATTESA: '等候名单已通知',
        ARRIVATO: '已到达',
        NON_ARRIVATO: '未到场',
        RIFIUTATO: '已拒绝',
        LISTA_ATTESA: '等候名单',
        ELIMINATO: '已取消',
      },
    },
    payment: {
      column: '付款',
      close: '关闭',
      connectRequired:
        '此门店尚未完成 Stripe Connect。请先完成入驻后再管理付款。',
      loadError: '无法加载此预订的付款操作。',
      linkCopied: '付款链接已复制。',
      linkCopyError: '无法复制付款链接。',
      linkReadyTitle: '定金链接已就绪',
      linkReadyDescription: '链接已准备好，可通过你的渠道发送。',
      badge: {
        title: '付款',
        cardPresent: '已有卡片',
        cardMissing: '无卡片',
        paidPendingOps: '已付款 · 待处理操作',
        manage: '管理',
        noCard: '未关联卡片',
        verifiedSuccess: '验证成功',
        depositAuthorizedCapture: '定金已授权 · 扣款',
        depositCaptured: '定金已扣款',
        depositRefunded: '定金已退款',
        depositPartiallyRefunded: '定金部分退款',
        depositPrefix: '定金',
      },
      tooltip: {
        cardPresent: '打开该门店已保存的卡片信息',
        cardMissing: '向客户发送卡片信息请求',
        openOperations: '打开此预订的定金或付款操作',
        openVerified: '打开付款详情与已验证卡片',
        noCard: '未关联卡片。打开以管理付款',
      },
      status: {
        notRequired: '不需要',
        pending: '等待付款',
        awaitingConfirmation: '付款已发送，等待 Stripe 确认',
        cardVerified: '卡片已验证',
        authorized: '未扣款',
        paid: '已付款',
        partiallyPaid: '部分付款',
        failed: '失败',
        canceled: '已取消',
        refundPending: '退款处理中',
        partiallyRefunded: '部分退款',
        refunded: '已退款',
        refundFailed: '退款失败',
      },
      actions: {
        copyLink: '复制链接',
        refund: '退款',
      },
      modal: {
        title: '定金',
        description: '查看预授权、生成链接，并决定扣取或释放定金。',
        bookingLabel: '预订 {{code}}',
        pendingWarning: '一笔或多笔付款已完成，但仍有待处理操作或链接。',
        requested: '已请求',
        captured: '已扣款',
        authorized: '已授权',
        netCaptured: '净扣款',
        refunded: '已退款',
        descriptionLabel: '描述',
        operationsTab: '操作（{{count}}）',
        newRequestTab: '新请求',
        historyTitle: '操作历史',
        historyDescription: '与此预订关联的定金和授权。',
        emptyTitle: '暂无操作',
        emptyDescription: '打开“新请求”选项卡以生成第一个链接。',
        createRequest: '创建请求',
      },
      create: {
        title: '创建定金链接',
        description: '客户授权金额；之后你可以扣款或释放。',
        amountLabel: '定金金额（€）',
        descriptionLabel: '可选描述',
        descriptionPlaceholder: '例如：预订定金',
        authorizeOnlyHint: '金额仅被授权。你可以稍后扣款或释放。',
        submit: '生成定金链接',
        invalidAmount: '请输入大于零的金额。',
        error: '无法创建 Stripe 链接。',
        success: '定金链接已创建。',
        sent: '定金链接已创建并发送。',
        linkCreatedMessageFailed: '链接已创建，但定金请求消息未发送。',
      },
      refund: {
        title: '退款',
        maxAmount: '最高可退金额：{{amount}}',
        amountLabel: '金额（€）',
        reasonLabel: '原因',
        invalidAmount: '请输入不超过可退上限的有效金额。',
        started: '退款已开始。',
        completed: '退款已完成。',
        error: '退款失败。请检查 Stripe 状态后重试。',
        reasons: {
          requested_by_customer: '客户申请',
          duplicate: '重复付款',
          fraudulent: '欺诈',
        },
      },
    },
    create: {
      title: '创建预订',
      cancel: '取消',
      submit: '创建预订',
      customerDetails: '客户详情',
      bookingDetails: '预订详情',
      firstName: '名 *',
      firstNamePlaceholder: '请输入名',
      lastName: '姓 *',
      lastNamePlaceholder: '请输入姓',
      email: '邮箱',
      emailPlaceholder: '请输入邮箱',
      phone: '电话号码 *',
      phonePlaceholder: '请输入电话号码',
      nickName: '昵称',
      nickNamePlaceholder: '请输入昵称',
      bookingImage: '预订图片',
      pdfDocument: 'PDF 文档',
      bookingTime: '预订时间 *',
      bookingTimePlaceholder: '选择时间',
      guests: '人数 *',
      guestsPlaceholder: '请输入人数',
      children: '儿童 *',
      childrenPlaceholder: '请输入儿童数',
      customerRequests: '客户要求',
      customerRequestsPlaceholder: '请输入客户要求',
      restaurantNotes: '餐厅备注',
      restaurantNotesPlaceholder: '请输入餐厅备注',
    },
    calendar: {
      selected: '已选择：{{date}}',
      dotsHint: '圆点表示可用预订',
    },
  },
  forms: {
    title: '表单预订',
    subtitle: '管理所有活动、暂停或归档的表单',
    addDataCollectionForm: '添加表单',
    searchPlaceholder: '按名称搜索表单',
    noDataTitle: '暂无表单',
    noDataDesc: '您尚未创建任何表单。请先创建第一个表单以开始收集预约和互动。',
    status: '状态',
    active: '启用',
    suspended: '已暂停',
    url: '链接',
    qrCode: '二维码',
    preview: '预览',
    googleBooking: 'Google 预订',
    googleReservation: 'Google 预订',
    copyUrl: '复制链接',
    downloadQr: '下载二维码',
    editForm: '编辑表单',
    dublicateUrl: '复制表单',
    statusActive: '启用',
    statusInactive: '停用',
    statusDraft: '草稿',
    statsCount: '{{count}} 个表单',
    updatedAgo: '2 分钟前更新',
    deleteForm: '删除表单',
    share: {
      label: '分享表单',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: '立即预订',
      instagramCopied: '链接已复制。请粘贴到 Instagram 进行分享。',
      instagramOpened: '已打开 Instagram。表单链接已复制 — 粘贴即可分享。',
      copyFailed: '无法复制链接，请重试。',
    },
    createTitle: '创建表单',
    formName: '表单名称',
    formNamePlaceholder: '输入表单名称',
    createForm: '创建表单',
    toast: {
      createSuccess: '表单创建成功。',
      createError: '表单创建失败，请重试。',
      deleteSuccess: '表单删除成功。',
      deleteError: '表单删除失败，请重试。',
      duplicateSuccess: '表单复制成功。',
      duplicateError: '表单复制失败，请重试。',
      statusActivateSuccess: '表单已启用。',
      statusSuspendSuccess: '表单已暂停。',
      statusActivateError: '启用表单失败，请重试。',
      statusSuspendError: '暂停表单失败，请重试。',
      googleReservationEnabled: '{{name}} 已启用 Google 预订',
      googleReservationDisabled: '{{name}} 已停用 Google 预订',
      googleReservationError: '无法更新 Google 预订，请重试。',
      logoUploadSuccess: 'Logo 上传成功。',
      logoUploadError: 'Logo 上传失败。',
      logoRemoveSuccess: 'Logo 删除成功。',
      logoRemoveError: 'Logo 删除失败。',
      bannerUploadSuccess: '横幅上传成功。',
      bannerUploadError: '横幅上传失败。',
      bannerRemoveSuccess: '横幅删除成功。',
      bannerRemoveError: '横幅删除失败。',
    },
    deleteModel: {
      deleteTitle: '删除表单？',
      deleteDescription: '确定要删除此表单吗？此操作无法撤销。',
    },
    validation: {
      formNameMin: '表单名称至少需要 2 个字符',
      formNameMax: '表单名称不能超过 50 个字符',
    },
    scanQrAccess: '扫描二维码访问：',
    qrClose: '关闭',
    qrDownload: '下载',
    edit: {
      pageTitle: '个人数据同意',
      editTitle: '编辑 {{name}}',
      subtitle: '配置预订表单',
      saveChanges: '保存更改',
      close: '关闭',
      toast: {
        saveSuccess: '表单保存成功。',
        saveError: '表单保存失败。',
      },
      tabListLabel: '表单编辑分区',
      uploading: '上传中...',
      removing: '删除中...',
      tabs: {
        general: '常规',
        capacity: '容量',
        schedule: '时间表',
        formFields: '表单字段',
        preferences: '偏好',
        services: '服务',
      },
      general: {
        media: '媒体',
        logo: 'Logo',
        banner: '横幅图片',
        bannerHint: '横幅图片 — 拖放或浏览上传',
        basicDetails: '基本信息',
        formName: '表单名称',
        customerName: '面向客户的名称',
        address: '表单上显示的门店地址',
        customThankYouPage: '自定义感谢页',
        buttonTextOnBookingForm: '预订表单按钮文字',
        policy: '政策',
        webLink: '网页链接',
      },
      capacity: {
        hint: '此表单的预订限制与桌位规则。',
        sections: {
          coverLimits: '人数限制',
          timeSettings: '时段设置',
          policies: '政策',
        },
        maxGuests: '通话前的最大人数',
        maxGuestsHint: '您的餐厅在需要电话确认前最多接受多少人数？',
        maxTableGuests: '每桌最大人数',
        maxTableGuestsHint:
          '设置单桌可接待的最大客人数量。若将值留为 0，则上限等于场地的总座位数。',
        minGuests: '每桌最少人数',
        minGuestsHint: '单桌可接待的最少人数。',
        phoneOverbooking: '超额预订电话',
        phoneOverbookingHint:
          '客人超过每桌上限时显示的电话。格式：区号 + 号码（例如 +39 3444393343）。',
        cut: '切割',
        cutHint: '按此时长将可用时间拆分为预订时段。',
        cutSlotOption: '将时段划分为 {{minutes}} 分钟的时间片',
        stayOnTable: '在桌停留时间',
        stayOnTableHint: '每次预订预计用餐时长。',
        minutes: '{{count}} 分钟',
        dogsAccepted: '接受携带宠物狗',
        dogsAcceptedHint: '允许顾客标明是否携带狗狗。',
        menuSelectionMandatory: '菜单选择必填',
        menuSelectionMandatoryHint:
          '启用后，客人必须为每位客人选择菜单（或“无菜单”），使总数等于成人 + 儿童后才能继续。',
        showRestaurantNoMenu: '显示餐厅无固定菜单卡片',
        showRestaurantNoMenuHint:
          '启用后，公开预订表单会显示一张说明餐厅没有固定菜单的卡片。',
      },
      schedule: {
        shiftOverview: '班次概览',
        weeklyHours: '每周营业时间',
        addTimeSlot: '添加时段',
        day: '日期',
        open: '营业',
        closed: '关闭',
        hours: '时间',
        editSlot: '编辑时段',
        deleteSlot: '删除时段',
        deleteSlotConfirmTitle: '删除此时段？',
        deleteSlotConfirmDesc: '将从该日移除所选时段。',
        deleteSlotConfirmMultiDay:
          '此服务在 {{count}} 天使用相同代码。删除后将从所有这些天移除。',
        extraordinarySection: '特殊营业 / 关闭',
        addException: '添加例外',
        dateRange: '日期范围',
        type: '类型',
        closedOpenDay: '关闭/营业',
        timeSlots: '时段',
        cuttingMin: '切割（分钟）',
        maxGuests: '最大客人数',
        noShifts: '尚未配置班次。',
        emptyWeeklyHours: '暂无每周营业时间。使用 + 为所有日期创建配置。',
        emptyExtraordinary: '尚未配置特殊营业或关闭。',
        createForAllDays: '为所有日期创建通用配置',
        resetConfiguration: '重置配置',
        saveConfiguration: '保存配置',
        resetConfirmTitle: '删除所有现有配置？',
        resetConfirmDesc: '这将删除此表单的所有时间表设置。',
        resetProceed: '继续',
        tags: '标签',
        tagsPlaceholder: '输入以逗号分隔的标签',
        tagsPickerDesc: '为此时段选择标签。',
        tagsPickerEmpty: '此分店暂无可用标签。',
        allowOverbook: '允许超额预订',
        alertMessage: '提醒消息',
        alertMessagePlaceholder: '为此时段输入提醒消息',
        enableOverbookBlocking: '启用超额预订拦截',
        disableOverbookBlocking: '禁用超额预订拦截',
        deposit: {
          tooltipInactive: '配置押金 / 验卡',
          tooltipValidation: 'Stripe：仅验证银行卡',
          tooltipDeposit: 'Stripe 押金：每位客人 {{amount}}',
          tooltipMixed: '各时段的 Stripe 配置不一致',
          allStandardBands: '所有标准时段',
          allExtraordinaryBands: '所有特别时段',
          singleBand: '所选时段',
          depositTitle: '押金',
          checkCardTitle: '验卡',
          bulkHint: '此配置将一次性应用到本节中的所有时段。',
          requestTitle: '要求信用卡',
          requestDesc: '您可以仅要求验卡，也可以要求预授权押金。',
          validationTitle: '验卡',
          validationDesc: '验证并绑定卡片，不冻结金额。',
          depositOptionTitle: '押金',
          depositOptionDesc: '验证卡片并预授权设定金额。',
          amountLabel: '金额 EUR',
          amountHint: '输入大于零的金额以启用押金。',
          saveConfiguration: '保存配置',
          close: '关闭',
          toastSaveSuccess: '押金配置已保存。',
          toastSaveError: '保存押金配置失败。',
        },
        legacyCreditCard: {
          enable: '启用直接请求信用卡信息',
          disable: '停用直接请求信用卡信息',
        },
        maxGuestsPerSlot: '每个时段最大接待人数',
        customizeCutter: '自定义标准配置的时段切割',
        noSlotsForDay: '该日暂无时段。使用 + 添加。',
        slotClosedOnDate: '此时段在 {{date}} 不可用',
        alertModalTitle: '选择此时段时向顾客显示的提醒',
        maxGuestsModalTitle: '为此时段 {{range}} 设置最大座位数',
        maxGuestsModalDesc: '选择此时段允许的最大客人数。',
        cutterModalTitle: '更新时段切割',
        days: {
          monday: '星期一',
          tuesday: '星期二',
          wednesday: '星期三',
          thursday: '星期四',
          friday: '星期五',
          saturday: '星期六',
          sunday: '星期日',
        },
        addTimeAllDays: {
          title: '为所有日期添加时间',
          subtitle: '可用时间遵循场地的时段及已配置的时段。',
          chosenTime: '已选时间',
          duration: '时长：{{duration}}',
          startAt: '1. 开始于',
          endsAt: '2. 结束于',
          allOkDesc: '您可以将此时间表保存到所有日期。',
          saveOnAllDays: '保存到所有日期',
        },
        slotTime: {
          title: '时段',
          subtitle: '请先选择开始时间，再选择结束时间。',
          allOk: '一切正常',
          allOkDesc: '您可以保存此时间。',
          saveTime: '保存时间',
          timeRangeInvalid: '结束时间必须晚于开始时间。',
          timeRangeConflict: '该时间段与其他时间段冲突，请修改或删除其中一个。',
          occupiedTitle: '已被占用的时间',
          occupiedHint: '第二个列表会自动避开这些时间。',
          cannotSave: '请更改时间',
          cannotSaveDesc: '此开始时间没有有效的结束时间。',
          cannotSaveButton: '无法保存',
          durationMinutes: '{{count}} 分钟',
          durationHours: '{{count}} 小时',
          durationHoursMinutes: '{{hours}} 小时 {{minutes}} 分钟',
          selectService: '选择服务',
          selectServiceDesc: '选择一个用餐时段以添加其营业时间。',
          noLocalSlots:
            '本地配置中的时段已删除。请先在那里重新创建，再添加到此日程。',
          selectedCount: '已选择 {{count}} 个服务',
          alreadyAdded: '已添加',
        },
        extraordinary: {
          badge: '特殊配置',
          createRange: '创建范围',
          editRange: '编辑范围',
          description: '描述',
          descriptionPlaceholder: '例如节假日、私人活动...',
          rangeDate: '日期范围',
          rangeDatePlaceholder: '选择日期范围',
          clearDates: '清除',
          applyDates: '应用',
          open: '营业',
          create: '创建',
          update: '更新',
          validation: '描述和日期范围为必填项。',
          dateRequired: '请选择日期范围。',
          dateOrder: '结束日期不能早于开始日期。',
          timeRangeInvalid: '结束时间必须晚于开始时间。',
          addConfiguration: '添加特殊配置',
          saveConfiguration: '保存配置',
          addTimeSlot: '添加时段',
          deleteRange: '删除范围',
          deleteConfirmTitle: '删除特殊范围？',
          deleteConfirmDesc: '这将永久删除此日期范围配置。',
          toast: {
            createSuccess: '特殊范围已创建。',
            updateSuccess: '特殊范围已更新。',
            saveError: '保存特殊范围失败。',
            deleteSuccess: '特殊范围已删除。',
            deleteError: '删除特殊范围失败。',
          },
        },
        toast: {
          saveSuccess: '时间表配置已保存。',
          saveError: '保存时间表失败。',
          resetSuccess: '时间表配置已重置。',
          resetError: '重置时间表失败。',
          addSlotSuccess: '时段已添加。',
          addSlotError: '添加时段失败。',
          deleteSlotSuccess: '时段已删除。',
          deleteSlotError: '删除时段失败。',
          updateTimeSuccess: '时段已更新。',
          updateTimeError: '更新时段失败。',
          dayToggleSuccess: '日期状态已更新。',
          dayToggleError: '更新日期状态失败。',
          configurationMissing: '该日配置尚不可用。',
          slotNotReady: '时段尚未就绪，请刷新后重试。',
          overbookToggleSuccess: '超额预订设置已更新。',
          overbookToggleError: '更新超额预订设置失败。',
          legacyCreditCardToggleSuccess: '信用卡请求已更新。',
          legacyCreditCardToggleError: '更新信用卡请求失败。',
          tagsSaveSuccess: '标签已保存。',
          tagsLoadError: '加载标签失败。',
          tagsSaveError: '保存标签失败。',
          tagsDeleteSuccess: '标签已移除。',
          alertSaveSuccess: '提醒消息已保存。',
          alertSaveError: '保存提醒消息失败。',
          alertDeleteSuccess: '提醒消息已移除。',
          maxGuestsInvalid: '请输入有效的客人数。',
          maxGuestsSaveSuccess: '最大客人数已更新。',
          maxGuestsSaveError: '更新最大客人数失败。',
          cutterSaveSuccess: '时段切割已更新。',
          cutterSaveError: '更新时段切割失败。',
        },
      },
      formFields: {
        hint: '选择在公共预订表单上显示哪些字段。',
        configure: '配置表单字段',
        configureDesc: '切换预订过程中向顾客显示的字段。',
        name: '名',
        surname: '姓',
        phone: '电话',
        email: '邮箱',
        dateOfBirth: '出生日期',
        zipCode: '邮编（CAP）',
        customerNotes: '顾客备注',
        field: '字段',
        mandatory: '必填',
        nonMandatory: '非必填',
      },
      preferences: {
        sectionTitle: '附加选项',
        sectionDescription:
          '额外配置，方便顾客更轻松地说明偏好、过敏或不耐受情况。',
        singleChoicePredefined: '按建议填写列表以创建单选偏好选项。',
        singleChoiceAdditional: '创建额外的单选偏好选项',
        multiChoicePredefined: '按建议填写列表以创建多选偏好选项。',
        multiChoiceAdditional: '创建额外的多选偏好选项',
        singleChoiceOptionsPlaceholder: '示例：户外区 | 室内区 | 吧台',
        multiChoiceOptionsPlaceholder: '例：乳糖 | 麸质 | 花生',
        singleChoiceFieldPlaceholder: '例：单选字段名称',
        multiChoiceFieldPlaceholder: '例：多选字段名称',
        pipeSeparatedNote: '注意：请使用 | 分隔各个值。',
        importRooms: '导入我的房间',
        importRoomsSuccess: '房间导入成功。',
        importRoomsError: '导入房间失败。',
        importRoomsEmpty: '此分店未找到房间。',
        importRoomsNotFound: '此分店未找到房间。请检查分店代码或先创建房间。',
        suggestAllergens: '为我推荐完整过敏原列表',
        validation: {
          fillRequired:
            '勾选 Mandatory 或 Non-mandatory 时，必须填写标签和选项。',
          labelRequired: '启用该选项时，标签为必填项。',
          optionsRequired: '启用该选项时，请至少添加一个以 | 分隔的选项。',
        },
      },
      services: {
        sectionTitle: '附加产品与服务',
        sectionDescription: '创建与预订表单关联的产品和服务列表。',
        mandatoryLabel: '我希望产品或服务选择为必填',
        title: '附加产品与服务',
        description: '在预订流程中追加销售附加项。',
        add: '添加产品或服务',
        name: '名称',
        descriptionCol: '描述',
        price: '价格',
        pax: '人数',
        empty: '此表单尚未关联任何产品或服务。',
        pickerTitle: '服务/产品列表',
        pickerDesc: '选择一项或多项关联到此表单。',
        pickerEmpty: '此分店暂无可用服务或产品。',
        deleteTitle: '移除服务/产品？',
        deleteDesc: '这将从该预订表单中移除所选项目。',
        toast: {
          saveSuccess: '服务与产品已更新。',
          saveError: '更新服务与产品失败。',
          deleteSuccess: '服务/产品已移除。',
          deleteError: '移除服务/产品失败。',
          loadError: '加载服务与产品失败。',
        },
      },
      formConfiguration: '表单配置',
      formConfigurationDesc: '常规表单配置',
      formImage: '表单图片',
      formImageHint: '建议使用透明背景的 PNG 标志。双击图片可立即跳转。',
      formBackgroundImage: '表单背景图',
      imageUpload: '上传图片',
      removeImage: '删除图片',
      redirectAfterCompilation: '填写后重定向',
      linkForm: '表单链接',
      buttonText: '按钮文字',
      dataFields: '数据字段',
      requestEmail: '请求邮箱',
      requestAddress: '请求地址',
      requestNationality: '请求国籍',
      requestZipCode: '请求邮编',
      requestDateOfBirth: '请求出生日期',
      tagsApplied: '应用于构建的标签',
      tagsPlaceholder: '选择标签',
      tagsHint: '逗号分隔的标签名称',
      validation: {
        buttonTextRequired: '按钮文字为必填项',
      },
    },
  },
  customers: {
    title: '我的客户',
    subtitle: '{{branch}} 的客户',
    subtitleDefault: '查看和管理客户列表',
    statsCount: '{{count}} 位客户',
    searchPlaceholder: '搜索姓名、邮箱或电话',
    customer: '客户',
    surname: '姓',
    name: '名',
    email: '邮箱',
    phone: '电话',
    contacts: '联系方式',
    dateOfBirth: '出生日期',
    reservations: '预订',
    noShow: '未到店',
    tags: '标签',
    visits: '到访',
    presenceCount: '{{count}} 次到访',
    noShowCount: '{{count}} 次未到店',
    registrationDate: '注册日期',
    itemsPerPage: '每页条数',
    range: '第 {{start}} - {{end}} 条，共 {{total}} 条',
    noBranch: '请选择分店以查看客户',
    empty: '未找到客户',
    emptyDesc: '该分店暂无客户。',
    downloadPdf: '下载 pdf',
    downloadPdfSuccess: '预订历史 PDF 已下载。',
    downloadPdfError: '下载预订历史 PDF 失败。',
    downloadPdfEmpty: '未找到该客户的预订记录。',
    downloadPdfMissingPhone: '该客户没有电话号码。',
    importExport: {
      menuLabel: '导入/导出',
      import: '导入客户列表',
      export: '导出客户列表',
      downloadSample: '下载示例列表',
      downloadSampleSuccess: '示例文件已下载。',
      downloadSampleError: '下载示例文件失败。',
      exportSuccess: '客户列表已导出。',
      exportError: '导出客户列表失败。',
      dialogTitle: '导入客户列表',
      dialogDescription: '选择 CSV 或 XLSX 文件以导入客户。',
      chooseFile: '选择文件',
      selectedFile: '已选文件',
      upload: '上传',
      uploading: '正在上传客户列表...',
      cancel: '取消',
      close: '关闭',
      invalidType: '请选择 CSV 或 XLSX 文件。',
      importError: '导入客户列表失败，请重试。',
      importAnother: '导入另一个文件',
      totalRecords: '总记录数',
      successfulImports: '成功导入',
      failedImports: '失败导入',
    },
    create: {
      title: '创建客户',
      action: '客户',
      tooltip: '添加客户',
      submit: '创建客户',
      cancel: '取消',
      firstName: '名',
      firstNamePlaceholder: '输入名字',
      lastName: '姓',
      lastNamePlaceholder: '输入姓氏',
      mobile: '手机号码',
      mobilePlaceholder: '输入手机号码',
      emailPlaceholder: '输入邮箱地址',
      nickname: '客户昵称',
      nicknamePlaceholder: '输入昵称',
      tagsTitle: '客户标签',
      tagsSubtitle: '选择与客户关联的标签。',
      tagsPlaceholder: '选择标签',
      success: '客户创建成功。',
      error: '创建客户失败，请重试。',
      loading: '正在创建客户...',
    },
    bookingHistoryPdf: {
      title: '预订历史',
      generatedMeta: '生成于 {{date}} · {{count}} 条预订',
      customerCode: '客户代码：{{code}}',
      phone: '电话',
      email: '邮箱',
      birthDate: '出生日期',
      address: '地址',
      registration: '注册日期',
      tags: '标签',
      notAvailable: '不可用',
      guestsWithChildren: '{{guests}} + {{children}} 位儿童',
      columns: {
        date: '日期',
        time: '时间',
        code: '代码',
        status: '状态',
        guests: '客人数',
        source: '来源',
      },
      sources: {
        WEB: '网页',
        APP_CUSTOMER: '门户',
        APP: '应用',
      },
    },
    filters: {
      title: '筛选',
      tags: '标签',
      birthYears: '出生年份',
      birthMonth: '出生月份',
      clearAll: '清除所有筛选',
      noTags: '暂无可用标签',
      onlyWithEmail: '已配置邮箱',
      marketingConsent: '营销同意',
      blacklist: '黑名单客户',
      phonePrefix: '国家区号',
      allPhonePrefixes: '全部区号',
      searchOptions: '搜索...',
      noSearchResults: '无匹配选项',
      minimumBookings: '最少预订次数',
      selectMinimumBookings: '选择最少预订次数',
      form: '表单',
      selectForm: '全部表单',
      noForms: '暂无可用表单',
      bookingPeriodTitle: '期间内的预订',
      bookingRangeLabel: '预订日期范围',
      bookingRangePlaceholder: '选择预订日期范围',
      removePeriod: '清除日期范围',
      minimumBookingsOptions: {
        '1': '至少 1 次预订',
        '2': '至少 2 次预订',
        '3': '至少 3 次预订',
        '4': '至少 4 次预订',
        '5': '至少 5 次预订',
        '10': '至少 10 次预订',
        '15': '至少 15 次预订',
        '20': '至少 20 次预订',
        '30': '至少 30 次预订',
        '50': '至少 50 次预订',
      },
      months: {
        '1': '一月',
        '2': '二月',
        '3': '三月',
        '4': '四月',
        '5': '五月',
        '6': '六月',
        '7': '七月',
        '8': '八月',
        '9': '九月',
        '10': '十月',
        '11': '十一月',
        '12': '十二月',
      },
    },
  },
  customerDetails: {
    title: '客户详情',
    subtitle: '{{branch}} 的客户详情',
    subtitleDefault: '查看和管理客户详情',
    deleteModelTitle: '删除客户？',
    deleteModelDescription: '删除客户后，与其关联的预订也将被删除。',
    deleteModelConfirmText: '是，删除',
    deleteModelCancelText: '取消',
    card: {
      title: '客户卡片',
      close: '关闭',
      delete: '删除',
      update: '更新客户',
      name: '名',
      surname: '姓',
      nickname: '昵称',
      mobile: '手机',
      address: '地址',
      city: '城市',
      province: '省份',
      postalCode: '邮编',
      country: '国家',
      personalDataTitle: '个人资料',
      personalDataSubtitle: '姓名和主要联系方式。',
      phoneTitle: '电话',
      phoneSubtitle: '国家代码和手机号码。',
      addressTitle: '地址',
      addressSubtitle: '地理数据和客户联系信息。',
      birthDateTitle: '出生日期',
      birthDateSubtitle: '存储格式：yyyy-MM-dd。',
      tagsTitle: '客户标签',
      tagsSubtitle: '选择与客户关联的标签。',
      tagsPlaceholder: '选择标签',
      updateSuccess: '客户更新成功。',
      updateError: '客户更新失败，请重试。',
      deleteSuccess: '客户删除成功。',
      deleteError: '客户删除失败，请重试。',
    },
  },
  dcfQuestions: {
    title: '问题和答案标签',
    description:
      '在同一个表单中收集客户资料和答案。可为已回答的问题和所选选项关联标签。',
    backendRequired:
      '问题功能需要更新 API。你可以在此配置问题，但服务支持前无法保存。',
    saveMismatch:
      'API 未返回已保存的问题。你的修改仍保留在此处，请联系 API 团队。',
    unavailable: '此表单暂时无法接收答案，请稍后重试。',
  },
  dataCollectionForms: {
    title: '数据收集表单',
    subtitle: '数据收集表单列表',
    addDataCollectionForm: '添加表单',
    searchPlaceholder: '按名称搜索数据收集表单',
    noDataTitle: '没有数据收集表单',
    noDataDesc: '创建您的第一个数据收集表单以开始收集潜在客户和互动。',
    form: '表单',
    tags: '标签',
    tagsMore: '+{{count}} 更多',
    tagsLess: '收起',
    status: '状态',
    interactions: '互动次数',
    interactionsCount: '{{count}} 次互动',
    url: 'URL',
    qrCode: '二维码',
    preview: '预览',
    copyUrl: '复制链接',
    downloadQr: '下载二维码',
    editDataCollectionForm: '编辑数据收集表单',
    statusActive: '启用',
    statusInactive: '停用',
    statusSuspended: '已暂停',
    statusDraft: '草稿',
    statsCount: '{{count}} 个数据收集表单',
    updatedAgo: '2 分钟前更新',
    deleteDataCollectionForm: '删除数据收集表单',
    duplicate: '复制',
    createTitle: '创建数据收集表单',
    formName: '表单名称',
    formNamePlaceholder: '输入表单名称',
    createForm: '创建表单',
    deleteModel: {
      deleteTitle: '删除数据收集表单？',
      deleteDescription: '您确定要删除此表单吗？此操作无法撤销。',
    },
    validation: {
      formNameMin: '名称至少需要 2 个字符',
      formNameMax: '名称不能超过 255 个字符',
    },
    scanQrAccess: '扫描二维码访问：',
    qrClose: '关闭',
    qrDownload: '下载',
    toast: {
      createSuccess: '表单创建成功。',
      createError: '表单创建失败，请重试。',
      updateSuccess: '表单更新成功。',
      updateError: '表单更新失败，请重试。',
      logoUploadSuccess: 'Logo 上传成功。',
      logoUploadError: 'Logo 上传失败。',
      logoRemoveSuccess: 'Logo 删除成功。',
      logoRemoveError: 'Logo 删除失败。',
      bgUploadSuccess: '背景上传成功。',
      bgUploadError: '背景上传失败。',
      bgRemoveSuccess: '背景删除成功。',
      bgRemoveError: '背景删除失败。',
      statusUpdateSuccess: '状态更新成功。',
      statusUpdateError: '状态更新失败，请重试。',
      deleteSuccess: '表单删除成功。',
      deleteError: '表单删除失败，请重试。',
      duplicateSuccess: '表单复制成功。',
      duplicateError: '表单复制失败，请重试。',
    },
    edit: {
      pageTitle: '个人数据同意',
      subtitle: '配置数据收集表单',
      close: '关闭',
      formConfiguration: '表单配置',
      formConfigurationDesc: '数据收集表单常规配置',
      formImage: '表单图片',
      formImageHint: '建议使用透明背景的 PNG 标志。双击图片可立即跳转。',
      formBackgroundImage: '表单背景图',
      imageUpload: '上传图片',
      removeImage: '删除图片',
      redirectAfterCompilation: '填写后重定向',
      linkForm: '表单链接',
      buttonText: '按钮文字',
      description: '描述',
      descriptionPlaceholder: '显示在公开表单左侧的文字',
      dataFields: '数据字段',
      requestEmail: '请求邮箱',
      requestAddress: '请求地址',
      requestNationality: '请求国籍',
      requestZipCode: '请求邮编',
      requestDateOfBirth: '请求出生日期',
      tagsApplied: '应用于表单的标签',
      tagsOnDownload: '下载时应用的标签',
      tagsOnUse: '保存数据后分配的标签',
      tagsPlaceholder: '选择标签',
      tagsHint: '逗号分隔的标签名称',
      uploading: '上传中...',
      removing: '删除中...',
      validation: {
        redirectUrlRequired: '重定向 URL 为必填项。',
        redirectUrlInvalid: '请输入有效的 URL（例如 https://example.com）。',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: '数据收集表单',
    loading: '正在加载表单',
    missingFormCode: 'URL 中缺少表单代码。',
    loadError: '无法加载此数据收集表单。',
    unavailableTitle: '表单不可用',
    unavailableMessage: '此数据收集表单当前已暂停或不可用。',
    personalInfo: '个人信息',
    prefixLabel: '区号',
    phoneLabel: '手机',
    phonePlaceholder: '你的号码',
    firstName: '名',
    firstNamePlaceholder: '你的名字',
    lastName: '姓',
    lastNamePlaceholder: '你的姓氏',
    email: '邮箱',
    emailPlaceholder: 'name@example.com',
    optional: '选填',
    cap: '邮政编码',
    capPlaceholder: '例如 09124',
    birthDate: '出生日期',
    address: '地址',
    addressPlaceholder: '街道和门牌号',
    nationality: '国籍',
    nationalityPlaceholder: '你的国籍',
    requiredNote: '* 必填项',
    consentsTitle: '你的偏好',
    privacyConsent: '隐私与数据处理',
    marketingConsent: '营销与通信同意',
    profilingConsent: '画像分析同意',
    detailsLink: '隐私声明',
    selectAll: '全选',
    submit: '进入页面',
    welcomeBackTitle: '欢迎回来，{{name}}！',
    welcomeBackTitleGeneric: '欢迎回来！',
    welcomeBackMessage:
      '我们已有您的资料。如需为其他人登记，可以添加另一个联系人。',
    addAnotherContact: '添加另一个联系人',
    toast: {
      success: '数据提交成功。',
      error: '提交数据失败，请重试。',
    },
    thankYou: {
      title: '谢谢！',
      message: '您的数据已成功提交。',
      continue: '继续',
    },
    validation: {
      prefixRequired: '请选择国家区号。',
      phoneMin: '请输入有效的手机号。',
      firstNameRequired: '名为必填项。',
      lastNameRequired: '姓为必填项。',
      emailRequired: '邮箱为必填项。',
      emailInvalid: '请输入有效的邮箱地址。',
      birthDateInvalid: '请输入有效且不是未来的日期。',
      privacyRequired: '必须同意隐私条款。',
    },
  },
  bulkMessage: {
    title: '群发消息',
    subtitle: '管理 {{branch}} 的 WhatsApp、邮件和短信活动',
    subtitleDefault: '查看并管理您的群发消息活动',
    statsCount: '{{count}} 个活动',
    searchPlaceholder: '搜索群发邮件...',
    campaignName: '活动名称',
    campaignNamePlaceholder: '输入活动名称',
    addCampaign: '新建活动',
    creating: '创建中...',
    createTitle: '创建新的消息发送活动',
    createDesc: '为您的分店添加新的群发消息活动。',
    close: '关闭',
    duplicate: '复制',
    editCampaign: '编辑活动',
    viewDetails: '查看详情',
    pause: '暂停活动',
    resume: '继续发送',
    delete: '删除活动',
    noBranch: '请选择分店以查看群发消息',
    empty: '未找到活动',
    emptyDesc: '该分店暂无群发消息活动。',
    channels: {
      whatsappWithImage: 'WHATSAPP 图片消息',
    },
    columns: {
      name: '名称',
      type: '类型',
      state: '状态',
      progress: '进度',
      selectedCustomers: '已选客户',
      status: '状态',
      channel: '渠道',
      messageType: '消息类型',
      customer: '客户',
      reference: '参考',
      attempts: '尝试次数',
      createdAt: '创建时间',
    },
    statusFilter: {
      all: '所有状态',
      pending: '待处理',
      sent: '已发送',
      delivered: '已送达',
      failed: '失败',
    },
    noDataTitle: '未找到消息',
    noDataDesc: '该分店暂无消息投递事件。',
    deleteModal: {
      title: '删除活动？',
      description: '确定要删除此活动吗？此操作无法撤销。',
    },
    details: {
      pageTitle: '活动 {{name}}',
      subtitle: '群发活动投递进度',
      empty: '未找到结果',
      emptyDesc: '此活动暂无投递事件。',
      loadError: '无法加载投递事件，请重试。',
      emailLabel: '邮箱：',
      phoneLabel: '电话：',
      chatId: '会话 {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: '重新发送',
      resubmitSuccess: '事件已重新加入队列。',
      resubmitError: '无法重新加入队列，请重试。',
      resubmitConfirmTitle: '上一次发送已成功',
      resubmitConfirmDescription: '确定要再次发送此消息吗？',
      resubmitConfirmAction: '是，再次发送',
      columns: {
        recipient: '收件人',
        message: '消息',
        channel: '渠道',
        status: '结果',
        details: '详情',
      },
    },
    validation: {
      nameMin: '活动名称至少需要 2 个字符',
      nameMax: '活动名称不能超过 120 个字符',
    },
    toast: {
      createSuccess: '活动创建成功。',
      createError: '创建活动失败，请重试。',
      updateSuccess: '活动保存成功。',
      updateError: '保存活动失败，请重试。',
      deleteSuccess: '活动删除成功。',
      deleteError: '删除活动失败，请重试。',
      duplicateSuccess: '活动复制成功。',
      duplicateError: '复制活动失败，请重试。',
      imageRemoveSuccess: '已成功移除附件图片。',
      imageRemoveError: '移除附件图片失败。',
      imageUploadSuccess: '图片上传成功。',
      imageUploadError: '图片上传失败，请重试。',
      imageTypeError: '仅允许 JPG 和 PNG 图片。',
      imageSizeError: '图片大小不得超过 5 MB。',
      startSuccess: '群发消息已开始。请在列表中查看进度。',
      startError: '启动活动失败，请重试。',
      pauseSuccess: '活动已暂停。',
      pauseError: '暂停活动失败，请几分钟后重试。',
      resumeSuccess: '活动已继续。',
      resumeError: '继续活动失败，请几分钟后重试。',
    },
    edit: {
      pageTitle: '编辑 {{name}}',
      subtitle: '配置活动详情和消息内容',
      startCampaign: '开始活动',
      start: {
        confirmTitle: '继续吗？',
        confirmDescription:
          '您即将向 {{count}} 位客户发送类型为 {{channel}} 的群发消息。',
        confirmAction: '是，继续',
        noRecipients: '没有收件人。请添加收件人以启动活动。',
        emptyMessage: '请输入消息以启动活动。',
        emptySubject: '请输入主题以启动邮件活动。',
        missingImage: '请添加图片以启动带图片的 WhatsApp 活动。',
        missingTemplate: '请选择模板以启动 WhatsApp Business 活动。',
        corruptTemplateVariables:
          '此模板的变量为空或不可用。模板似乎已损坏。请选择其他模板。',
        corruptTemplateLanguages:
          '此模板没有关联语言，似乎已损坏。请选择其他模板。',
      },
      saveConfiguration: '保存配置',
      configTitle: '群发消息配置',
      configDesc: '设置 WhatsApp 和邮件活动以发送给所选客户。',
      configuration: '配置',
      configurationDesc: '设置活动名称和群发消息渠道。',
      audience: '受众',
      selectedCustomers: '已选客户',
      customersReady: '{{count}} 位客户已准备好群发',
      selectCustomers: '选择客户',
      selectCustomersSoon: '客户选择功能即将推出。',
      channel: '渠道',
      whatsappTitle: 'WhatsApp 消息',
      whatsappDesc: '配置 WhatsApp 消息内容。',
      whatsappPlaceholder: '在此输入您的消息...',
      whatsappBusinessTitle: 'WhatsApp Business 模板',
      whatsappBusinessDesc:
        '从 Meta 批准的模板中选择，通过 WhatsApp Business API 发送。',
      whatsappBusinessEmptyDesc:
        '选择一个 Meta 批准的模板以设置通过 WhatsApp Business API 发送。',
      selectedTemplate: '已选模板',
      noTemplateSelected: '未选择模板',
      selectTemplate: '选择模板',
      selectTemplateTooltip: '从 Meta 批准的模板中选择',
      selectTemplateSoon: '模板选择功能即将推出。',
      templateModal: {
        title: '选择模板',
        description: '从 WhatsApp Business API 已审核通过的模板中选择一个。',
        messageTypeBadge: '适用于 {{messageType}}',
        countBadge: '{{count}} 个模板',
        countBadgeFiltered: '{{count}} / {{total}}',
        searchPlaceholder: '按名称搜索模板...',
        empty: '暂无可用模板',
        selectAction: '选择模板',
        recommended: '根据所选消息类型推荐的模板（{{messageType}}）',
        bodyPreview: 'Body Preview',
        noPreview: '暂无正文预览。',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          '此模板似乎已损坏且无法使用。它可能在 Facebook Business Manager 中被手动修改，不再符合 WhatsApp Business API 所需结构。',
        loadError: '无法加载模板',
        loadErrorDesc: '获取 WhatsApp Business 模板失败，请重试。',
        selectSuccess: '模板已成功选择。',
        selectError: '选择模板失败，请重试。',
      },
      whatsappWithImageTitle: '带图片的 WhatsApp',
      whatsappWithImageDesc: '配置消息、图片附件和动态占位符。',
      whatsappMessageText: 'WhatsApp 消息文本',
      emailTitle: '群发邮件',
      emailDesc: '配置邮件、附件和 HTML 内容。',
      emailSubject: '邮件主题',
      emailSubjectPlaceholder: '例如：专属优惠送给你 🎉',
      emailBody: '邮件内容',
      emailBodyHint: '如果插入链接，请留一个空格',
      applyTemplate: '应用模板',
      sendTest: '发送测试消息',
      attachedImage: '附件图片',
      uploadImage: '上传图片',
      removeImage: '移除图片',
      uploading: '上传中...',
      removing: '移除中...',
      test: {
        title: '测试发送',
        emailTitle: '测试发送',
        oopsTitle: '哎呀...',
        ok: '确定',
        send: '发送',
        testSending: '测试发送',
        backwards: '返回',
        emptyMessage: '请输入消息以尝试发送。',
        emptySubject: '请输入邮件主题以尝试发送。',
        missingImage: '此活动没有关联图片。请添加一张图片以测试带图发送。',
        mobilePlaceholder: '手机号码',
        emptyPhone: '请输入手机号码以尝试发送。',
        emptyEmail: '请输入邮箱地址以尝试发送。',
        emailLabel: '邮箱：',
        emailPlaceholder: '输入接收测试邮件的邮箱地址',
        apiSoon: '测试发送 API 即将推出。',
        sendSuccess: '测试消息已成功发送。',
        sendError: '无法发送测试消息，请重试。',
      },
    },
    selectCustomers: {
      pageTitle: '为 {{name}} 分配客户',
      subtitle: '筛选并选择此活动的客户',
      subtitleWithBranch: '筛选并选择 {{branch}} 的客户',
      searchPlaceholder: '搜索客户...',
      selected: '已选',
      clearSelection: '清除选择',
      removeAll: '全部移除',
      saveList: '保存客户列表',
      bookingPeriodTitle: '期间内的预订',
      bookingRangeLabel: '预订范围',
      bookingRangePlaceholder: '选择预订范围',
      removePeriod: '移除期间',
      marketingConsentShort: '营销同意',
      listTitle: '客户列表',
      listDesc: '管理客户选择',
      selectPage: '选择本页',
      deselectPage: '取消选择本页',
      selectAll: '全选',
      deselectAll: '取消全选',
      progressAdding: '正在添加 {{current}} / {{total}}',
      selectRow: '选择客户',
      results: '结果',
      page: '页',
      noEmail: '无邮箱',
      columns: {
        customer: '客户',
        phone: '电话',
        birth: '生日',
        bookings: '预订',
        tags: '标签',
      },
      toast: {
        saveSuccess: '客户列表保存成功。',
        saveError: '保存客户列表失败，请重试。',
        updateError: '更新客户选择失败，请重试。',
        selectAllSuccess: '已选择 {{count}} 位客户。',
        selectAllPartial:
          '已添加 {{added}} 位客户，随后请求失败。请再次尝试全选。',
        clearSuccess: '已从选择中移除所有客户。',
        clearError: '清除客户选择失败，请重试。',
      },
    },
  },
  fidelity: {
    title: '忠诚计划',
    subtitleDefault: '查看并管理您的忠诚计划',
    statsCount: '{{count}} 个计划',
    searchPlaceholder: '按名称搜索计划...',
    addProgram: '新建计划',
    creating: '创建中...',
    saving: '保存中...',
    createTitle: '创建忠诚计划',
    createDesc: '为您的分店添加新的忠诚计划。',
    createActiveConflict: '该分店已存在启用的 {{type}} 计划“{{name}}”。',
    createActiveConflictAction: '请先停用现有计划，或将此计划保持为未启用。',
    createActiveBlocked: '请关闭“启用计划”以继续，或从列表中停用“{{name}}”。',
    edit: {
      pageTitle: '修改忠诚计划',
    },
    programName: '计划名称',
    programNamePlaceholder: '输入计划名称',
    description: '描述',
    descriptionPlaceholder: '输入计划描述',
    policyMessageCard: '政策 / 消息卡',
    tagsOnDownload: '下载时应用的标签',
    tagsOnUse: '使用时应用的标签',
    tagsPlaceholder: '选择标签',
    type: '类型',
    typePoints: '积分',
    typeDiscount: '折扣',
    typeDiscountPrepaid: '折扣 / 预付',
    customerCards: '客户卡',
    noThresholds: '尚未配置积分门槛。',
    codeLinePrepaid: '带预付余额并将折扣应用到会员卡的计划。',
    color: '颜色',
    value: '数值',
    active: '启用',
    inactive: '未启用',
    activeProgram: '启用计划',
    discountApplied: '应用折扣 (%)',
    codeLineRange: '从 {{from}} 到 {{to}} 欧元',
    codeLineDiscount: '{{value}}% 折扣',
    thresholdCount: '{{count}} 个阈值',
    customersCount: '{{count}} 位客户',
    thresholdsTitle: '积分阈值',
    thresholdsHint: '后端会重新排序阈值并填补空隙。',
    addThreshold: '阈值',
    updateThreshold: '更新阈值',
    thresholdFrom: '起始欧元*',
    thresholdTo: '结束欧元*',
    thresholdPointsLabel: '积分*',
    thresholdLabel: '标签',
    thresholdColor: '阈值颜色',
    editProgram: '编辑计划',
    deleteProgram: '删除计划',
    duplicate: '复制',
    editThreshold: '编辑阈值',
    deleteThreshold: '删除阈值',
    noBranch: '请选择分店以查看忠诚计划',
    empty: '尚未配置忠诚计划',
    emptyDesc: '创建积分或折扣计划以开始生成客户卡。',
    cardsTitle: '客户卡',
    cardsSubtitle: '已发卡、二维码与余额',
    cardsEmpty: '未找到与此计划关联的忠诚卡。',
    searchCustomerPlaceholder: '关于客户、卡片...',
    createCard: '创建卡片',
    refreshCards: '刷新卡片',
    update: '更新',
    scanQrCode: '扫描二维码',
    useCard: '使用卡片',
    toggleCardStatus: '切换已用/可用状态',
    operationHistory: '操作历史',
    qrCodeTitle: '扫描二维码',
    downloadQr: '下载',
    historyTitle: '忠诚卡历史',
    historyEmpty: '此忠诚卡暂无操作记录。',
    postedBy: '发布者',
    branchCode: '分店',
    fidelityCode: '忠诚',
    customerId: '客户 ID',
    pointsSuffix: '积分',
    amountSuffix: '欧元',
    cardAvailable: '可用',
    cardUsed: '已使用',
    columns: {
      qr: '二维码',
      customer: '客户',
      contacts: '联系方式',
      card: '卡片',
      balance: '余额',
      state: '状态',
      activation: '激活',
      threshold: '阈值',
      label: '标签',
      points: '积分',
      color: '颜色',
    },
    createCardPage: {
      title: '选择客户',
      subtitle: '选择一位客户以发放新的忠诚卡。',
      subtitleWithProgram: '{{branch}} · {{program}} 的客户',
      newCard: '新卡片',
      chooseCustomer: '选择客户',
      searchPlaceholder: '按姓名、电话或邮箱搜索...',
      results: '结果',
      page: '页',
      availableCustomers: '可用客户',
      availableCustomersDesc: '选择一位客户以打开建卡确认。',
      noEmail: '无邮箱',
      columns: {
        customer: '客户',
        telephone: '电话',
        wood: '到店',
        tags: '标签',
      },
    },
    createCardConfirm: {
      title: '要为此客户创建卡片吗？',
      confirm: '是，创建卡片',
      creating: '创建中...',
    },
    statCards: {
      programs: '计划',
      active: '启用',
      types: '类型',
    },
    deleteModal: {
      title: '删除忠诚计划？',
      description: '确定要删除此忠诚计划吗？此操作无法撤销。',
    },
    deleteThresholdModal: {
      title: '删除阈值？',
      description: '确定要删除此阈值吗？此操作无法撤销。',
    },
    thresholdModal: {
      createTitle: '添加积分阈值',
      editTitle: '修改积分阈值',
      description: '配置范围、积分、标签和阈值颜色。',
      lowerLimit: '下限*',
      upperLimit: '上限*',
      pointsApplied: '应用积分*',
    },
    toast: {
      createSuccess: '忠诚计划创建成功。',
      createError: '创建忠诚计划失败，请重试。',
      updateSuccess: '忠诚计划更新成功。',
      updateError: '更新忠诚计划失败，请重试。',
      deleteSuccess: '忠诚计划删除成功。',
      deleteError: '删除忠诚计划失败，请重试。',
      duplicateSuccess: '忠诚计划复制成功。',
      duplicateError: '复制忠诚计划失败，请重试。',
      thresholdCreateSuccess: '阈值创建成功。',
      thresholdCreateError: '创建阈值失败。',
      thresholdUpdateSuccess: '阈值更新成功。',
      thresholdUpdateError: '更新阈值失败。',
      thresholdDeleteSuccess: '阈值删除成功。',
      thresholdDeleteError: '删除阈值失败。',
      cardCreateSuccess: '忠诚卡创建成功。',
      cardCreateError: '创建忠诚卡失败。',
      cardUseSuccess: '卡片已标记为已使用。',
      cardUseError: '更新卡片使用状态失败。',
      cardToggleSuccess: '卡片状态更新成功。',
      cardToggleError: '更新卡片状态失败。',
    },
    operations: {
      addPoints: '已添加积分',
      updatePoints: '已更新积分',
      removePoints: '已移除积分',
      addBillAmount: '已添加账单金额',
      addPrepaidAmount: '已添加预付金额',
      updatePrepaidAmount: '已更新预付金额',
      disableAccount: '已禁用账户',
      createAccount: '已创建账户',
      used: '已使用卡片',
    },
    registration: {
      title: '客户自助注册',
      description: '分享此链接，客户可以自行创建会员卡。',
      linkLabel: '会员注册链接',
      openForm: '打开表单',
      copy: '复制链接',
      qr: '二维码',
    },
    views: {
      label: '会员计划',
      configuration: '配置',
      configurationHint: '规则、权益和消息',
      cards: '客户卡',
      cardsHint: '已发卡、二维码和记录',
      allPrograms: '全部会员计划',
    },
    wizard: {
      program: '计划与权益',
      messages: '消息',
      review: '摘要',
      back: '返回',
      continue: '继续',
      save: '保存配置',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: '忠诚计划设置步骤',
      pointsIntro: '设置计划、积分规则和欢迎消息。',
      discountIntro: '设置折扣比例、卡片外观和欢迎消息。',
      discountNote:
        '此计划使用折扣比例，不累计积分。预付余额在客户资料中管理。',
      benefitPoints: '积分累计',
      benefitDiscount: '{{value}}% 折扣',
      messageLabel: '欢迎消息',
      messagePlaceholder: '选择会员消息',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · 邮件 {{email}}',
      messageActive: '启用',
      messageInactive: '停用',
      channelOn: '开',
      channelOff: '关',
      messageWarning: '保存前请为此消息启用 WhatsApp 或邮件。',
      messageLoadError: '无法加载消息。',
      configureMessages: '配置消息',
      rulesLabel: '计划规则',
      reviewTitle: '计划摘要',
      name: '名称',
      status: '状态',
      benefit: '权益',
      message: '消息',
      autoUpdates: '自动更新',
      autoUpdatesValue: '每次操作都有专用消息，可在消息中配置。',
      createProgram: '计划',
      createBenefits: '权益',
      createPoints: '消费积分区间',
      createRewards: '可兑换奖励',
      createHistorical: '累计等级',
      createProgramDesc: '选择计划名称和类型。',
      createPointsDesc:
        '设置唯一的积分累计规则，同一积分同时计入可用余额和历史余额。',
      createRewardsDesc:
        '设置可兑换奖励及其门槛。兑换时会从可用余额中扣除所需积分。',
      createHistoricalDesc:
        '设置每个等级的积分、名称、颜色和折扣，也可从0积分开始。Fidelity二维码识别折扣，历史积分不会扣减。',
      createBenefitsDesc: '设置积分消费区间，或折扣百分比。',
      createMessagesDesc: '设置带会员卡二维码的 WhatsApp 和邮件欢迎消息。',
      createReviewDesc: '创建计划前请核对权益、状态和消息。',
      createAction: '创建计划',
      afterSaveNote: '保存计划后即可创建客户卡。',
      thresholdStepHint: '为每个消费区间设置客户获得的积分。',
      thresholdReviewLine: '{{from}} 至 {{to}} 欧元：{{points}} 积分',
    },
    validation: {
      nameMin: '计划名称至少需要 2 个字符',
      nameMax: '计划名称不能超过 100 个字符',
      colorInvalid: '请输入有效的十六进制颜色（例如 #FFFFFF）',
      thresholdRequired: '请至少添加一个阈值。',
      thresholdRange: '上限必须大于或等于下限。',
      thresholdLower: '请输入有效的下限。',
      thresholdUpper: '请输入有效的上限。',
      thresholdPoints: '请输入有效积分。',
      thresholdLabelRequired: '阈值标签为必填项。',
      thresholdLabelMax: '阈值标签不能超过 100 个字符。',
    },
  },
  marketingGuide: {
    infoButton: '营销指南',
    title: '选择合适的营销工具',
    intro: '促销和 Fidelity 都可以提供优惠，但它们服务于不同的目标。',
    promotionTitle: '促销 · 有期限的营销活动',
    promotionBody:
      '使用促销吸引新客户、唤回偶尔到店的客户，或带动淡季。客户通过公开链接或二维码参加并获得优惠券，无需积累积分。\n示例：“周日前享九折，消费满 30 欧元，每位客户限用一次。”',
    fidelityTitle: 'Fidelity · 鼓励客户再次光临',
    fidelityBody:
      '使用 Fidelity 奖励回头客。消费按已配置的规则累计积分；达到门槛后，客户可以兑换奖励。余额和操作记录都保存在会员卡中。\n示例：“每张小票都可累计积分，达到目标后选择一项奖励。”',
    rulesTitle: '实用营销规则',
    rulesBody:
      '1. 明确一个目标：获客、唤回客户、增加回访或提高客单价。\n2. 保护利润：设置可持续的折扣和最低消费，并限制数量或预算。\n3. 清楚说明条件：日期、适用商品或渠道、最低消费及是否可叠加。\n4. 让优惠容易理解和使用；条件过多会降低参与度。\n5. 关注兑换和后续消费，而不只是发出的优惠券或累计的积分。',
    togetherTitle: '两者可以配合使用',
    togetherBody:
      '先用限时促销吸引客户到店，再邀请客户加入 Fidelity，建立长期回访的理由。清楚区分促销优惠券和 Fidelity 奖励。',
  },
  promotions: {
    title: '促销活动',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: '{{branch}} 的促销活动列表',
    subtitleDefault: '查看和管理促销活动列表',
    pageDescription: '管理场所的促销、优惠券和促销配置',
    statsCount: '{{count}} 个促销活动',
    searchPlaceholder: '按名称搜索促销活动...',
    promoName: '促销名称',
    promoNamePlaceholder: '输入促销名称',
    description: '描述',
    descriptionPlaceholder: '输入促销描述',
    used: '已使用',
    usedCouponCount: '{{count}} 张优惠券',
    downloaded: '已下载',
    messages: '消息',
    messagesTooltip: '通过此促销发送的消息',
    messageHistory: {
      title: '消息历史',
      reference: '参考 {{code}}',
      empty: '暂无消息。',
      refresh: '刷新',
      loadError: '加载消息失败。',
      attempts: '{{count}} 次尝试',
    },
    customerCoupons: {
      title: '客户优惠券',
      subtitle: '促销：{{name}} · 已发放 {{count}} 张',
      discount: '{{percent}}% 折扣',
      redeemed: '已兑换',
      notRedeemed: '未兑换',
      generatedOn: '生成于 {{date}}',
      redeemedOn: '兑换于 {{date}}',
      operator: '操作员：{{name}}',
      empty: '尚未发放优惠券。',
      loadError: '加载客户优惠券失败。',
      loading: '正在加载优惠券...',
    },
    status: '状态',
    active: '活跃',
    expired: '已过期',
    url: 'URL',
    qrCode: '二维码',
    preview: '预览',
    copyUrl: '复制 URL',
    duplicate: '复制',
    stop: '停止',
    restart: '重启',
    editPromotion: '编辑促销',
    delete: '删除促销',
    addPromotion: '添加促销',
    creating: '创建中...',
    createTitle: '创建促销',
    createDesc: '为您的分店添加新的消息活动。',
    createWizard: {
      detailsDesc: '设置名称、可用优惠券和折扣。创建后可以自定义客户表单。',
      messageDesc: '从设置 → 消息中已配置的三个消息里选择一个。',
      reviewHint: '创建促销前请核对设置。',
      stepCount: '{{current}} / {{total}}',
      back: '返回',
      continue: '继续',
      createAndCustomize: '创建并自定义',
      couponHint: '如果促销没有百分比折扣，请使用 0。',
      automaticUpdatesLabel: '自动更新',
      automaticUpdates: '每个操作都有专用消息，可在消息中配置。',
    },
    close: '关闭',
    save: '保存',
    saveChanges: '保存更改',
    buttonLabel: '按钮文字',
    buttonLabelPlaceholder: '输入按钮文字',
    redirectPage: '重定向页面',
    redirectPagePlaceholder: '自定义感谢页 URL',
    percentageDiscount: '折扣 (%)',
    maxCouponCount: '最大优惠券数',
    maxUsageCount: '最大使用次数',
    itemsPerPage: '每页项目数',
    range: '{{start}} - {{end}} / {{total}}',
    noBranch: '请选择分店以查看促销活动',
    empty: '暂无促销活动',
    emptyDesc: '您尚未为此场所创建促销活动。请先创建第一个促销。',
    deleteModal: {
      title: '删除促销？',
      description: '确定要删除此促销吗？此操作无法撤销。',
    },
    validation: {
      promotionNameMin: '名称至少需要 2 个字符',
      promotionNameMax: '名称不能超过 120 个字符',
      couponMin: '请输入大于零的整数。',
      discountRange: '请输入 0 到 100 的整数。',
      redirectPageInvalid: '请输入有效的网址（例如 https://example.com）。',
    },
    toast: {
      createSuccess: '促销创建成功。',
      createError: '无法创建促销，请重试。',
      updateSuccess: '促销更新成功。',
      updateError: '无法更新促销，请重试。',
      deleteSuccess: '促销删除成功。',
      deleteError: '无法删除促销，请重试。',
      duplicateSuccess: '促销复制成功。',
      duplicateError: '无法复制促销，请重试。',
      stopSuccess: '促销已停止。',
      stopError: '无法停止促销，请重试。',
      restartSuccess: '促销已重启。',
      restartError: '无法重启促销，请重试。',
      logoUploadSuccess: 'Logo 上传成功。',
      logoUploadError: 'Logo 上传失败。',
      logoRemoveSuccess: 'Logo 已移除。',
      logoRemoveError: 'Logo 移除失败。',
      posterUploadSuccess: '海报上传成功。',
      posterUploadError: '海报上传失败。',
      posterRemoveSuccess: '海报已移除。',
      posterRemoveError: '海报移除失败。',
    },
    edit: {
      pageTitle: '编辑促销',
      subtitle: '配置表单和促销设置。',
      tabListLabel: '促销编辑部分',
      wizard: {
        stepListLabel: '促销设置步骤',
        form: '客户表单',
        formDesc: '自定义客户领取优惠券时填写的表单。',
        rules: '规则',
        rulesDesc: '设置期限、可用数量、折扣以及向客户收集的信息。',
        message: '消息',
        messageDesc: '从设置 → 消息中已配置的三个消息里选择一个。',
        review: '核对',
        reviewDesc: '核对设置并保存。未保存的更改只保留在此页面。',
        editStep: '编辑',
        period: '期限',
        requestedFields: '收集的信息',
        noDate: '无日期',
        none: '无',
      },
      uploadImage: '上传图片',
      removeImage: '移除图片',
      uploading: '上传中...',
      removing: '移除中...',
      tabs: {
        formConfig: '表单配置',
        promoConfig: '促销配置',
      },
      formConfig: {
        media: '媒体',
        logo: '促销 Logo',
        details: '表单详情',
        promoText: '促销文字',
        promoLink: '促销链接',
      },
      promoConfig: {
        poster: '海报',
        posterLabel: '促销海报',
        options: '选项',
        useOnlyOnce: '优惠券仅可使用一次',
        useOnlyOnceDesc: '将每位客户的优惠券下载限制为一次',
        showEmail: '请求邮箱',
        showEmailDesc: '在促销表单中显示邮箱字段',
        showAddress: '请求地址',
        showAddressDesc: '在促销表单中显示地址字段',
        showCap: '请求邮编',
        showCapDesc: '在促销表单中显示邮编字段',
        showDob: '请求出生日期',
        showDobDesc: '在促销表单中显示出生日期字段',
        showCountry: '请求国家',
        limits: '日期和限制',
        startDate: '开始日期',
        endDate: '结束日期',
        tags: '标签',
        tagsOnDownload: '下载时应用的标签',
        tagsOnUse: '使用时应用的标签',
        tagsPlaceholder: '选择标签',
        messaging: '消息',
        messageLabel: '发给客户的消息',
        messagePlaceholder: '请选择三个共享配置之一。',
        messageStatus: '{{status}} · WhatsApp：{{whatsapp}} · 邮件：{{email}}',
        messageActive: '配置已启用',
        messageInactive: '配置已停用',
        channelOn: '开启',
        channelOff: '关闭',
        messageWarning: '请在消息中至少启用一个渠道，才能发送此通知。',
        configureMessages: '在消息中配置文案、渠道和模板',
        messageLoadError: '无法加载配置。请从消息中重试。',
        sharedMessageNote:
          '每个操作使用在消息中配置的专用消息。PROMO_INFO 仅用于状态更新。',
        whatsappMessage: 'WhatsApp 优惠券消息',
        whatsappSubtitle: '随促销二维码发送的消息',
        whatsappPlaceholder: '在此输入消息...',
        applyTemplate: '应用默认模板',
        sendMessage: '发送消息',
      },
      detailsTitle: '促销详情',
      detailsDesc: '更新活动信息和限制。',
      branchHint: '分店：{{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: '已收货（有变更）',
    processedModified: '已处理但有修改',
    processed: '已处理',
    title: '供应商订单',
    subtitle: '采购、配送和产品核对',
    history: '订单历史',
    create: '创建订单',
    search: '搜索订单或产品…',
    supplier: '供应商',
    allSuppliers: '所有供应商',
    invalidDates: '请选择有效的日期范围。',
    onlyModified: '仅显示已修改的订单',
    loadError: '无法加载订单。',
    ordersOn: '{{date}} 的订单 · {{count}}',
    empty: '未找到订单',
    order: '订单',
    delivery: '配送',
    status: '状态',
    totalVat: '含税总额',
    perPage: '每页',
    orderCount: '{{count}} 个订单',
    modified: '已修改',
    recipient: '收件方',
    chooseRecipient: '选择供应商或门店',
    branchSuffix: '门店',
    deliveryDate: '配送日期',
    futureDate: '请选择今天或以后的日期。',
    pastDate: '配送日期必须是今天或以后的日期。',
    receiveFrom: '接收开始',
    receiveTo: '至',
    catalogSearch: '在目录中搜索…',
    catalogError: '无法加载目录。',
    noProducts: '没有可用产品',
    product: '产品',
    vatLine: '{{unit}} · 增值税 {{vat}}%',
    net: '净价',
    quantity: '数量 {{name}}',
    removeNamed: '移除{{name}}',
    remove: '移除',
    add: '添加',
    productCount: '{{count}} 个产品',
    totalLine: '含税总额：{{amount}}',
    createHint:
      '请选择收件方、日期，以及至少一种数量大于零的产品。结束时间必须晚于开始时间。',
    createError: '无法创建订单。',
    calendarTitle: '订单日历',
    otherDate: '其他日期',
    loadPeriod: '加载其他时段',
    today: '今天',
    previousDays: '滚动到更早的日期',
    nextDays: '滚动到更晚的日期',
    window: '（±20 天）',
    incoming: '在途',
    toReview: '待核对',
    calendarTotals: '合计 · 所有供应商',
    calendarError: '日历统计暂不可用。',
    calendarDays: '订单日历日期',
    incomingSummary: '{{count}} 在途',
    reviewSummary: '{{count}} 待核对',
    processedSummary: '{{count}} 个已处理',
    ordersSummary: '{{count}} 个订单，状态明细不可用',
    countsUnavailable: '统计不可用',
    calendarHint:
      '每日数量包含已计划、待收货及已处理订单。搜索和供应商筛选仅用于下方列表。',
    exportJson: '导出 JSON',
    importJson: '导入 JSON',
    exported: '文件已导出。',
    importedCount: '已导入 {{count}} 项。',
    exportError: '导出失败。请重试。',
    invalidJson: 'JSON 无效。',
    importProducts: '请输入包含有效名称、净价、增值税和单位的产品数组。',
    importSuppliers: '请输入每一行都有名称的供应商数组。',
    importError: '导入失败。请检查数据后重试。',
    importTitle: '导入{{label}}',
    importAction: '导入',
    jsonFile: 'JSON 文件',
    fileTooLarge: '文件超过 5 MB。',
    fileReadError: '无法读取文件。',
    importOrdersHint: '导入会为所选门店创建新订单。再次导入会创建更多订单。',
    jsonArray: 'JSON 数组',
    ordersLabel: '订单',
    productsLabel: '产品',
    suppliersLabel: '供应商',
    statuses: {
      BOZZA: '草稿',
      PROGRAMMATO: '已排期',
      IN_PREPARAZIONE: '准备中',
      IN_CONSEGNA: '配送中',
      DA_VERIFICARE: '待核对',
      RICEVUTO: '已收货',
      NON_CONSEGNATO: '未送达',
      ANNULLATO: '已取消',
      INVIATO: '已发送',
      DA_CONFERMARE: '待确认',
      CONSEGNATO: '已送达',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Match to product',
    skipLine: 'Skip this line',
    duplicateMatch: 'Match each line to a different product.',
    continue: 'Continue',
    category: 'Category',
    close: 'Close',
    allCategories: 'All categories',
    personalWhatsApp: 'Personal WhatsApp',
    localWhatsApp: 'Restaurant WhatsApp',
    confirmSend: 'Send the order to the supplier using this channel?',
    confirmEdit: 'Save the updated order quantities?',
    checkQuantities: 'Check quantities',
    alreadyDelivered: 'Goods have arrived: proceed to verification',
    createReview: 'Order review',
    discardDraft: 'Discard the selected products?',
    savedShare:
      'Order saved. Would you like to choose how to send it to the supplier?',
    title: '供应商订单',
    orderTitle: '订单 #{{id}}',
    back: '订单',
    unavailable: '此订单不可用。',
    requestedDelivery: '要求配送',
    changeDate: '更改日期',
    orderTotal: '订单含税总额',
    netLine: '净额 {{net}} · 增值税 {{vat}}',
    products: '已订产品',
    addReceived: '添加已收产品',
    verifyAll: '全部核对',
    ordered: '订购数量',
    sent: '已发数量',
    unitNet: '单位净价',
    received: '已收数量',
    receivedPrice: '已收净价',
    verified: '已核对',
    verifyNamed: '核对{{name}}',
    receivedNamed: '已收数量 {{name}}',
    receivedPriceNamed: '已收价格 {{name}}',
    editOrder: '更新订单',
    prepare: '准备订单',
    dispatch: '确认发货',
    arrived: '货物已到达',
    cancelOrder: '取消订单',
    notDelivered: '未送达',
    receiptTitle: '核对收货',
    invoiceTotal: '发票含税总额',
    receivedProductsTotal: '商品计算总额（含税）',
    invoiceTotalHint: '确认收货时记录的金额。',
    invoiceDraftHint: '收货时输入的金额，将在确认后保存。',
    insertedAmount: '输入总额',
    expectedAmount: '订单预计总额',
    recordedAmount: '已记录总额',
    receivedProductsTitle: '已收货商品明细',
    lineTotal: '行总额（含税）',
    receivedLineCalculation: '{{quantity}} × {{price}} + {{vat}}% 增值税',
    invoiceGoodsDifference: '发票与商品总额差额：{{amount}}',
    pendingVerification: '待核验',
    verifiedProgress: '已核验 {{checked}} / {{total}} 行',
    receiptBlockedTitle: '要启用“确认收货”，请完成：',
    receiptNeedsProducts: '请至少添加一种已收货商品。',
    receiptNeedsChecks: '请勾选其余商品行的“已核验”复选框：{{count}} 行。',
    receiptInvalidLines:
      '请修正无效的实收数量或价格（必须大于或等于零）。共 {{count}} 行。',
    receiptInvalidInvoice: '请输入大于或等于零的有效发票总额。',
    receiptSaving: '请等待订单保存完成。',
    receiptUploading: '请等待发票照片上传完成。',
    receiptAnalyzing: '请等待发票分析完成。',
    goToVerification: '前往下一条待核验商品行',
    receiptReady: '所有商品行均已核验，可以确认收货。',
    receiptTotalDifference:
      '发票总额 {{invoice}} 与实收商品总额 {{products}} 不一致。请核对金额；此差异不会禁用确认按钮。',
    movedToHistory: '消息已移至订单历史记录',
    receiptHint:
      '请核对实收数量和未税单价，然后勾选每行的“已核验”。修改数量或价格后，该行的核验状态将被清除。',
    confirmReceipt: '确认收货',
    invoice: '发票',
    invoiceAlt: '发票照片',
    invoicePhoto: '发票照片',
    choosePhoto: '选择文件',
    ocrLabel: '可选备注',
    ocrPlaceholder: '可选 AI 分析备注。留空则仅根据照片分析。',
    ocrRunning: '正在用 AI 分析发票…',
    ocrFailed: '发票分析失败。请换一张照片或添加备注。',
    clearPhotoAndText: '清除照片和文字',
    readingPhotosTitle: '正在加载照片…',
    ocrProcessingTitle: '正在用 AI 分析发票…',
    ocrProcessingHint: '正在服务器上读取发票照片，可能需要几秒钟。',
    aiAnalyzingTitle: '正在用 AI 分析发票…',
    aiAnalyzingHint: '正在服务器上读取发票照片，可能需要几秒钟。',
    photoUploadingTitle: '正在保存发票照片…',
    photoUploadingHint: '先上传图片，再进行分析。',
    analyze: '分析发票',
    reviewExtracted: '应用前请核对提取的数值。',
    applyMatches: '应用到匹配的产品',
    photoType: '请上传不超过 5 MB 的 JPG 或 PNG 图片。',
    imageInvalid: '图片无效。',
    imageReadError: '无法读取图片。',
    photoSaved: '发票照片已保存。',
    photoSaveError: '无法保存发票照片。',
    extractError: '发票分析失败。',
    communications: '发给供应商的消息',
    configureMessages: '配置消息',
    communicationsError: '无法加载消息。',
    openSupplier: '打开供应商页面',
    recipientEmail: '收件人邮箱',
    sendEmail: '发送邮件',
    updated: '订单已更新。',
    operationError: '操作失败。',
    sendFailed: '发送失败。',
    messageSent: '消息已发送。',
    requestLogged: '请求已记录。请在消息中查看结果。',
    communicationError: '消息发送失败。',
    saveDate: '保存日期',
    invalidDate: '日期无效。',
    confirmTitle: '确认操作',
    confirmAction: '确认',
    confirmBody: '更新此订单？',
    confirmReceive: '收货将按已核对的数量和价格保存。',
    unexpectedProduct: '未预计的已收产品',
    catalogUnavailable: '目录不可用。',
  },
  supplierOrderHistory: {
    title: '订单历史',
    subtitle: '按供应商、产品和配送日期搜索采购',
    back: '供应商订单',
    dateRange: '配送日期',
    load: '加载历史',
    loading: '加载中…',
    reset: '重置筛选',
    orders: '订单',
    ordered: '已订',
    received: '已收',
    vatProducts: '含税 · 已筛选产品',
    allResults: '所有筛选结果',
    search: '按产品名称搜索…',
    hint: '所有状态，包括已归档 · 金额含税',
    summaryError: '无法加载汇总。',
    dirty: '筛选已更改：请按“加载历史”以更新结果和导出。',
    loadError: '无法加载历史。',
    results: '搜索结果',
    resultsMeta: '{{count}} 个订单 · 导出包含所有筛选结果。',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: '正在导出…',
    empty: '没有符合这些筛选条件的订单',
    deliveryDate: '配送日期',
    products: '产品',
    filteredTotal: '已筛选产品 · 含税',
    exported: '已导出 {{orders}} 个订单和 {{rows}} 行产品。',
    exportError: '导出失败。',
    allSuppliers: '所有供应商',
    exportSheetProducts: '产品',
    exportSheetSearch: '搜索',
    exportTitle: '供应商订单历史',
    exportOrder: '订单',
    exportDelivery: '配送日期',
    exportSupplier: '供应商',
    exportSupplierCode: '供应商代码',
    exportStatus: '状态',
    exportProduct: '产品',
    exportUnit: '单位',
    exportOrderedQty: '订购数量',
    exportSentQty: '已发数量',
    exportReceivedQty: '已收数量',
    exportOrderedNet: '订购净价',
    exportReceivedNet: '已收净价',
    exportVat: '增值税 %',
    exportOrderedTotal: '订购含税总额',
    exportReceivedTotal: '已收含税总额',
    exportQtyChange: '数量变化',
    exportPriceChange: '价格变化',
    exportYes: '是',
    exportNo: '否',
    exportBranch: '门店',
    exportDeliveryFrom: '配送开始',
    exportDeliveryTo: '配送结束',
    exportProductName: '产品名称',
    exportAllProducts: '全部',
    exportStatuses: '状态',
    exportAllStatuses: '全部，包括已归档',
    exportOrderCount: '订单合计',
    exportOrderedAmount: '订购含税金额',
    exportReceivedAmount: '已收含税金额',
    exportProductRows: '产品行',
    exportPage:
      '第 {{page}} 页，共 {{pages}} 页 · {{orders}} 个订单 · {{rows}} 行产品',
  },
  suppliersPage: {
    title: '供应商',
    subtitle: '本门店订单的联系人和目录',
    create: '新建供应商',
    active: '使用中',
    archived: '已归档',
    search: '搜索供应商、邮箱或增值税号…',
    noBranch: '请选择门店。',
    loadError: '无法加载供应商。',
    supplier: '供应商',
    contacts: '联系方式',
    catalog: '目录',
    openCatalog: '打开目录',
    productCount: '{{count}} 个产品',
    vatLine: '增值税号 {{vat}}',
    editNamed: '编辑{{name}}',
    archiveNamed: '归档{{name}}',
    restoreNamed: '恢复{{name}}',
    empty: '未找到供应商。',
    archiveTitle: '归档供应商',
    restoreTitle: '恢复供应商',
    archiveAction: '归档',
    restoreAction: '恢复',
    archiveDesc:
      '归档{{name}}？目录和订单历史会保留。恢复之前，该供应商不能用于新订单。',
    restoreDesc: '将{{name}}恢复为使用中的供应商？',
    saveError: '无法保存供应商。请重试。',
    archiveError: '无法更新供应商归档状态。请重试。',
    editTitle: '编辑供应商',
    groupContacts: '资料和联系方式',
    groupFiscal: '税务信息',
    groupAddress: '地址',
    legalName: '公司名称',
    email: '邮箱',
    phone: '电话',
    pec: '认证邮箱',
    vat: '增值税号',
    taxCode: '税号',
    street: '街道',
    city: '城市',
    postcode: '邮编',
    country: '国家',
    fromInvoice: '从发票导入',
    invoiceTitle: '从发票创建供应商',
    createSupplier: '创建供应商',
    invoicePhoto: '发票照片',
    photosSelected: '已选择 {{count}} 张照片',
    removePhotos: '移除照片',
    readingPhotos: '正在读取照片…',
    tooManyPhotos: '最多选择 5 张 JPG 或 PNG 图片，每张不超过 5 MB。',
    invalidImage: '图片无效。',
    imageReadError: '无法读取照片。',
    photosReadError: '无法读取照片。请重试。',
    analyze: '分析',
    existing: '已有供应商',
    analyzeError: '无法分析发票。',
    createError: '无法创建供应商。',
  },
  menu: {
    ingredientsPage: {
      title: '食材',
      subtitle: '可选食材、分类和加价',
      search: '搜索食材…',
      createIngredient: '创建食材',
      createCategory: '创建分类',
      ingredients: '食材',
      categories: '分类',
      loadError: '无法加载目录。',
      operationError: '操作失败。请重试。',
      filterCategory: '筛选分类',
      allCategories: '所有分类',
      pageHint: '分类用于归组本页食材。一个分类可以延续到下一页。',
      available: '可用',
      unavailable: '已停用',
      editNamed: '编辑{{name}}',
      deleteNamed: '删除{{name}}',
      emptyCategory: '此分类中没有食材。',
      emptyPage: '本页没有食材。请更改搜索或返回上一页。',
      perPage: '每页食材数',
      createCategoryFirst: '请先创建一个分类。',
      categoryOrder: '目录顺序。每个菜品可以自定义自己的分类顺序。',
      moveUp: '上移{{name}}',
      moveDown: '下移{{name}}',
      newIngredient: '新食材',
      editIngredient: '编辑食材',
      newCategory: '新分类',
      editCategory: '编辑分类',
      ingredientDesc: '名称、分类和加价。',
      categoryDesc: '分类名称和翻译。',
      name: '名称 *',
      category: '分类 *',
      select: '选择',
      supplement: '加价 € *',
      description: '描述',
      translations: '翻译',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: '从目录中删除',
      deleteDesc: '删除“{{name}}”？已关联到菜品的食材以及非空分类受到保护。',
      delete: '删除',
    },
    recipeEditor: {
      createTitle: '创建菜品',
      tabsLabel: '菜品与翻译',
      configurationTab: '菜品',
      basePrice: '基础价格',
      dishName: '菜品名称',
      typeTitle: '菜品类型',
      simpleTitle: '普通菜品',
      simpleDesc: '名称、描述和过敏原',
      customizableTitle: '自选配料菜品',
      customizableDesc: '基础菜品 + 自选配料',
      compositeTitle: '组合套餐',
      compositeDesc: '固定价格的普通菜品组合',
      wineTitle: '葡萄酒 / 香槟',
      wineDesc: '酒瓶、杯装与酒品信息',
      includedDishes: '包含的菜品',
      searchDishes: '搜索菜品…',
      includedSections: '所含菜品的分类',
      allSections: '所有分类',
      selectionHint:
        '已选择 {{count}} 道菜品。切换页面或搜索时将保留所选内容。',
      cannotInclude: '{{reason}}：无法加入',
      weightedDish: '按重量计价',
      noDishes: '未找到菜品。',
      dishesPerPage: '每页菜品数',
      range: '第 {{from}}–{{to}} 项，共 {{total}} 项',
      previous: '上一页',
      next: '下一页',
      minTwo: '请至少选择两道普通菜品。',
      selectableIngredients: '可选配料',
      minimumTotal: '最少选择总数',
      maximumTotal: '最多选择总数',
      searchIngredients: '搜索配料…',
      ingredientLoadError: '配料加载失败。重试',
      disabled: '已禁用',
      ingredientPage: '共 {{total}} 种配料 · 第 {{page}} 页',
      categoryFilter: '分类',
      allCategories: '所有分类',
      noIngredients: '未找到配料。',
      categoryOrderHelp:
        '使用箭头设置顾客自定义菜品时看到的分类顺序。仅显示包含已选配料的分类。调整顺序不会更改配料或价格。',
      categoryOrder: '菜品中的配料分类顺序',
      moveEarlier: '将 {{name}} 前移',
      moveLater: '将 {{name}} 后移',
      availability: '供应状态与价格',
      restaurantMenu: '堂食菜单',
      takeaway: '自取',
      delivery: '外送',
      enabled: '已启用',
      channelPrice: '{{channel}}价格（€）',
      invalidChoices: '请选择配料并检查最少和最多选择数量。',
      invalidPrices: '价格无效：最多保留两位小数。',
      invalidComposition:
        '自选配料菜品和组合套餐必须使用固定价格，且不能设为酒品。',
      invalidForm: '请检查名称、价格和必填项。',
      numericError: '请输入有效数字。',
      nonNegativeError: '数值必须大于或等于零。',
      wineRed: '红葡萄酒',
      wineWhite: '白葡萄酒',
      wineRose: '桃红葡萄酒',
      wineChampagne: '香槟',
      wineSparkling: '起泡酒',
      wineOther: '其他',
    },
    title: '菜单',
    disabled: {
      badge: '功能未启用',
      title: '此部分尚未启用',
      description: '此功能当前已针对您的账户禁用。请联系支持以了解更多信息。',
      infoTitle: '需要访问此部分？',
      infoDescription: '请联系我们的支持团队，我们将协助您开始使用。',
      footerHelp: '仍需要帮助？',
      footerHelpDesc: '我们的支持团队全天候 24/7 为您提供协助。',
    },
    subtitleDefault: '查看和管理您的数字菜单',
    statsCount: '{{count}} 个菜单',
    searchPlaceholder: '按名称搜索菜单...',
    menuName: '菜单名称',
    menuNamePlaceholder: '输入菜单名称',
    description: '描述',
    descriptionPlaceholder: '输入菜单描述',
    newMenu: '新建菜单',
    createTitle: '创建新菜单',
    createAction: '创建',
    url: 'URL',
    qrCode: '二维码',
    preview: '预览',
    copyUrl: '复制 URL',
    editMenu: '编辑菜单',
    deleteMenu: '删除菜单',
    duplicate: '复制',
    close: '关闭',
    saveChanges: '保存',
    noBranch: '请选择分店以查看菜单',
    empty: '未找到菜单',
    emptyDesc: '该分店暂无菜单。',
    validation: {
      nameRequired: '菜单名称为必填项',
      nameMax: '菜单名称不能超过 100 个字符',
      descriptionMax: '描述不能超过 500 个字符',
    },
    deleteModal: {
      title: '删除菜单？',
      description: '确定要删除此菜单吗？此操作无法撤销。',
    },
    toast: {
      createSuccess: '菜单创建成功。',
      createError: '菜单创建失败，请重试。',
      updateSuccess: '菜单更新成功。',
      updateError: '菜单更新失败，请重试。',
      deleteSuccess: '菜单删除成功。',
      deleteError: '菜单删除失败，请重试。',
      duplicateSuccess: '菜单复制成功。',
      duplicateError: '菜单复制失败，请重试。',
      logoUploadSuccess: '标志上传成功。',
      logoUploadError: '标志上传失败。',
      logoRemoveSuccess: '标志移除成功。',
      logoRemoveError: '标志移除失败。',
      backgroundUploadSuccess: '背景上传成功。',
      backgroundUploadError: '背景上传失败。',
      backgroundRemoveSuccess: '背景移除成功。',
      backgroundRemoveError: '背景移除失败。',
      templateUpdateSuccess: '模板更新成功。',
      templateUpdateError: '模板更新失败。',
    },
    edit: {
      pageTitle: '编辑菜单详情',
      subtitle: '配置菜单内容、样式和分区。',
      tabListLabel: '菜单编辑选项卡',
      withPhoto: '显示图片',
      withoutPhoto: '不显示图片',
      uploading: '上传中...',
      removing: '移除中...',
      tabs: {
        titleDescription: '标题与描述',
        menuStyle: '菜单样式',
        sectionsRecipes: '分区与菜品',
      },
      informationTitle: '菜单信息',
      informationDesc: '配置菜单名称和翻译。',
      languages: {
        italian: '意大利语',
        italianMain: '主要语言',
        english: '英语',
        englishTranslation: '英语翻译',
        spanish: '西班牙语',
        spanishTranslation: '西班牙语翻译',
        french: '法语',
        frenchTranslation: '法语翻译',
      },
      descriptions: {
        italian: '菜单描述',
        english: '英语描述',
        spanish: '西班牙语描述',
        french: '法语描述',
      },
      style: {
        appearanceTitle: '菜单外观',
        appearanceDesc: '为您的菜单上传标志和背景图片。',
        logo: '选择菜单标志',
        logoHint: '建议使用方形标志（PNG），最大 5MB。',
        background: '选择菜单背景图片',
        backgroundHint: '建议使用横向图片，最大 5MB。',
        removeImage: '移除图片',
        templatesTitle: '选择要应用到菜单的样式',
        templatesDesc: '为您的菜单展示选择模板。',
        previewStyleMenu: '菜单样式预览',
        select: '选择',
        selected: '已选择',
        active: '启用中',
      },
    },
    sections: {
      title: '分区与菜品',
      description: '将菜单组织为分区并添加菜品。',
      addSection: '创建新分区',
      searchPlaceholder: '按名称、描述或代码搜索分区或菜品...',
      noSearchResults: '没有匹配的分区或菜品',
      name: '分区名称',
      namePlaceholder: '输入分区名称',
      descriptionPlaceholder: '输入分区描述',
      createTitle: '在菜单 {{menu}} 上创建分区',
      createAction: '创建',
      editTitle: '编辑菜单项',
      editDesc: '管理内容、翻译和配料',
      dishDrink: '菜品/饮品',
      missingTranslations: '缺少翻译',
      missingTranslationsTooltip:
        '此分区的名称或描述尚未翻译为菜单提供的所有语言；切换语言时将显示意大利语版本',
      addRecipe: '向 {{section}} 添加菜品/产品',
      sortAlphabetically: '按字母顺序排序菜品 A-Z',
      moveLeft: '向左移动分区',
      moveRight: '向右移动分区',
      settings: '分区设置',
      delete: '删除分区',
      menuPrice: '菜单价格',
      sectionVisibility: '显示分区',
      sectionVisibilityHint: '在餐厅、配送和自取菜单中显示此分区。',
      sectionHidden: '分区已隐藏',
      hideSectionPrices: '隐藏分区价格',
      showSectionPrices: '显示分区价格',
      visibleSection: '显示分区',
      visibility: '显示/隐藏分区',
      fixedMenuPrice: '固定菜单 ({{price}}€)',
      empty: '暂无分区',
      emptyDesc: '使用加号按钮创建您的第一个分区。',
      emptyRecipes: '此分区尚未添加菜品。',
      recipeCount: '此分区有 {{count}} 道菜品',
      deleteModal: {
        title: '删除分区？',
        description: '确定要删除此分区吗？此操作无法撤销。',
      },
      validation: {
        nameRequired: '分区名称为必填项',
        nameMax: '分区名称不能超过 100 个字符',
        descriptionMax: '描述不能超过 500 个字符',
      },
      toast: {
        createSuccess: '分区创建成功。',
        createError: '分区创建失败，请重试。',
        updateSuccess: '分区更新成功。',
        updateError: '分区更新失败，请重试。',
        deleteSuccess: '分区删除成功。',
        deleteError: '分区删除失败，请重试。',
        sortSuccess: '菜品已按字母顺序排序。',
        sortError: '菜品排序失败，请重试。',
      },
    },
    recipes: {
      editTitle: '编辑菜单项',
      editDesc: '管理内容、翻译和配料',
      imageTitle: '图片',
      productPhoto: '产品照片',
      winePhoto: '葡萄酒照片',
      wineChampagne: '葡萄酒/香槟',
      imageHintCreate: '选择要上传的图片。您可以在保存前添加菜品名称。',
      generalTitle: '基本信息',
      generalDesc: '名称、描述和配置',
      priceSection: '价格',
      priceHint: '输入产品的最终价格',
      price: '价格 €',
      bottlePrice: '整瓶价格',
      priceBottleLabel: '整瓶价格 (€)',
      chalice: '酒杯',
      pricePerGlass: '单杯价格',
      alcoholPercentage: '酒精度 (%) - 例如：14',
      wineType: '葡萄酒类型',
      wineTypePlaceholder:
        '葡萄酒类型（例如：红葡萄酒、白葡萄酒、桃红葡萄酒、香槟...）',
      blend: '混酿',
      blendPlaceholder:
        '混酿（例如：50% Negroamaro、20% Nero di Troia、30% Malvasia Nera）',
      yearOfProduction: '生产年份',
      cellar: '酒庄',
      countryRegion: '国家/地区',
      provenance: '产地',
      pricePerWeight: '价格是否按重量单位（例如公斤、升）而非按份计？',
      unitOfMeasurement: '计量单位',
      unitPlaceholder: '计量单位（例如：Kg、Lt、Pz）',
      assignAllergens: '指定过敏原',
      productConfiguration: '产品配置',
      allergensTitle: '过敏原',
      bestSeller: '畅销品',
      bestSellerDesc: '将此产品标记为最受欢迎的商品之一，并在菜单上突出显示。',
      available: '可供应',
      availableDesc: '在数字菜单上向顾客显示此菜品并允许点单。',
      notAvailable: '不可用',
      active: '启用',
      visible: '可见',
      delete: '删除菜品/产品',
      dragHandle: '拖动以重新排序菜品',
      deleteModal: {
        title: '删除菜品？',
        description: '确定要删除此菜品吗？此操作无法撤销。',
      },
      validation: {
        nameRequired: '菜品名称为必填项',
      },
      toast: {
        createSuccess: '菜品创建成功。',
        createError: '菜品创建失败，请重试。',
        updateSuccess: '菜品更新成功。',
        updateError: '菜品更新失败，请重试。',
        deleteSuccess: '菜品删除成功。',
        deleteError: '菜品删除失败，请重试。',
        moveSuccess: '菜品顺序已更新。',
        moveError: '菜品顺序更新失败，请重试。',
        saveBeforeImage: '请先保存菜品再上传图片。',
        imageUploadSuccess: '菜品图片上传成功。',
        imageUploadError: '菜品图片上传失败。',
        imageRemoveSuccess: '菜品图片移除成功。',
        imageRemoveError: '菜品图片移除失败。',
      },
    },
    allergens: {
      cereals: '含麸质谷物',
      gluten: '麸质',
      crustaceans: '甲壳类',
      molluscs: '软体动物',
      fish: '鱼类',
      eggs: '鸡蛋',
      peanuts: '花生',
      soybean: '大豆',
      mushrooms: '蘑菇',
      nuts: '树坚果',
      celery: '芹菜',
      mustard: '芥末',
      sesame: '芝麻',
      sulfur: '二氧化硫',
      lupins: '羽扇豆',
      lactose: '乳糖',
      corn: '玉米',
      pork: '猪肉',
      beef: '牛肉',
      poultry: '禽肉',
      nickel: '镍',
      legumes: '豆类',
      citrus: '柑橘类水果',
      cacao: '可可',
      honey: '蜂蜜',
    },
    translate: {
      action: '翻译',
      progressTitle: '正在翻译...',
      progressDesc: '请等待翻译完成。',
      translationsTitle: '翻译',
      success: '翻译应用成功。',
      error: '翻译失败，请重试。',
    },
    previewPage: {
      pageTitle: '菜单预览',
      loading: '正在加载菜单',
      menuLabel: '菜单',
      sectionLabel: '分区',
      priceLabel: '价格',
      noPhoto: '暂无图片',
      bestSeller: 'Best',
      allergensTitle: '过敏原',
      poweredBy: 'Powered by PRO20',
      scrollCue: '滚动到菜单',
      storyContinue: '菜单',
      close: '关闭',
      wine: {
        grape: '葡萄品种',
        cellar: '酒庄',
        year: '年份',
        region: '产区',
        alcohol: '酒精度',
        glass: '杯装',
        bottle: '瓶装',
      },
    },
  },
  landingPages: {
    title: '落地页',
    subtitle: '{{branch}} 的落地页列表',
    subtitleDefault: '查看和管理落地页列表',
    searchPlaceholder: '按名称搜索落地页',
    searchPlaceholder2: '输入落地页名称',
    landingPageName: '落地页名称',
    itemsPerPage: '每页项目数',
    range: '{{start}} - {{end}} / {{total}}',
    noBranch: '请选择分店以查看落地页',
    empty: '未找到落地页',
    emptyDesc: '该分店暂无落地页。',
    addLandingPage: '添加落地页',
    newLanding: '新建落地页',
    newLandingDesc: '为您的分店创建新的落地页。',
    createLanding: '创建落地页',
    statsCount: '{{count}} 个落地页',
    present: '存在',
    url: '网址',
    qrCode: '二维码',
    preview: '预览',
    showPreview: '显示预览',
    copyUrl: '复制网址',
    editLanding: '编辑落地页',
    deleteLanding: '删除落地页',
    duplicate: '复制',
    validation: {
      landingPageNameMin: '名称至少需要 2 个字符',
      landingPageNameMax: '名称不能超过 50 个字符',
    },
    deleteModal: {
      title: '删除落地页？',
      description: '您确定要删除此落地页吗？此操作无法撤销。',
    },
    toast: {
      createSuccess: '落地页创建成功。',
      createError: '创建落地页失败，请重试。',
      deleteSuccess: '落地页删除成功。',
      deleteError: '删除落地页失败，请重试。',
      duplicateSuccess: '落地页复制成功。',
      duplicateError: '复制落地页失败，请重试。',
      updateSuccess: '落地页更新成功。',
      updateError: '更新落地页失败，请重试。',
      logoUploadSuccess: '标志上传成功。',
      logoUploadError: '标志上传失败。',
      logoRemoveSuccess: '标志删除成功。',
      logoRemoveError: '标志删除失败。',
      backgroundUploadSuccess: '背景上传成功。',
      backgroundUploadError: '背景上传失败。',
      backgroundRemoveSuccess: '背景已移除。',
      backgroundRemoveError: '背景移除失败。',
      buttonSaveSuccess: '按钮保存成功。',
      buttonSaveError: '按钮保存失败。',
      buttonDeleteSuccess: '按钮删除成功。',
      buttonDeleteError: '按钮删除失败。',
    },
    edit: {
      pageTitle: '编辑落地页',
      subtitle: '配置媒体和操作按钮。',
      formConfiguration: '落地页配置',
      formConfigurationDesc: '配置媒体和操作按钮。',
      linkForm: '落地页链接',
      close: '关闭',
      mediaTitle: '媒体',
      mediaDescription: '上传徽标和背景图片以自定义您的落地页',
      detailsTitle: '详情',
      logo: '标志',
      background: '背景图片',
      imageUpload: '点击或拖动以上传图片',
      removeImage: '移除图片',
      uploading: '上传中...',
      removing: '移除中...',
      buttons: {
        title: '落地页按钮',
        description: '添加显示在落地页上的操作按钮。使用勾选图标保存每一行。',
        text: '按钮文字',
        link: '链接',
        color: '背景',
        textColor: '文字颜色',
        add: '添加按钮',
        save: '保存按钮',
        delete: '删除按钮',
        empty: '还没有按钮。点击 + 添加一个。',
        textPlaceholder: '例如：立即预订',
        linkPlaceholder: 'https://',
        validationRequired: '按钮文字和链接为必填项。',
      },
    },
    public: {
      documentTitle: '落地页',
      loading: '正在加载落地页...',
      unavailableTitle: '落地页不可用',
      unavailableDesc: '找不到此落地页，或该页面已不可用。',
      buttonFallback: '打开',
    },
  },
  tags: {
    title: '标签',
    titleWithBranch: '标签 {{branch}}',
    pageDescription: '暂无标签',
    subtitle: '{{branch}} 的标签列表',
    subtitleDefault: '查看和管理标签列表',
    searchPlaceholder: '按名称搜索标签',
    searchPlaceholder2: '输入标签名称',
    tagName: '标签名称',
    itemsPerPage: '每页项目数',
    range: '{{start}} - {{end}} / 共 {{total}} 条',
    noBranch: '请选择分支以查看标签',
    empty: '暂无标签',
    emptyDesc: '创建第一个标签，以便更好地组织客户、预订和营销分群。',
    addTag: '添加标签',
    createTitle: '添加标签',
    createDesc: '为您的分支创建新标签。',
    editTitle: '编辑标签',
    editDesc: '更新标签名称。',
    editTag: '编辑标签',
    duplicate: '复制',
    typeLabel: 'TAG',
    statsCount: '{{count}} 个标签',
    updatedAgo: '2 分钟前更新',
    creating: '创建中...',
    saving: '保存中...',
    saveChanges: '保存更改',
    toast: {
      createSuccess: '标签创建成功。',
      createError: '创建标签失败，请重试。',
      updateSuccess: '标签更新成功。',
      updateError: '更新标签失败，请重试。',
      deleteSuccess: '标签删除成功。',
      deleteError: '删除标签失败，请重试。',
      duplicateSuccess: '标签复制成功。',
      duplicateError: '复制标签失败，请重试。',
    },
    validation: {
      tagNameMin: '标签名称至少需要 2 个字符',
      tagNameMax: '标签名称不能超过 50 个字符',
    },
    deleteTag: '删除标签',
    deleteModel: {
      deleteTitle: '删除标签？',
      deleteDescription: '您确定要删除此标签吗？此操作无法撤销。',
    },
  },
  publicRelations: {
    title: '公共关系',
    titleWithBranch: '公关 {{branch}}',
    pageDescription: '暂无公关',
    subtitle: '{{branch}} 的公关列表',
    subtitleDefault: '查看和管理公关列表',
    searchPlaceholder: '按名称搜索公关',
    searchPlaceholder2: '输入公关名称',
    prName: '公关名称',
    itemsPerPage: '每页项目数',
    range: '{{start}} - {{end}} / 共 {{total}} 条',
    noBranch: '请选择分支以查看公关',
    empty: '暂无公关',
    emptyDesc: '创建第一个公关记录，以便组织推荐、合作与客户管理。',
    addPr: '添加公关',
    createTitle: '添加公关',
    createDesc: '为您的分支创建新的公共关系联系人。',
    editTitle: '编辑公关',
    editDesc: '更新公共关系联系人名称。',
    editPr: '编辑公关',
    duplicate: '复制',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} 个公关',
    updatedAgo: '2 分钟前更新',
    creating: '创建中...',
    saving: '保存中...',
    saveChanges: '保存更改',
    toast: {
      createSuccess: '公关创建成功。',
      createError: '创建公关失败，请重试。',
      updateSuccess: '公关更新成功。',
      updateError: '更新公关失败，请重试。',
      deleteSuccess: '公关删除成功。',
      deleteError: '删除公关失败，请重试。',
      duplicateSuccess: '公关复制成功。',
      duplicateError: '复制公关失败，请重试。',
    },
    validation: {
      prNameMin: '公关名称至少需要 2 个字符',
      prNameMax: '公关名称不能超过 50 个字符',
    },
    deletePR: '删除公关关系',
    deleteModel: {
      deleteTitle: '删除公关？',
      deleteDescription: '您确定要删除此公关吗？此操作无法撤销。',
    },
  },
  staff: {
    collaborators: {
      title: '协作者',
      subtitle: '{{branch}} 的协作者',
      subtitleDefault: '查看和管理协作者列表',
      searchPlaceholder: '按姓名、姓氏、电子邮件、电话号码或角色搜索协作者',
      firstName: '名字',
      lastName: '姓氏',
      firstNamePlaceholder: '输入名字',
      lastNamePlaceholder: '输入姓氏',
      email: '电子邮件',
      phone: '电话号码',
      role: '角色',
      itemsPerPage: '每页项目数',
      viewDetails: '查看详情',
      sharedCredentials: '共享凭据',
      addCollaborator: '添加协作者',
      editCollaborator: '编辑协作者',
      deleteCollaborator: '删除协作者',
      changePassword: {
        action: '更改密码',
        title: '更改密码',
        subtitle: '为 {{name}} 设置新密码',
        newPassword: '新密码',
        newPasswordPlaceholder: '输入 6 位数字密码',
        confirmPassword: '确认密码',
        confirmPasswordPlaceholder: '确认 6 位数字密码',
        submit: '保存密码',
        validation: {
          passwordDigits: '密码必须恰好为 6 位数字',
          passwordMismatch: '两次输入的密码不一致',
        },
      },
      range: '{{start}} - {{end}} / {{total}}',
      noBranch: '请选择分店以查看协作者',
      empty: '未找到协作者',
      emptyDesc: '该分店暂无协作者。',
      emailPlaceholder: '输入电子邮件',
      phonePlaceholder: '输入电话号码',
      username: '用户名',
      usernamePlaceholder: '输入用户名',
      password: '密码',
      passwordPlaceholder: '输入密码',
      gender: '性别',
      selectGender: '选择性别',
      male: '男',
      female: '女',
      selectRole: '选择角色',
      remunerationType: '薪酬类型',
      selectRemunerationType: '选择薪酬类型',
      hourly: '按小时',
      daily: '按天',
      monthly: '按月',
      sendCredentials: '发送凭据',
      roles: {
        receptionist: '接待员',
        assistantCook: '助理厨师',
        administrator: '管理员',
        bartender: '调酒师',
        chef: '厨师',
        dishwasher: '洗碗工',
        pizzaChef: '披萨师',
        director: '总监',
        waiter: '服务员',
        publicRelations: '公共关系',
        deputyDirector: '副总监',
      },
      validation: {
        firstNameMin: '名字至少需要 2 个字符',
        lastNameMin: '姓氏至少需要 2 个字符',
        firstNameMax: '名字不能超过 40 个字符',
        lastNameMax: '姓氏不能超过 40 个字符',
        emailInvalid: '请输入有效的电子邮件地址',
        phoneInvalid: '电话号码必须包含 6 到 15 位数字',
        countryCodeInvalid: '请选择有效的国家区号',
        usernameMin: '用户名至少需要 4 个字符',
        usernameMax: '用户名不能超过 30 个字符',
        passwordMin: '密码至少需要 8 个字符',
        passwordDigits: '密码必须恰好为 6 位数字',
        genderRequired: '请选择性别',
        roleRequired: '请选择角色',
        remunerationRequired: '请选择薪酬类型',
      },
      deleteModel: {
        deleteTitle: '删除协作者？',
        deleteDescription: '您确定要删除此协作者吗？此操作无法撤销。',
      },
      collaboratorDetails: {
        title: '协作者详情',
        subtitle: '查看协作者信息和工作详情。',
        personalDetails: '个人信息',
        employmentDetails: '工作信息',
        jobDescription: '职位描述',
        dateOfBirth: '出生日期',
        branchCode: '分店代码',
        contractType: '合同类型',
        startDateInduction: '入职开始日期',
        endDateInduction: '入职结束日期',
        mobileDeviceAccessDetail: '移动设备访问详情',
        uniqueId: '唯一 ID',
        platform: '平台',
        model: '型号',
        machine: '机器',
        release: '版本',
        fcmToken: 'FCM 令牌',
        registrationDate: '注册日期',
        noMobileDevices: '暂无已注册的移动设备。',
        close: '关闭',
        contractTypes: {
          determined: '固定期限',
          indeterminate: '无固定期限',
        },
        visible: '可见',
        fired: '已解雇',
        retribution: '薪酬',
        yes: '是',
        no: '否',
      },
      toast: {
        createSuccess: '协作者创建成功。',
        createError: '创建协作者失败。请重试。',
        updateSuccess: '协作者更新成功。',
        updateError: '更新协作者失败。请重试。',
        deleteSuccess: '协作者删除成功。',
        deleteError: '删除协作者失败。请重试。',
        sendCredentialsSoon: '发送凭据功能即将推出。',
        sendCredentialsSuccess: '凭据发送成功。',
        sendCredentialsError: '发送凭据失败，请重试。',
        sendCredentialsMissingPhone: '该协作人员没有有效的电话号码。',
        sendCredentialsMissingCredentials: '该协作人员缺少用户名或密码。',
        changePasswordSuccess: '密码更新成功。',
        changePasswordError: '更新密码失败。请重试。',
      },
      credentialsMessage:
        '您的 Proventi 凭据：\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: '协作者报告',
      titleWithMonth: '{{branch}} · {{monthYear}} 报告',
      subtitle: '{{branch}} 的协作者报告',
      subtitleDefault: '查看和管理协作者报告列表',
      noReportsPresent: '暂无报告',
      searchPlaceholder: '按姓名搜索',
      name: '名字',
      lunch: '午餐',
      cena: '晚餐',
      illness: '病假',
      holiday: '假期',
      free: '休息',
      hoursWorked: '工作时长',
      itemsPerPage: '每页项目数',
      range: '{{start}} - {{end}} / {{total}}',
      noBranch: '请选择分店以查看协作者报告',
      empty: '未找到协作者报告',
      emptyDesc: '该分店暂无协作者报告。',
      previousMonth: '上个月',
      nextMonth: '下个月',
      downloadPdf: '下载 {{monthYear}} 报告',
      downloadPdfSuccess: '协作者报告 PDF 已下载。',
      downloadPdfError: '下载协作者报告 PDF 失败。',
      pdfTitle: '协作者报告',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} 名协作者 · 生成于 {{date}}',
    },
  },
  extraServices: {
    title: '附加服务和产品',
    subtitle: '管理 {{branchName}} 可用的服务、产品和设施',
    subtitleDefault: '查看和管理附加服务列表',
    statsCount: '{{count}} 个附加服务',
    searchPlaceholder: '搜索服务或产品...',
    noBranch: '请选择分店以查看附加服务',
    empty: '暂无附加服务',
    emptyDesc: '您尚未配置附加服务或产品。添加设施以提升客户体验。',
    itemsPerPage: '每页项目数',
    range: '{{start}} - {{end}} / {{total}}',
    columns: {
      service: '服务',
      price: '价格',
      guests: '客人',
      state: '状态',
    },
    active: '活跃',
    inactive: '非活跃',
    createTitle: '创建新服务',
    createDesc: '为您的分店添加新服务或产品。',
    editTitle: '编辑服务',
    editDesc: '更新服务信息',
    createService: '创建服务',
    addService: '添加服务',
    creating: '创建中...',
    saveChanges: '保存更改',
    close: '关闭',
    name: '名称',
    namePlaceholder: '输入服务名称',
    description: '描述',
    descriptionPlaceholder: '输入描述',
    price: '价格 (€)',
    pricePlaceholder: '0',
    guests: '客人数量',
    guestsPlaceholder: '1',
    activeService: '活跃服务',
    serviceImage: '服务图片',
    uploadImage: '上传图片',
    removeImage: '移除图片',
    uploading: '上传中...',
    removing: '移除中...',
    editService: '编辑服务',
    duplicate: '复制',
    deleteService: '删除服务',
    deleteModal: {
      title: '删除服务？',
      description: '确定要删除此服务吗？此操作无法撤销。',
    },
    validation: {
      nameRequired: '名称为必填项',
      nameMax: '名称不能超过120个字符',
      descriptionMax: '描述不能超过300个字符',
      numberInvalid: '请输入有效数字',
      priceMin: '价格不能为负数',
      guestsMin: '客人数量不能为负数',
      guestsInteger: '客人数量必须为整数',
    },
    toast: {
      createSuccess: '服务创建成功。',
      createError: '创建服务失败，请重试。',
      updateSuccess: '服务更新成功。',
      updateError: '更新服务失败，请重试。',
      deleteSuccess: '服务删除成功。',
      deleteError: '删除服务失败，请重试。',
      duplicateSuccess: '服务复制成功。',
      duplicateError: '复制服务失败，请重试。',
      uploadSuccess: '图片上传成功。',
      uploadError: '图片上传失败。',
      removeImageSuccess: '图片移除成功。',
      removeImageError: '图片移除失败。',
    },
  },
  formMenu: {
    title: '表单菜单',
    subtitleDefault: '查看和管理分店的表单菜单',
    statsCount: '{{count}} 个表单菜单',
    searchPlaceholder: '搜索表单菜单...',
    noBranch: '请选择分店以查看表单菜单',
    empty: '暂无表单菜单',
    emptyDesc: '您尚未配置任何表单菜单。添加一个即可开始。',
    columns: {
      item: '菜单名称',
      status: '状态',
      price: '价格',
      totalBooking: '总预订',
      forms: '表单',
      state: '状态',
    },
    formsCount: '{{count}} 个表单',
    active: '启用',
    inactive: '停用',
    createTitle: '创建表单菜单',
    createDesc: '为您的分店添加新的表单菜单。',
    editTitle: '编辑表单菜单',
    editDesc: '更新表单菜单信息',
    createItem: '创建',
    addItem: '添加表单菜单',
    saveChanges: '保存更改',
    close: '关闭',
    name: '菜单名称',
    namePlaceholder: '输入表单菜单名称',
    description: '描述',
    descriptionPlaceholder: '输入描述',
    price: '价格（€）',
    pricePlaceholder: '0',
    forms: '表单',
    formsPlaceholder: '选择表单',
    activeItem: '启用表单菜单',
    itemImage: '菜单图片',
    uploadImage: '上传图片',
    removeImage: '移除图片',
    uploading: '上传中...',
    weekdaysLabel: '星期',
    selectAllWeekdays: '全选',
    clearWeekdays: '清除',
    availabilityDates: {
      title: '菜单可用日期',
      dateRange: '日期范围',
      placeholder: '选择日期范围',
      empty: '暂无可用日期。添加日期范围以开始。',
      addRange: '添加日期范围',
      createTitle: '添加日期范围',
      editTitle: '编辑日期范围',
      create: '添加',
      update: '更新',
      editRange: '编辑日期范围',
      deleteRange: '删除日期范围',
      deleteTitle: '删除日期范围？',
      deleteDescription: '确定要删除此可用日期范围吗？',
      dateRequired: '请选择日期范围。',
      dateOrder: '结束日期必须等于或晚于开始日期。',
      dateOverlap: '该范围与现有可用日期重叠。请选择空闲日期。',
    },
    weekdays: {
      monday: '星期一',
      tuesday: '星期二',
      wednesday: '星期三',
      thursday: '星期四',
      friday: '星期五',
      saturday: '星期六',
      sunday: '星期日',
    },
    editItem: '编辑表单菜单',
    duplicate: '复制',
    editNotFound: '未找到此表单菜单，或它已不可用。',
    deleteItem: '删除表单菜单',
    deleteModal: {
      title: '删除表单菜单？',
      description: '确定要删除此表单菜单吗？此操作无法撤销。',
    },
    validation: {
      nameRequired: '名称为必填项',
      nameMax: '名称不能超过 120 个字符',
      descriptionMax: '描述不能超过 300 个字符',
      numberInvalid: '请仅输入有效数字',
      priceMin: '价格不能为负数',
    },
    toast: {
      createSuccess: '表单菜单创建成功。',
      createError: '创建表单菜单失败，请重试。',
      updateSuccess: '表单菜单更新成功。',
      updateError: '更新表单菜单失败，请重试。',
      deleteSuccess: '表单菜单删除成功。',
      deleteError: '删除表单菜单失败，请重试。',
      duplicateSuccess: '表单菜单复制成功。',
      duplicateError: '复制表单菜单失败，请重试。',
      showEnabled: '表单菜单现已显示。',
      showDisabled: '表单菜单现已隐藏。',
      statusError: '更新表单菜单状态失败，请重试。',
      uploadSuccess: '图片上传成功。',
      uploadError: '图片上传失败。',
      removeImageSuccess: '图片移除成功。',
      removeImageError: '图片移除失败。',
      dateRangeCreateSuccess: '可用日期范围已添加。',
      dateRangeCreateError: '添加可用日期范围失败。',
      dateRangeUpdateSuccess: '可用日期范围已更新。',
      dateRangeUpdateError: '更新可用日期范围失败。',
      dateRangeDeleteSuccess: '可用日期范围已删除。',
      dateRangeDeleteError: '删除可用日期范围失败。',
      dateRangeLoadError: '加载可用日期范围失败。',
    },
  },
  events: {
    title: '活动',
    subtitleDefault: '查看和管理分店活动',
    statsCount: '{{count}} 个活动',
    searchPlaceholder: '搜索活动...',
    filterDatePlaceholder: '按日期筛选',
    reset: '重置',
    noBranch: '请选择分店以查看活动',
    empty: '暂无活动',
    emptyDesc: '您尚未创建任何活动。添加一个活动以开始。',
    columns: {
      event: '活动',
      start: '开始',
      end: '结束',
      capacity: '人数上限',
      totalBooking: 'Total Booking',
      forms: '表单',
      status: '状态',
    },
    formsCount: '{{count}} 个表单',
    createTitle: '创建活动',
    createDesc: '为您的分店添加新活动。',
    editTitle: '编辑活动',
    editDesc: '更新活动信息',
    createEvent: '创建活动',
    createItem: '创建',
    addEvent: '添加活动',
    saveChanges: '保存更改',
    close: '关闭',
    name: '名称',
    namePlaceholder: '输入活动名称',
    description: '描述',
    descriptionPlaceholder: '输入描述',
    startDate: '开始日期',
    startTime: '开始时间',
    endDate: '结束日期',
    endTime: '结束时间',
    capacity: '最大人数',
    capacityPlaceholder: '100',
    visible: '显示最大人数',
    forms: '表单',
    formsPlaceholder: '选择表单',
    mandatory: '活动为必填',
    weekdaysLabel: '星期',
    selectAllWeekdays: '全选',
    clearWeekdays: '清除',
    availabilityDates: {
      title: '活动可用日期',
      dateRange: '日期范围',
      placeholder: '选择日期范围',
      empty: '尚无可用日期。添加一个日期范围以开始。',
      addRange: '添加日期范围',
      createTitle: '添加日期范围',
      editTitle: '编辑日期范围',
      create: '添加',
      update: '更新',
      editRange: '编辑日期范围',
      deleteRange: '删除日期范围',
      deleteTitle: '删除日期范围？',
      deleteDescription: '确定要移除此可用日期范围吗？',
      dateRequired: '请选择日期范围。',
      dateOrder: '结束日期必须等于或晚于开始日期。',
      dateOverlap: '此范围与现有可用日期重叠。请选择空闲日期。',
    },
    weekdays: {
      monday: '周一',
      tuesday: '周二',
      wednesday: '周三',
      thursday: '周四',
      friday: '周五',
      saturday: '周六',
      sunday: '周日',
    },
    eventImage: '活动图片',
    uploadImage: '上传图片',
    removeImage: '移除图片',
    uploading: '上传中...',
    removing: '移除中...',
    editEvent: '编辑活动',
    duplicate: '复制',
    deleteEvent: '删除活动',
    deleteModal: {
      title: '删除活动？',
      description: '确定要删除此活动吗？此操作无法撤销。',
    },
    validation: {
      nameRequired: '名称为必填项',
      nameMax: '名称不能超过 120 个字符',
      descriptionMax: '描述不能超过 500 个字符',
      capacityInvalid: '请输入有效的人数上限',
      capacityInteger: '人数上限必须为整数',
      capacityMin: '人数上限至少为 1',
      capacityMax: '人数上限不能超过 999',
      startDateRequired: '开始日期为必填项',
      startTimeRequired: '开始时间为必填项',
      endDateRequired: '结束日期为必填项',
      endTimeRequired: '结束时间为必填项',
      endBeforeStart: '结束时间必须晚于开始时间',
    },
    toast: {
      createSuccess: '活动创建成功。',
      createError: '创建活动失败，请重试。',
      updateSuccess: '活动更新成功。',
      updateError: '更新活动失败，请重试。',
      deleteSuccess: '活动删除成功。',
      deleteError: '删除活动失败，请重试。',
      duplicateSuccess: '活动复制成功。',
      duplicateError: '复制活动失败，请重试。',
      showEnabled: '活动现已可见。',
      showDisabled: '活动现已隐藏。',
      statusError: '更新活动状态失败。请重试。',
      uploadSuccess: '图片上传成功。',
      uploadError: '图片上传失败。',
      saveBeforeImage: '请先填写活动必填信息，然后再上传图片。',
      removeImageSuccess: '图片移除成功。',
      removeImageError: '图片移除失败。',
      dateRangeCreateSuccess: '已添加可用日期范围。',
      dateRangeCreateError: '添加可用日期范围失败。',
      dateRangeUpdateSuccess: '已更新可用日期范围。',
      dateRangeUpdateError: '更新可用日期范围失败。',
      dateRangeDeleteSuccess: '已删除可用日期范围。',
      dateRangeDeleteError: '删除可用日期范围失败。',
      dateRangeLoadError: '加载可用日期范围失败。',
    },
  },
  reports: {
    title: '报告',
    subtitle: '分析和报告仪表板',
    comingSoon: '报告即将推出',
    comingSoonDesc: '高级分析和自定义报告将在未来版本中提供。',
  },
  errors: {
    somethingWrong: '出了点问题',
    unexpected: '发生意外错误，请重试。',
    pageNotFound: '页面未找到',
    pageNotFoundDesc: '您查找的页面不存在或已被移动。',
    accessDenied: '访问被拒绝',
    accessDeniedDesc: '您没有权限访问此资源，请联系管理员。',
    backToDashboard: '返回仪表板',
    backToHome: '返回首页',
    publicHint: '请检查您收到的链接，或联系商家获取帮助。',
    applicationError: '应用程序错误',
    reload: '重新加载页面',
  },
  publicPromo: {
    documentTitle: '促销',
    loading: '正在加载促销',
    unavailableTitle: '促销不可用',
    unavailableDesc: '此促销链接缺失、无效或已停用。',
    expiredDesc: '此促销已过期，不再可用。',
    comingSoonDesc: '此促销尚未开放收集回复。',
    formUnavailableTitle: '表单不可用',
    formUnavailableMessage: '此促销表单当前已暂停或不可用。',
    badge: '促销',
    requestTitle: '领取优惠券',
    requestSubtitle: '填写资料以申请此促销。',
    optional: '选填',
    birthDate: '出生日期',
    cap: '邮政编码',
    capPlaceholder: '例如 100000',
    address: '地址',
    addressPlaceholder: '街道和门牌号',
    nationality: '国籍',
    nationalityPlaceholder: '您的国籍',
    requiredNote: '* 必填项',
    submitting: '正在保存…',
    personalInfo: '个人信息',
    phoneLabel: '手机',
    phonePlaceholder: '手机号',
    firstName: '名',
    firstNamePlaceholder: '名',
    lastName: '姓',
    lastNamePlaceholder: '姓',
    email: '邮箱',
    emailPlaceholder: '邮箱',
    consentsTitle: '我同意处理个人数据',
    privacyConsent: '隐私与数据处理',
    marketingConsent: '营销与通信同意',
    profilingConsent: '画像分析同意',
    detailsLink: '详情',
    selectAll: '全选',
    submit: '领取优惠券',
    toast: {
      success: '优惠券生成成功。',
      error: '生成优惠券失败，请重试。',
    },
    thankYou: {
      title: '谢谢！',
      message: '您的请求已成功提交。',
      continue: '继续',
    },
    lookupFound: '我们已找到您的资料并填入表单。',
    welcomeBack: '欢迎回来，{{firstName}}。我们已有您的资料。',
    welcomeBackNoName: '欢迎回来。我们已有您的资料。',
    alreadyHasCoupon: '您已领取过此促销的优惠券。',
    alreadyUsed: '此优惠券已使用。',
    maxCoupons: '此促销已达到优惠券数量上限，无法再生成。',
    lookupError: '暂时无法查询此号码，您仍可以继续填写表单。',
    lookupNotFound: '未找到此促销。',
    lookupInvalid: '请检查手机号后重试。',
    resend: {
      title: '通过 WhatsApp 发送优惠券？',
      message:
        '您已领取过优惠券。我们可以再次通过 WhatsApp 发送，并附上二维码。',
      confirm: '通过 WhatsApp 发送',
      decline: '暂时不用',
      close: '关闭',
      success: '优惠券已发送到您的 WhatsApp。',
      error: '优惠券发送失败，请重试。',
    },
    validation: {
      prefixRequired: '请选择国家区号。',
      phoneMin: '请输入有效的手机号。',
      phoneRange: '请输入 6 到 15 位数字。',
      firstNameRequired: '名为必填项。',
      lastNameRequired: '姓为必填项。',
      emailRequired: '邮箱为必填项。',
      emailInvalid: '请输入有效的邮箱地址。',
      birthDateInvalid: '请输入有效且不是未来的日期。',
      privacyRequired: '必须同意隐私条款。',
    },
  },
  publicFidelity: {
    documentTitle: '会员卡',
    loading: '正在加载会员计划',
    unavailableTitle: '计划不可用',
    unavailableDesc: '请检查收到的链接，或联系餐厅。',
    badge: '您的会员计划',
    cardLabel: '会员卡',
    previewPoints: '每次到店都算数。',
    previewDiscount: '专属于您的优惠。',
    requestTitle: '申请您的卡片',
    requestSubtitle: '创建您的个人卡片并随身携带。',
    optional: '可选',
    requiredNote: '* 必填项',
    submitting: '正在保存…',
    phoneLabel: '手机',
    phonePlaceholder: '您的号码',
    firstName: '名',
    firstNamePlaceholder: '您的名字',
    lastName: '姓',
    lastNamePlaceholder: '您的姓氏',
    email: '邮箱',
    emailPlaceholder: 'name@example.com',
    consentsTitle: '您的偏好',
    privacyConsent: '隐私与数据处理',
    marketingConsent: '营销与沟通同意',
    profilingConsent: '画像同意',
    detailsLink: '详情',
    selectAll: '全选',
    submit: '创建我的卡片',
    successTitle: '您的卡片已准备好。',
    successSubtitle: '保存二维码并向餐厅出示。',
    qrAlt: '您的会员卡二维码',
    openQr: '打开并保存二维码',
    welcomeBack: '欢迎回来，{{firstName}}。我们已有您的资料。',
    welcomeBackNoName: '欢迎回来。我们已有您的资料。',
    lookupError: '无法核实此号码。您仍可继续填写表单。',
    lookupUnavailable: '此计划已不可用。',
    lookupInvalid: '请检查手机号码后重试。',
    requestLink: '重新获取链接',
    requestLinkSending: '正在发送请求…',
    requestLinkSent: '如果此联系方式关联有效卡片，链接将再次发送。',
    requestLinkError: '无法请求链接，请重试。',
    errors: {
      alreadyRegistered:
        '您已拥有此计划的卡片。请使用收到的二维码，或联系餐厅找回。',
      unavailable: '此计划已不可用。',
      generic: '无法创建卡片。请稍后再试，或联系餐厅。',
    },
    validation: {
      prefixRequired: '请选择国家代码。',
      phoneRange: '请输入 6 到 15 位数字。',
      firstNameRequired: '请输入名字。',
      lastNameRequired: '请输入姓氏。',
      emailInvalid: '请检查邮箱地址。',
      privacyRequired: '必须同意隐私条款。',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: '放大图片：{{name}}',
      close: '关闭图片预览',
      error: '无法加载图片。',
    },
    title: '预订',
    welcome: '欢迎来到',
    loading: '正在加载预订表单',
    missingFormCode: 'URL 中缺少表单代码。',
    loadError: '无法加载此预订表单。',
    inactiveTitle: '表单不可用',
    inactiveMessage: '此预订表单当前未启用。',
    invalidFormType: '此链接不是预订表单。',
    steps: {
      date: '日期',
      hours: '时间',
      menu: '菜单',
      extra: '附加',
      details: '详情',
      calendar: '日历',
    },
    eventBanner: {
      countOne: '本月有 {{count}} 场活动',
      count: '本月有 {{count}} 场活动',
      tapHint: '点按查看具体日期',
    },
    menuBanner: {
      countOne: '本月有 {{count}} 个菜单',
      count: '本月有 {{count}} 个菜单',
      tapHint: '点按查看具体日期',
    },
    calendar: {
      legendEvent: '特别活动',
      legendMenu: '可选菜单',
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: '不选菜单的客人数',
    },
    eventSheet: {
      fromTime: '{{time}} 起',
      maxCovers: '最多 {{count}} 位',
      gotIt: '继续',
      continueWithEvent: '继续参加活动',
      showMore: '显示更多',
      showLess: '收起',
      close: '关闭',
    },
    hours: {
      selectedDate: '所选日期',
      guestsLabel: '客人数（不含儿童）',
      childrenLabel: '儿童',
      timeSlotLabel: '可用时段',
      guestOption: '{{count}} 位客人',
      childrenOption: '{{count}} 位儿童',
      overlimit: '超出限额 {{count}}',
      overbooking: '超额预订 {{count}}',
      availabilityFinished: '此时段已无可用名额',
      attentionTitle: '注意',
      attentionMessage: '所选客人数量超过此时段的剩余可预订名额。',
      attentionOk: '确定',
      callOverMaxOption: '超过 {{count}} 位客人请致电预订',
      callOverMaxTitle: '提示',
      callOverMaxMessageBefore: '是否拨打',
      callOverMaxMessageAfter: '预订餐桌？',
      callOverMaxHint: '将打开手机拨号界面以完成通话。',
      callOverMaxCall: '拨打',
      callOverMaxCancel: '取消',
    },
    extra: {
      badge: '附加',
      catalogTitle: '服务目录',
      mandatoryNotice: '必须至少选择一项产品/服务。',
      serviceForGuests: '适用于 {{count}} 位客人的服务/桌位',
      decrease: '减少数量',
      increase: '增加数量',
    },
    menu: {
      badge: '菜单',
      catalogTitle: '菜单目录',
      mandatoryQuantityHint:
        '请为每位客人选择菜单（或“无菜单”）。选择总数必须等于 {{count}}（成人 + 儿童）。',
      decrease: '减少数量',
      increase: '增加数量',
    },
    recap: {
      title: '预订总结',
      time: '时间',
      guests: '客人数',
      children: '儿童',
      selectedServices: '已选服务',
      selectedMenus: '已选菜单',
      total: '总计',
      paymentOnSite: '到店支付',
    },
    details: {
      phoneTitle: '输入您的手机号码以继续',
      welcomeBackTitle: '欢迎回来，{{name}}！',
      welcomeBackTitleGeneric: '欢迎回来！',
      welcomeBackMessage: '我们已有您的资料。您可以继续，或在需要时更新详情。',
      personalInfo: '个人信息',
      consentsTitle: '我同意处理个人数据',
      privacyConsent: '隐私与数据处理',
      marketingConsent: '营销与通信同意',
      commercialConsent: '同意将数据传输给第三方用于商业目的',
      profilingConsent: '画像分析同意',
      detailsLink: '详情',
      selectAll: '全选',
      consentModal: {
        title: '个人信息处理须知',
        subtitle: '（依据欧盟第 679/2016 号条例第 13 条 - GDPR）',
        addressLabel: '地址',
        emailLabel: '电子邮件',
        lastUpdated: '最后更新',
        section1Title: '1. 前言',
        section1P1:
          '本须知由 {{branchName}}（税号与增值税号）提供，注册地址位于 {{branchAddress}}（以下简称“数据控制者”）。（以下简称“数据控制者”），依据欧盟 2016/679 号条例（GDPR）以及经 <b>D.lgs. 101/2018.</b> 修订的 D.lgs. 196/2003（隐私法）。',
        section1P2:
          '本须知适用于数据控制者通过 <b>"Pro20"</b> 平台对经营、预订、所提供服务以及与客户沟通所进行的个人数据处理。',
        section1P3: '本须知面向以下用户：',
        section1Li1: '• 使用所有者网站询价或预订餐位的用户；',
        section1Li2:
          '• 通过个人移动设备或本地设备（如平板电脑）自愿提供数据以使用忠诚计划或免费 Wi-Fi 等服务的用户；',
        section1Li3:
          '• 亲自到店并提供数据以接收新闻通讯、商业优惠及所有者产品与服务促销材料的用户。',
        section2Title: '2. 数据控制者',
        section2Sensitive:
          '敏感数据仅在确有必要时处理，并须经用户同意，同意可通过明确操作体现，例如勾选在线选项（如“我接受”或“我同意”）或签署纸质表格。',
        section3Title: '3. 处理的数据及获取方式',
        section3Intro: '数据通过以下方式获取：',
        section3Li1: '• 通过 PRO20 平台管理预订及所提供服务。',
        section3Li2: '• 通过所有者网站，当用户询价或预订餐位时',
        section3Li3:
          '• 直接在店内，当用户为使用服务（Wi-Fi、忠诚计划等）提供数据时。',
        section4Title: '4. 浏览数据',
        section4Body:
          '若用户与网站或其他数字平台交互，将自动收集浏览数据（例如 IP 地址、设备类型、操作系统、访问页面）。Cookie 及类似工具的使用见所有者网站上的 Cookie 政策。',
        section5Title: '5. 处理目的',
        section51Title: '5.1 主要目的',
        section51Intro: '数据处理用于：',
        section51Li1:
          '✔ 管理所需的预订与服务（快速排队、Wi-Fi、忠诚计划、满意度问卷等）',
        section51Li2: '✔ 履行法律、税务与行政义务',
        section51Li3: '✔ 保护人员与服务免受非法或欺诈行为',
        section51Li4: '✔ 匿名统计分析以改善服务质量',
        section51Li5: '✔ 就预订或其他所请求服务与用户沟通',
        section51Note:
          '📌 提供数据是访问服务所必需的：不提供可能导致无法预订或无法提供服务。',
        section52Title: '5.2 次要目的（营销与促销）：仅在明确同意后方可。',
        section52Intro: '经明确同意后，数据可用于：',
        section52Li1:
          '📢 通过电子邮件、短信或其他数字渠道发送促销信息与个性化优惠',
        section52Li2: '📢 市场调查与客户体验调查',
        section52Li3: '📢 分析消费习惯以提供个性化体验。',
        section52Note: '⚠ 营销同意为可选，可随时通过写信至 {{email}} 撤回。',
        section6Title: '6. 数据接收方',
        section6Intro: '所收集的数据可能告知：',
        section6Li1: '• 所有者经授权的内部人员，用于服务管理。',
        section6Li2:
          '• IT 服务提供商及管理平台（如在线预订、电子邮件营销、网站托管），并遵守保密协议。',
        section6Li3: '• 公共机关，在法律要求或监管义务情形下。',
        section6Note: '📌 数据不会向第三方披露或出售。',
        section7Title: '7. 数据保留',
        section7Intro: '数据将保留用于：',
        section7Li1: '📌 行政与会计目的：履行法定义务所需时间（最长 10 年）。',
        section7Li2:
          '📌 营销目的：直至撤回同意，或在无互动情况下最长 24 个月。',
        section7Li3: '📌 浏览数据：依照 Cookie 政策的规定。',
        section8Title: '8. 用户权利',
        section8Intro: '用户有权：',
        section8Li1: '✅ 访问其个人数据并获取有关处理的信息。',
        section8Li2: '✅ 请求更正或删除个人数据。',
        section8Li3: '✅ 基于合法理由限制或反对处理。',
        section8Li4: '✅ 随时撤回同意，不影响此前处理的合法性。',
        section8Li5: '✅ 向个人数据保护局提出投诉（www.garanteprivacy.it）。',
        section9Title: '9. 同意将数据转让给第三方用于商业目的',
        section9Li1:
          '✅ 我同意第三方为直接营销目的处理我的个人数据，包括将其转让给在出版、技术、旅行、金融等行业运营的商业伙伴。上述主体可通过电子邮件、电话或其他渠道就其自身促销活动联系我。同意为可选，可随时写信至 {{email}} 撤回。',
        section9Contact: '📧 行使权利请联系：{{email}}',
        section10Title: '10. 须知变更',
        section10Body:
          '数据控制者保留更新本须知的权利。必要时将通过网站或电子邮件通知变更。',
      },
    },
    actions: {
      continue: '继续',
      back: '返回',
      proceedToDetails: '前往个人信息',
    },
    validation: {
      date: '请选择日期。',
      hours: '请填写客人数、儿童数和时段。',
      menu: '请检查您的菜单选择。',
      menuMandatory: '请选择菜单，使总数等于成人 + 儿童。',
      extra: '请至少选择一项服务。',
    },
    fieldErrors: {
      firstNameMin: '名字为必填项。',
      firstNameInvalid: '名字只能包含字母。',
      lastNameMin: '姓氏至少需要2个字符。',
      lastNameInvalid: '姓氏只能包含字母。',
      emailRequired: '电子邮件为必填项。',
      emailInvalid: '请输入有效的电子邮件。',
      postalCodeRequired: '邮政编码为必填项。',
      dateOfBirthRequired: '出生日期为必填项。',
      specialRequestsRequired: '特殊要求为必填项。',
      singleChoiceRequired: '请选择一个选项。',
      multiChoiceRequired: '请至少选择一个选项。',
      dateOfBirthInvalid: '请输入有效的出生日期。',
      dateOfBirthFuture: '出生日期不能是未来日期。',
      countryCodeInvalid: '请选择有效的国家代码。',
      phoneRequired: '电话号码为必填项。',
      phoneInvalid: '电话号码只能包含数字。',
      phoneMin: '电话号码至少需要6位数字。',
      phoneMax: '电话号码最多15位数字。',
      privacyRequired: '必须同意隐私条款。',
      cardRequestConsentRequired: '请接受验卡 / 押金条款。',
      bookingTimeInvalid: '请选择有效时间。',
      coverOverlimit: '客人和儿童人数超过此表单的最大容纳人数。',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice: '确认此预订需要为所选时段提供支付卡。',
      alreadyOnFile: '此号码已保存银行卡。确认预订即可使用。',
      acceptValidation: '我接受验卡。此操作不会扣款或冻结任何金额。',
      acceptDeposit:
        '我接受对卡片进行 {{amount}} 的预授权，以及关于收款、取消与未到店的相关条款。',
      acceptPayNow: '我确认支付 {{amount}}，并接受取消、退款与未到店相关条款。',
      policyToggle: '阅读完整支付政策',
      policyTitle: '支付、卡片与取消政策',
      policy: {
        s1Title: '1. 门店身份',
        s1Body:
          '预订与支付由 {{branchName}}（{{branchAddress}}）管理。Stripe 仅作为支付服务提供商。联系方式：{{contact}}。',
        s2Title: '2. 验卡',
        s2Body: '仅验卡时不会扣款或冻结金额。卡片通过 Stripe 验证有效性。',
        s3Title: '3. 预授权 / 押金',
        s3Body:
          '可能临时预授权 {{amount}}（每位客人 {{perGuest}}）。解冻时间取决于发卡银行。',
        s4Title: '4. 预付款',
        s4Body: '如已配置，可按门店预订规则收取预付或押金。',
        s5Title: '5. 取消与修改',
        s5Body:
          '在门店截止时间前可能可免费取消；之后可能按政策扣留或收取费用。',
        s6Title: '6. 未到店与迟到',
        s6Body: '未到店或严重迟到时，门店可按规则扣留或收取费用。',
        s7Title: '7. 退款',
        s7Body: '符合条件的退款将退回原支付方式，时效以门店与支付服务商为准。',
        s8Title: '8. 卡片保存',
        s8Body:
          '卡片数据可能由 Stripe 以令牌形式保存，仅在获得明确同意后复用。PRO20 与门店不保存完整卡号。',
        s9Title: '9. 争议与协助',
        s9Body: '取消、争议或协助请联系门店（{{contact}}）并提供预订编号。',
        version: '版本 {{version}}',
      },
    },
    legacyCard: {
      eyebrow: '直接验证',
      title: '信用卡信息',
      subtitle: '本店此次预订不使用 Stripe。',
      holder: '持卡人姓名',
      number: '卡号',
      month: '月',
      year: '年',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: '卡片已验证 · 尾号 {{last4}}',
      cancel: '取消',
      verify: '验证卡片',
      verifying: '正在验证…',
      confirm: '确认',
      verifySuccess: '卡片验证成功',
      verifyError: '卡片信息无效',
      requiredTitle: '需要卡片',
      required: '必须验证卡片才能完成预订。',
      storeWarning: '预订已创建，但未能保存卡片。请从卡片请求处重试。',
      saved: '卡片已保存 · 尾号 {{last4}}',
      alreadyOnFile: '该顾客已有卡片，无需再次填写。',
      pageLoadError: '无法打开此卡片请求。',
      saveError: '卡片未能保存，请重试。',
      fieldRequired: '必填项',
      fieldInvalid: '值无效',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: '验证您的卡片',
      verifyDescription: '输入卡片信息以确认此预订。现在不会扣款。',
      verifyConfirm: '验证卡片',
      payTitle: '完成支付',
      payDescription: '授权 {{amount}} 以确认此预订。',
      payConfirm: '支付 {{amount}}',
      initializing: '正在准备安全支付…',
      processing: '处理中…',
      initError: '无法启动 Stripe 结账，请重试。',
      confirmError: '支付未能完成，请重试。',
      verifyPending: 'Stripe 尚未确认该卡片，请重试。',
      cardRequired: '必须验证卡片才能完成预订。',
      resumePayment: '完成支付',
      resumeNotice:
        '您的预订正在等待支付。完成支付即可确认，不会创建新的预订。',
    },
    phonePlaceholder: '输入电话号码',
    unknownCustomer: '未知客户',
    blacklist: '黑名单',
    customerDetails: '您的信息',
    bookingDetails: '预订详情',
    firstName: '名',
    lastName: '姓',
    email: '电子邮件',
    phone: '电话',
    postalCode: '邮政编码',
    dateOfBirth: '出生日期',
    guests: '客人数',
    children: '儿童',
    time: '时间',
    selectTime: '选择时间',
    slotNotAvailable: '时段不可用',
    noAvailableTimes: '所选日期没有可用时间。',
    specialRequests: '个人笔记和请求',
    comingWithDogs: '我带狗前来',
    extraServices: '额外服务',
    amenityGuests: '{{count}} 位客人',
    submit: '提交预订',
    submitError: '无法提交您的预订，请重试。',
    existingBooking: {
      title: '请注意，预订已存在',
      date: '日期',
      time: '时间',
      guests: '人数',
      children: '儿童',
      event: '活动',
      menus: '菜单',
      noMenu: '无菜单',
      services: '服务',
      continueQuestion: '是否仍要继续并创建此预订？',
      continue: '继续',
    },
    thankYou: {
      title: '谢谢！',
      message: '我们已收到您的预订请求，将尽快与您联系。',
      summaryTitle: '预订摘要',
      date: '日期',
      time: '时间',
      guests: '客人',
      guestsValue: '{{guests}} 位客人（{{children}} 位儿童）',
      location: '地点',
      name: '姓名',
      phone: '电话',
      eventCode: '活动代码',
      eventName: '活动',
      menus: '菜单',
      makeAnother: '再次预订',
      backHome: '返回首页',
      continue: '继续',
    },
  },
  publicBookingUpdate: {
    title: '修改预订',
    loading: '正在加载预订',
    missingBookingCode: '网址中缺少预订代码。',
    loadError: '无法加载此预订。',
    formLoadError: '无法加载此预订的可用时段。',
    noFormTitle: '无法在线修改',
    noFormMessage: '此预订无法在线修改。请直接联系餐厅。',
    notEditable: '该预订已无法再修改',
    hello: '你好 {{name}}',
    subtitle: '您正在修改在 {{branch}} 的预订',
    currentBadge: '当前：{{time}} · {{date}}',
    currentlyBooked: '当前预订',
    current: {
      title: '您的预订',
      client: '客户',
      date: '当前日期',
      time: '当前时间',
      guests: '人数',
      children: '儿童',
      event: '活动',
      menu: '菜单',
      menuPeople: '人数',
      showDetails: '详情',
      hideDetails: '收起',
    },
    pendingModification: {
      title: '已有修改请求正在等待确认',
      summary: '{{date}} · {{time}} · {{guests}} 人，{{children}} 名儿童',
      date: '日期',
      time: '时间',
      guests: '人数',
      children: '儿童',
    },
    form: {
      title: '更改预订',
      subtitle: '选择新的日期、人数和时间段。',
      selectedDate: '已选日期',
      guestsLabel: '人数（不含儿童）',
      childrenLabel: '儿童',
      timeSlotLabel: '可用时段',
      guestOption: '{{count}} 人',
      childrenOption: '{{count}} 名儿童',
      selectTime: '选择时间',
      noAvailableTimes: '所选日期没有可用时段。',
      notesLabel: '个人备注与要求',
      notesPlaceholder: '为餐厅添加备注…',
      readyToUpdate: '请核对更改，然后点击更新以发送修改请求。',
      calendarUnavailable: '此预订无法显示日历（缺少表单或分店）。',
    },
    actions: {
      update: '修改预订',
      updating: '更新中…',
      delete: '删除',
      cancelReservation: '取消预订',
      cancelling: '正在取消…',
    },
    deleteConfirm: '确定要取消此预订吗？此操作无法撤销。',
    deleteModal: {
      title: '取消预订？',
      description: '确定要取消此预订吗？此操作无法撤销。',
    },
    toast: {
      updateSuccess: '修改请求已发送。',
      updateError: '无法更新预订，请重试。',
      validationError: '请先修正标出的字段再更新。',
      deleteSuccess: '预订已成功删除。',
      deleteError: '无法删除预订，请重试。',
    },
    thankYou: {
      badge: '请求已发送',
      title: '谢谢',
      headerMessage: '我们已收到您的预订修改请求，将尽快回复。',
      statusLabel: '请求已发送',
      awaitingTitle: '修改正在等待确认',
      awaitingMessage: '我们已收到您的预订修改请求，将尽快回复。',
      step1Title: '餐厅审核',
      step1Message: '工作人员将核对可用性与请求详情。',
      step2Title: '自动回复',
      step2Message: '修改被接受或拒绝后，您将收到更新通知。',
      closeHint: '您可以关闭此页面：您的请求已正确登记。',
    },
    result: {
      updatedTitle: '预订已更新',
      updatedMessage: '您的更改已保存。餐厅已收到更新通知。',
      deletedTitle: '预订已删除',
      deletedMessage: '您的预订已取消。期待下次再见。',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: '退出登录',
    login: {
      documentTitle: '后台登录',
      title: '登录后台',
      subtitle: '使用管理员邮箱和密码继续。',
      emailPlaceholder: '邮箱',
      passwordPlaceholder: '密码',
      rememberCredentials: '记住凭据',
      signIn: '登录',
      invalidCredentials: '邮箱或密码无效。',
      adminBadge: '管理员访问',
      authorizedOnly: '仅限授权人员',
      heroTitle: '自信地运营 Pro20 平台',
      heroSubtitle: '安全的后台访问，用于管理餐厅、消息和全平台控制。',
      heroCardTitle: '安全控制中心',
      heroCardSubtitle: '平台运营',
      heroPointOne: '管理餐厅、员工与入驻流程',
      heroPointTwo: '监控消息健康状态与运营告警',
      heroPointThree: '应用全平台设置并保持完整可追溯性',
      trustedBy: '值得信赖的安全运营',
      trustAccessTitle: '受控访问',
      trustAccessDesc: '仅管理员凭据，会话受保护',
      trustAuditTitle: '可审计',
      trustAuditDesc: '关键操作可追踪',
      trustPlatformTitle: '平台规模',
      trustPlatformDesc: '从一个控制台管理多个门店',
      validation: {
        emailRequired: '邮箱为必填项',
        emailInvalid: '请输入有效的邮箱地址',
        emailMax: '邮箱最多 255 个字符',
        passwordMin: '密码至少 8 个字符',
        passwordMax: '密码最多 128 个字符',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: '欢迎使用 Pro20 操作面板。',
    headingWelcomeBack: '欢迎回来，{{name}}',
    headingWelcomeBackGeneric: '欢迎回来',
    actions: {
      announcements: '公告',
      notifications: '通知',
      globalAlertTooltip: '应用全局提醒',
      expiredActivities: '已过期活动',
      expiredActivitiesTooltip: '切换截止日期提醒',
      removeBanner: '移除横幅',
      removeBannerTooltip: '从应用中移除全局横幅',
    },
    releaseMobile: {
      title: '移动端发布',
      currentVersion: '当前版本 {{version}}',
      manageVersion: '管理版本',
      toast: {
        versionUpdated: '应用版本已更新',
        versionUpdateFailed: '无法更新应用版本',
        bannerRemoved: '全局横幅已移除',
        bannerRemoveFailed: '无法移除全局横幅',
      },
    },
    manageVersionModal: {
      title: '更新应用版本',
      loading: '正在加载当前版本…',
      loadError: '无法加载当前版本。',
      major: 'Major',
      majorHint: '重大更改',
      minor: 'Minor',
      minorHint: '新功能',
      bug: 'Bug',
      bugHint: '应用修复',
      patch: 'Patch',
      patchHint: '发布审核',
      formatHint: '发布格式为 Major.Minor.Bug.Patch。',
      update: '更新版本',
    },
    removeBannerModal: {
      title: '移除全局横幅？',
      description: '该消息将不再在应用中显示。',
      confirm: '移除横幅',
      cancel: '取消',
    },
    globalAlertModal: {
      title: '输入新的全局提醒消息',
      confirmation: '确认',
      cancel: '取消',
      toast: {
        success: '全局提醒已更新',
        failed: '无法更新全局提醒',
      },
    },
    createActivityModal: {
      title: '创建分店',
      cancel: '取消',
      create: '创建',
      selectUser: '选择用户',
      selectUserPlaceholder: '请选择用户',
      selectUserSearchPlaceholder: '按姓名或电子邮件搜索用户',
      restaurantName: '餐厅名称',
      email: '电子邮件',
      phone: '电话',
      vatNumber: '增值税号',
      companyName: '公司名称',
      sections: {
        marketing: '营销',
        address: '地址',
        geolocation: '地理定位',
        integration: '集成',
      },
      gtmCode: 'GTM 代码',
      gtmCodeHint: 'Google Tag Manager 代码',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'Pixel 代码仅由数字组成',
      address: '地址',
      postalCode: '邮政编码',
      city: '城市',
      country: '国家',
      latitude: '纬度',
      latitudeHint: '例如 45.464211',
      longitude: '经度',
      longitudeHint: '例如 9.191383',
      ipraticoApiKey: 'iPratico API 密钥',
      ipraticoApiKeyHint: '用于 iPracto 集成的 API 密钥',
      noUsers: '暂无可用用户',
      toast: {
        created: '分店已创建',
        createFailed: '无法创建分店',
      },
      validation: {
        userRequired: '必须选择用户',
        restaurantRequired: '餐厅名称为必填项',
        emailInvalid: '请输入有效的电子邮件地址',
        facebookPixelNumeric: 'Facebook Pixel 只能包含数字',
      },
    },
    editActivity: {
      pageTitle: '编辑活动',
      breadcrumbRoot: 'backoffice',
      close: '关闭',
      back: '返回活动列表',
      notFound: '未找到活动。',
      update: '更新',
      enable: '启用',
      disabled: '已禁用',
      smsRemaining: '剩余短信',
      addSms: '添加短信',
      editSms: '编辑剩余短信',
      configureSmsModal: {
        title: '更新剩余短信数量',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: '配置短信',
      },
      aiCreditsRemaining: 'AI 额度',
      editAiCredits: '编辑 AI 额度',
      configureAiCreditsModal: {
        title: '更新 AI 额度余额',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: '配置 AI 额度',
      },
      emailSetup: '邮件设置',
      editEmailConfig: '编辑邮件配置',
      restoreDefaultEmailConfig: '恢复默认配置',
      emailFields: {
        mailResidue: '邮件余量',
        customerEmail: '客户邮箱',
        fromAddress: '发件地址',
        password: '密码',
        smtpServer: 'SMTP 服务器',
        smtpPort: 'SMTP 端口',
      },
      configureEmailModal: {
        title: '更新剩余邮件数量',
        mailResidue: '邮件余量',
        customerEmail: '用于个性化消息的客户邮箱',
        fromAddress: '发件地址',
        password: '生成的密码',
        smtpServer: 'SMTP 服务器',
        smtpPort: 'SMTP 端口',
        required: '此字段为必填项',
        save: '保存配置',
      },
      sections: {
        general: '基本信息',
        subscription: '订阅',
        address: '地址',
        coordinates: '坐标',
        marketing: '营销',
        integrations: '集成',
        communications: '通讯',
      },
      restaurantName: '分店名称',
      branchNameOnGoogle: '品牌名称（Google）',
      email: '电子邮件',
      capacity: '场地容量',
      vatNumber: '增值税号',
      companyName: '公司名称',
      phone: '手机',
      url: 'URL',
      deadline: '到期日期',
      address: '地址',
      postalCode: '邮政编码',
      city: '城市',
      province: '省 / 地区',
      region: '区域',
      country: '国家',
      longitude: '经度',
      latitude: '纬度',
      gtmCode: 'GTM 代码',
      facebookPixel: 'FB Pixel 代码',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: '输入密钥',
      menuRestaurant: '餐厅菜单',
      menuRestaurantDesc: '为此分店启用数字餐厅菜单。',
      officialApi: '官方 API',
      officialApiDesc: '为此分店启用官方 WhatsApp API。',
      googleReservation: 'Google Reservation',
      googleReservationDesc: '为此分店启用 Google Reservation。',
      supplierOrders: '供应商订单',
      supplierOrdersDesc: '为此分店启用供应商订单。',
      deliveryTakeaway: '外卖 / 自取',
      deliveryTakeawayDesc: '为此分店启用外卖和自取。需要先启用餐厅菜单。',
      automation: '自动化',
      automationDesc: '为此分店启用自动化。',
      statusEnabled: '已启用',
      statusDisabled: '已关闭',
      sectionHints: {
        general: '分店名称、联系方式以及可接待的客人数。',
        subscription: '此分店访问权限的到期时间。',
        address: '顾客和合作方找到此分店的地址。',
        coordinates: '公开列表使用的地图位置。',
        marketing: '此分店网站的跟踪代码。',
        integrations: '连接到此分店的产品和合作服务。',
        communications: '短信余额、AI 额度以及发给顾客的邮箱。',
      },
      publicApi: {
        title: '公开 REST API',
        description:
          '公开此分店的预订历史。调用方通过 X-API-KEY 请求头进行认证。',
        configure: '配置',
        statusConfigured: '已配置',
        statusMissing: '未配置',
        notConfigured: '尚无 X-API-KEY。配置此分店即可在服务器上创建。',
        loadFailed: '无法加载 X-API-KEY。',
        retry: '重试',
        configureFailed: '无法创建 X-API-KEY',
        configured: '已创建 X-API-KEY',
        deleteConfiguration: '删除配置',
        deleteConfirmTitle: '删除 REST API 配置？',
        deleteConfirmDescription:
          '这将移除该门店的 X-API-KEY。现有调用方将无法再使用它。',
        deleted: '已删除 REST API 配置',
        deleteFailed: '无法删除 REST API 配置',
        keyLabel: 'X-API-KEY',
        keyHint: '每次查询预订历史时，都要在 X-API-KEY 请求头中发送此值。',
        showKey: '显示密钥',
        hideKey: '隐藏密钥',
        copyKey: '复制密钥',
        copiedKey: '已复制 X-API-KEY',
        copyFailed: '无法复制到剪贴板',
        bookingHistory: '预订历史',
        sampleRequest: '示例请求',
        sampleResponse: '示例响应',
        copySample: '复制示例',
        copiedSample: '已复制示例',
        filtersHint: '每个参数单独列出。端点会随所选过滤器更新。',
        copyFilters: '复制全部过滤器',
        showFilters: '显示过滤器',
        hideFilters: '隐藏过滤器',
        copiedFilters: '已复制过滤器',
        endpointLabel: '端点',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: '分店已更新',
        updateFailed: '无法更新分店',
        featureUpdated: '功能已更新',
        featureUpdateFailed: '无法更新功能',
        deliveryRequiresMenu: '启用外卖/自取前请先开启餐厅菜单。',
        smsUpdated: '短信数量已更新',
        smsUpdateFailed: '无法更新短信数量',
        aiCreditsUpdated: 'AI 额度已更新',
        aiCreditsUpdateFailed: '无法更新 AI 额度',
        emailUpdated: '邮件配置已保存',
        emailRestored: '邮件配置已恢复',
      },
      validation: {
        restaurantRequired: '分店名称为必填项',
        emailInvalid: '请输入有效的电子邮件地址',
        urlInvalid: '请输入有效链接',
        facebookPixelNumeric: 'Facebook Pixel 只能包含数字',
        capacityInvalid: '请输入有效的容量',
        capacityInteger: '容量必须为整数',
        capacityMin: '容量至少为 0',
      },
    },
    activities: {
      title: '分店',
      count: '{{count}} 个分店',
      create: '创建分店',
      loadError: '无法加载分店，请重试。',
      expiringCaption: '即将到期',
      expiringAria: '{{month}} 有 {{count}} 个分店到期',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: '安全',
    },
    security: {
      title: '修改密码',
      subtitle: '更新您的后台账户密码。',
      password: '密码',
      confirmPassword: '确认密码',
      submit: '更新密码',
      validation: {
        required: '密码为必填项',
        minLength: '密码至少需要 6 个字符',
        confirmRequired: '请确认密码',
        mismatch: '两次输入的密码不一致',
      },
      toast: {
        success: '密码已成功更新',
        failed: '无法更新密码，请重试。',
      },
    },
    users: {
      title: '用户',
      count: '{{count}} 位用户',
      create: '创建用户',
      searchPlaceholder: '搜索用户..',
      loadError: '无法加载用户，请重试。',
      table: {
        userCode: 'UserCode',
        name: '姓名',
        email: '邮箱',
        phone: '电话',
        activity: '活动',
        visibleOnApp: '在应用中可见',
      },
      tooltips: {
        linkActivity: '将活动关联到用户',
        editUser: '编辑用户',
        goToBackoffice: '前往用户后台',
        deleteUser: '删除用户',
      },
      fields: {
        name: '姓名',
        email: '邮箱',
        phone: '电话',
        password: '密码',
        confirmPassword: '确认密码',
      },
      createModal: {
        title: '创建用户',
        cancel: '取消',
        create: '创建',
      },
      editModal: {
        title: '编辑用户',
        cancel: '取消',
        update: '更新',
      },
      linkActivityModal: {
        title: '选择活动',
        searchLabel: '搜索活动',
        searchPlaceholder: '按名称或代码搜索',
        availableTitle: '可用活动',
        cancel: '取消',
        confirm: '确认',
      },
      relatedActivitiesModal: {
        title: '关联活动',
        name: '名称',
        code: '代码',
        expiration: '到期时间',
        unlink: '取消关联活动',
        close: '关闭',
      },
      unlinkModal: {
        title: '取消关联活动？',
        description: '确定要取消“{{name}}”与该用户的关联吗？此操作无法撤销。',
      },
      deleteModal: {
        title: '删除用户？',
        description: '确定要删除“{{name}}”吗？此操作无法撤销。',
      },
      validation: {
        nameRequired: '姓名为必填项',
        emailRequired: '邮箱为必填项',
        emailInvalid: '请输入有效的邮箱地址',
        passwordMin: '密码至少需要 6 个字符',
        passwordMismatch: '两次输入的密码不一致',
      },
      toast: {
        created: '用户已创建',
        createFailed: '无法创建用户',
        updated: '用户已更新',
        updateFailed: '无法更新用户',
        deleted: '用户已删除',
        deleteFailed: '无法删除用户',
        loadFailed: '无法加载用户详情',
        activitiesLinked: '活动已关联到用户',
        activitiesLinkFailed: '无法关联活动',
        activityUnlinked: '活动已取消关联',
        activityUnlinkFailed: '无法取消关联活动',
        noAssociatedActivity: '暂无关联活动',
        visibilityUpdated: '用户可见性已更新',
        visibilityUpdateFailed: '无法更新用户可见性',
      },
    },
    reports: {
      badge: '集成监控',
      title: 'iPratico 错误',
      subtitle: '查看发送到 iPratico 期间记录的请求、响应和异常。',
      errorsFound: '发现错误',
      actions: {
        update: '更新',
        reset: '重置',
        filter: '筛选',
      },
      filters: {
        branchCode: '分店代码',
        operation: '操作',
        httpStatus: 'HTTP 状态',
        fromDay: '起始日期',
        toDay: '结束日期',
        allStatuses: '全部状态',
        status400: '400 — 无效请求',
        status401: '401 — 未授权',
        status403: '403 — 拒绝访问',
        status404: '404 — 资源未找到',
        status409: '409 — 冲突',
        status429: '429 — 请求过多',
        status500: '500 — iPratico 错误',
        status502: '502 — 网关错误',
        status503: '503 — 服务不可用',
      },
      table: {
        date: '日期',
        branch: '分店',
        operation: '操作',
        status: '状态',
        endpoint: 'Endpoint',
        mistake: '错误',
      },
      detail: {
        title: '错误详情',
        branch: '分店',
        branchName: '分店名称',
        branchCode: '分店代码',
        date: '日期',
        method: '方法',
        status: '状态',
        apiKey: '使用的 X-API-KEY',
        endpoint: 'Endpoint',
        message: '消息',
        requestPayload: '请求 Payload',
        responsePayload: 'iPratico 响应',
      },
      tooltips: {
        openDetail: '打开详情',
        closeDetail: '关闭详情',
        copyPayload: '复制 Payload',
      },
      toast: {
        updated: '报告已更新',
        copied: '已复制到剪贴板',
        loadFailed: '无法加载 iPratico 错误',
      },
      loadError: '无法加载 iPratico 错误，请重试。',
    },
    searchPlaceholder: '按名称、代码、公司名称或增值税号搜索',
    filters: {
      features: {
        label: '功能',
        google: 'Google 预订',
        menu: '餐厅菜单',
        ipraticoKey: 'iPratico 密钥',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: '分店名称',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: '到期',
      expiredOn: '已于 {{date}} 过期',
      alertOnApp: '应用内提醒',
      companyName: '公司名称',
      noCompanyName: '无公司名称',
      delete: '删除分店',
      settings: '更新分店',
      chat: '监控消息',
      send: '进入后台',
      tooltips: {
        whatsappBusiness: '已启用 WhatsApp Business',
        menu: '已启用菜单',
        google: '已启用 Google 预订',
        ipraticoKey: 'iPratico 密钥',
        sms: '剩余短信：{{count}}',
        aiCredits: 'AI 额度：{{count}}',
        loadError: '无法加载此分店的用户',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect 已启用 — 查看或移除配置',
        pending: '完成、继续或移除 Stripe 入驻',
        unlinked: '将分店关联到 Stripe Connect',
      },
      link: {
        title: 'Stripe 入驻链接',
        body: '{{branch}} 的链接已生成。',
        warning: '这是一次性的临时 Stripe 链接，请尽快分享或使用。',
        openLink: '打开链接',
        copyLink: '复制链接',
        linkCopied: '链接已复制',
        copyFailed: '复制失败 — 请手动选择并复制链接。',
      },
      connected: {
        title: 'Stripe Connect 已启用',
        body: '{{branch}} 已完成关联，可以接收付款和结算。',
      },
      pending: {
        title: 'Stripe 审核中',
        body: 'Stripe 仍需对 {{branch}} 完成部分审核。',
        resume: '继续入驻',
      },
      error: {
        title: 'Stripe 配置失败',
        fallback: '请检查 Stripe 配置后重试。',
      },
      remove: {
        action: '移除 Stripe 配置',
        confirmTitle: '移除 Stripe 配置？',
        confirmBody:
          '要断开 {{branch}} 的 Stripe 连接吗？在重新关联之前，该分店将无法接受 Stripe 付款。',
        success: '已移除 Stripe 配置',
        failed: '无法移除 Stripe 配置',
      },
      close: '关闭',
    },
    accessUserModal: {
      title: '选择要用于访问后台的用户',
      name: '姓名',
      email: '电子邮件',
      close: '关闭',
      loadError: '无法加载此分店的用户',
    },
    signInAsUser: {
      success: '已以所选用户身份登录',
      failed: '无法以该用户身份登录',
      missingIdentity: '需要用户代码和电子邮件',
    },
    expiringBranchesModal: {
      title: '{{month}} 即将到期的分店',
      name: '名称',
      userCode: '代码',
      email: '邮箱',
      expires: '到期日',
      close: '关闭',
      empty: '本月没有即将到期的分店。',
      openDetails: '打开分店详情',
    },
    deleteActivityModal: {
      title: '确认删除分店？',
      description: '请在此输入分店代码 ({{branchCode}}) 以继续删除',
      branchCodePlaceholder: '分店代码',
      eliminate: '删除',
      cancel: '取消',
      deleted: '分店已删除',
      deleteFailed: '无法删除分店',
    },
    access: {
      title: 'Backoffice 访问',
      description: '请输入数字代码以访问 Pro20 操作面板。',
      codePlaceholder: '',
      enter: '进入',
    },
    validation: {
      codeRequired: '访问代码为必填项',
      codeNumeric: '访问代码只能包含数字',
      invalidCode: '访问代码无效',
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle: '检查用于 API 测试的普通与定时提交。',
      close: '关闭',
      reset: '重置',
      update: '更新',
      stats: {
        posted: '本页已发送',
        errors: '本页错误',
        inProgress: '本页进行中',
      },
      filters: {
        origin: '来源',
        state: '状态',
        channel: '渠道',
        reference: '参考',
        referencePlaceholder: '参考',
        originEveryone: '全部',
        originNormal: '普通',
        originScheduled: '定时',
        originBulk: '批量',
        stateEveryone: '全部',
        stateSent: '已发送',
        stateErrors: '错误',
        statePending: '待处理',
        stateInQueue: 'In coda',
        stateInProgress: '进行中',
        channelEveryone: '全部',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'Email',
        channelSms: 'SMS',
      },
      eventsFound: '找到 {{count}} 个事件',
      empty: '未找到符合所选筛选条件的消息。',
      loadError: '无法加载消息事件。请重试。',
      actions: {
        resubmit: '重新发送消息',
      },
      toast: {
        resubmitted: '消息已重新发送',
        resubmitFailed: '无法重新发送消息',
      },
      table: {
        date: 'Data',
        type: '类型',
        recipient: '收件人',
        reference: '参考',
        message: '消息',
        channel: '渠道',
        state: '状态',
        attempts: '尝试次数',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: '详情',
      },
    },
  },
  homeLanding: {
    documentTitle: 'PRO20 — 餐厅预订与桌位管理系统',
    login: '登录',
    bookDemo: '预约演示',
    startFreeTrial: '申请免费试用',
    learnMore: '了解更多',
    nav: {
      home: '首页',
      product: '产品',
      solutions: '解决方案',
      industries: '行业',
      integrations: '集成',
      pricing: '价格',
      resources: '资源',
      why: '为何选择 PRO20',
      apps: '应用',
      howItWorks: '如何运作',
      reviews: '客户评价',
      faq: '常见问题',
      about: '关于我们',
    },
    hero: {
      partnerBadge: 'Google Reservation C2C 合作伙伴',
      metaPartnerBadge: '官方 Meta 技术提供商',
      titleBefore: '一站式平台，专为',
      titleHighlight: '餐厅',
      titleAfter: '桌位预订打造。',
      subtitle:
        '管理预订、平面图、员工、营销、支付并取悦顾客 — 尽在一个强大平台。',
      checkGoogle: 'Google 预订',
      checkWhatsapp: 'WhatsApp 官方 API',
      checkFloor: '平面图与桌位映射',
      checkMarketing: '营销与会员',
      checkPayments: '支付与订金',
      checkApps: 'iOS 与 Android 应用',
      trustedBy: '已获意大利 10+ 城市 100+ 餐厅信赖。',
      previewAlt: 'PRO20 餐厅预订仪表盘预览',
      chatAria: '打开聊天',
    },
    trust: {
      heading: '已获 100+ 餐厅与场地品牌信赖',
      subtitle: '独立餐厅与多门店集团每天都在使用 PRO20 填满桌位。',
    },
    stats: {
      title: '全球餐厅团队的共同选择',
      subtitle: '覆盖餐厅、咖啡馆、海滩俱乐部与酒吧的真实规模。',
      restaurantsValue: '100+',
      restaurantsLabel: '餐厅',
      countriesValue: '10+',
      countriesLabel: '意大利城市',
      bookingsValue: '2M+',
      bookingsLabel: '已管理预订',
      uptimeValue: '99.9%',
      uptimeLabel: '正常运行时间',
    },
    features: {
      title: '运营预订所需的一切',
      subtitle: '从预订与平面图，到营销、会员与集成 — 一个平台搞定。',
      reservationsTitle: '智能预订',
      reservationsDesc: '实时接受、管理并优化各渠道预订。',
      floorTitle: '平面图与桌位',
      floorDesc: '可视化平面图与实时桌位状态，服务更顺畅。',
      bookingLinkTitle: '公开预订链接',
      bookingLinkDesc:
        '创建预订表单，并将公开链接分享到网站、二维码、WhatsApp 或社交渠道，让客人在线订桌。',
      marketingTitle: '营销与活动',
      marketingDesc: '通过定向优惠、提醒与活动触达客人。',
      loyaltyTitle: '会员与促销',
      loyaltyDesc: '用灵活促销奖励常客并提升复访。',
      reportsTitle: '报表与洞察',
      reportsDesc: '通过清晰仪表盘跟踪上座率、营收与客人趋势。',
    },
    why: {
      title: '为何选择 PRO20？',
      subtitle: '专为餐厅与酒吧打造 — 不是通用预订小部件。',
      allInOneTitle: '一站式平台',
      allInOneDesc: '预订、平面图、营销、会员与支付集于一处。',
      channelsTitle: '原生 Google 与 WhatsApp',
      channelsDesc: '在客人已经预订和沟通的地方与之相遇。',
      hospitalityTitle: '为前厅运营而生',
      hospitalityDesc: '平面图、员工流程与实时服务工具，贴合前厅节奏。',
      appsTitle: '覆盖各类设备的应用',
      appsDesc: 'Android 与 iOS 原生手机和 iPad 应用，与网页后台同步。',
      fidelityTitle: '会员与忠诚计划',
      fidelityDesc: '用积分、会员卡和促销奖励常客 — 在一个计划中提升复访。',
      supportTitle: '7×24 小时支持',
      supportDesc: '全天候为团队提供帮助 — 白天或夜晚，一周七天随时响应。',
    },
    industries: {
      title: '适合各类餐饮场所',
      subtitle: '从精致餐饮到海滩俱乐部 — PRO20 适配你接待客人的方式。',
      restaurants: '餐厅',
      cafes: '咖啡馆',
      beachClubs: '海滩俱乐部',
      bars: '酒吧与酒馆',
      rooftops: '露台酒吧',
      resorts: '度假村',
    },
    integrations: {
      title: '强大集成',
      subtitle: '连接你已在使用的工具 — 预订、消息、POS 与支付。',
      google: 'Google 预订',
      whatsapp: 'WhatsApp 官方 API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Stripe 支付',
      meta: 'Meta 技术合作伙伴',
    },
    engage: {
      title: '沟通。互动。取悦。',
      subtitle: '通过 WhatsApp 官方 API 与自动消息，即时连接客人。',
      confirmations: '预订确认',
      reminders: '提醒与通知',
      offers: '优惠与促销',
      feedback: '反馈与评价',
      chatGuest: '您好！您的 2 人桌已确认，时间为晚上 8:00。',
      chatModify: '修改预订',
      chatCancel: '取消预订',
      chatDirections: '获取路线',
    },
    apps: {
      title: '用手机或 iPad 运营门店',
      subtitle:
        '面向手机与 iPad 的原生 PRO20 应用 — 支持 Android 与 iOS，让团队在前厅与外出时保持同步。',
      phoneTablet: '手机与 iPad',
      androidIos: 'Android 与 iOS',
      floorPlan: '在 iPad 上完整控制平面图与桌位',
      onTheGo: '随时随地用手机管理预订',
      sync: '与网页后台相同的实时数据',
      appStore: '在 App Store 下载',
      googlePlay: '在 Google Play 获取',
    },
    about: {
      title: '关于我们',
      subtitle: '我们帮助餐厅品牌把每一张桌子变成更好的客人体验。',
      body: 'PRO20 是备受场所信赖的一站式餐厅桌位预订平台。从独立餐厅到多门店集团，我们将预订、平面图、客人消息与营销合为一体，让经营者专注于服务体验 — 而不是在工具之间疲于奔命。',
      missionTitle: '我们的使命',
      missionBody: '让世界级的预订运营对每个场所都简单、互联且愉悦。',
      pillarGuests: '客人优先',
      pillarOperators: '赋能经营者',
      pillarConnected: '始终互联',
      imageAlt: '餐厅团队工作中',
    },
    howItWorks: {
      title: '六个简单步骤即可开始',
      subtitle: '从设置到满意的客人，管理餐厅运营所需的一切。',
      steps: {
        s1: {
          title: '创建你的餐厅',
          desc: '几分钟内设置菜系、位置与品牌资料。',
        },
        s2: {
          title: '设计楼层与桌位',
          desc: '用拖放式平面图规划师搭建楼层，并按你的方式组织桌位。',
        },
        s3: {
          title: '配置预订规则',
          desc: '定义时段、人数、订金与符合你服务的政策。',
        },
        s4: {
          title: '分享并接收预订',
          desc: '将预订链接发布到网站、WhatsApp、社交渠道或二维码。',
        },
        s5: {
          title: '管理与增长',
          desc: '在预订到来时，从前厅、员工到营销统一管理。',
        },
        s6: {
          title: '分析与优化',
          desc: '通过清晰的上座率、营收与客人趋势报表改进每一次服务。',
        },
      },
      mocks: {
        createTitle: '创建你的餐厅',
        name: '餐厅名称',
        cuisine: '菜系类型',
        location: '位置',
        continue: '继续',
        floorTitle: '楼层规划',
        rulesTitle: '预订设置',
        timeSlots: '时段',
        bookingRules: '预订规则',
        advancePayment: '预付款',
        shareTitle: '分享预订链接',
        manageTitle: '今日概览',
        reservations: '预订',
        occupied: '已占桌位',
        walkIns: '散客',
        revenue: '营收',
        reportsTitle: '报表概览',
        totalReservations: '总预订',
        occupancy: '上座率',
      },
      ctaText: '准备好开始了吗？加入已在使用 PRO20 成长的数千家餐厅。',
      bookDemo: '预约演示',
      contactSales: '联系销售',
    },
    faq: {
      title: '常见问题',
      subtitle: '关于 PRO20 多门店餐厅预订软件的快速解答。',
      viewAll: '查看全部常见问题',
      q1: '我可以分享公开预订链接吗？',
      a1: '可以。创建预订表单，并将公开 URL 分享到网站、二维码、WhatsApp 或社交渠道，让客人在线订桌。',
      q2: '是否支持多家餐厅？',
      a2: '支持。多门店集团可通过 PRO20 后台集中管理场所。',
      q3: '可以连接 WhatsApp 吗？',
      a3: '可以。PRO20 支持 WhatsApp Business 与 Meta 官方 API，用于确认、提醒与营销活动。',
      q4: '有哪些可用集成？',
      a4: 'Google 预订、WhatsApp 官方 API、POS 系统、Stripe、Razorpay 等更多。',
      q5: '有移动应用吗？',
      a5: '有。Android 与 iOS 的原生手机和 iPad 应用，与网页平台保持同步。',
      q6: '如何开始？',
      a6: '预约演示或开始免费试用 — 我们将协助你设置楼层、桌位与公开预订链接。',
    },
    reviews: {
      title: '不必只听我们说',
      subtitle:
        '意大利 10+ 城市的餐厅已在使用 PRO20 管理预订、填满桌位并取悦客人。准备好了解热议的原因了吗？',
      reassurance:
        '（真的。没有隐藏条款。没有销售压力。只有扎实的软件，以及懂餐厅的团队。）',
      trustLabel: '来自数百条评价',
      r1: {
        quote:
          'PRO20 取代了我们以前用的三个工具。预订和 WhatsApp 终于在一个地方。',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote: '设置很快，桌位规划正是高峰时段前厅员工需要的。',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote: '季节性容量和客人消息曾经一团糟。现在可预期且专业。',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          '公开预订链接填满了我们以前空着的晚间时段。客人自行预订 — 我们只需确认。',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: '准备好让餐厅更上一层楼了吗？',
      body: 'PRO20 旨在简化日常运营、增加预订，并减轻管理繁忙场所的压力。',
      closing: '今天就开始使用 PRO20，亲身体验不同。',
    },
    cta: {
      heading: '准备好改变你的餐厅了吗？',
      subtitle: '加入数千家与 PRO20 一起成长的餐厅。',
    },
    footer: {
      blurb: '一站式餐厅预订软件，覆盖桌位预订、客人互动与场所运营。',
      product: '产品',
      productReservations: '预订',
      productFloor: '平面图',
      productMarketing: '营销',
      productLoyalty: '会员',
      productReports: '报表',
      productIntegrations: '集成',
      solutions: '解决方案',
      solutionsRestaurants: '适用于餐厅',
      solutionsCafes: '适用于咖啡馆',
      solutionsBeach: '适用于海滩俱乐部',
      solutionsBars: '适用于酒吧',
      solutionsChains: '适用于连锁',
      resources: '资源',
      resourcesBlog: '博客',
      resourcesGuides: '指南',
      resourcesHelp: '帮助中心',
      resourcesCases: '案例研究',
      resourcesApi: 'API 文档',
      resourcesApps: '移动应用',
      company: '公司',
      companyAbout: '关于我们',
      companyCareers: '招聘',
      companyContact: '联系我们',
      companyPartners: '合作伙伴',
      legal: '法律',
      legalPrivacy: '隐私政策',
      legalTerms: '服务条款',
      legalCookies: 'Cookie 政策',
      copyright: '© {{year}} PRO20。保留所有权利。',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: '常见问题 — PRO20 餐厅预订软件',
    title: '常见问题',
    subtitle: '快速了解关于 PRO20 餐厅预订软件最常见的问题。',
    searchPlaceholder: '搜索你的问题...',
    emptyResults: '没有匹配的问题。请尝试其他关键词或分类。',
    categories: {
      all: '全部问题',
      reservations: '预订',
      whatsapp: 'WhatsApp',
      payments: '支付',
      reports: '报表',
      staff: '员工与用户',
      integrations: '集成',
      general: '通用',
    },
    promo: {
      badge: '一体化平台',
      titleBefore: '你需要的一切，用于',
      titleAccent: '管理与增长',
      titleAfter: '你的餐厅',
      body: '从预订到营销，从 WhatsApp 自动化到报表——PRO20 助您更聪明地运营业务。',
      restaurantsValue: '10,000+',
      restaurantsLabel: '餐厅',
      bookingsValue: '2M+',
      bookingsLabel: '预订',
      countriesValue: '10+',
      countriesLabel: '意大利城市',
      uptimeValue: '99.9%',
      uptimeLabel: '正常运行时间',
    },
    items: {
      q1: '我可以分享预订链接吗？',
      a1: '可以。创建预订表单，并将公开 URL 分享到网站、二维码、WhatsApp 或社交渠道，让客人在线订桌。',
      q1Tag1: '公开链接',
      q1Tag2: '二维码',
      q1Tag3: 'WhatsApp',
      q1Tag4: '社交媒体',
      q1Tag5: '网站',
      q2: 'PRO20 是否支持多家餐厅或多门店？',
      a2: '支持。多门店集团可通过 PRO20 后台集中管理场所，并共享控制与报表。',
      q3: '我可以连接 WhatsApp Official 并发送消息吗？',
      a3: '可以。PRO20 支持 WhatsApp Business 与 Meta 官方 API，用于确认、提醒、营销与客人消息。',
      q4: '支持哪些支付网关？',
      a4: 'PRO20 与 Stripe 及其他支付提供商集成，可收取订金并减少未到店。',
      q5: '是否有移动应用？',
      a5: '有。Android 与 iOS 的手机和平板原生应用与网页平台保持同步。',
      q6: '支持哪些语言？',
      a6: 'PRO20 为运营方与客人提供多语言支持，包括英语、意大利语、西班牙语、法语、荷兰语和中文。',
      q7: '我可以管理员工及其角色吗？',
      a7: '可以。分配角色与权限，让团队只看到所需内容——从前厅到多门店经理。',
      q8: '我可以管理营销活动与优惠码吗？',
      a8: '可以。在 PRO20 中开展活动、优惠与优惠码，填补清淡时段并奖励常客。',
      q9: '我能获得哪些报表？',
      a9: '通过面向餐饮运营的仪表盘跟踪上座率、预订、营收趋势与客人活动。',
      q10: '我的数据有多安全？',
      a10: '我们采用行业标准安全实践保护餐厅与客人数据，并提供可靠的正常运行时间。',
    },
    cta: {
      eyebrow: '还有问题？',
      title: '我们随时为您服务！',
      body: '与我们的餐厅专家交流，了解 PRO20 如何改变您的业务。',
      bookDemo: '预约演示',
      contactSales: '联系销售',
      startTrial: '或申请免费试用',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: '隐私政策 — PRO20',
      loading: '正在加载隐私政策…',
      loadError: '无法加载控制方详情。政策将以有限信息显示。',
    },
    privacyPolicy: {
      documentTitle: '隐私声明 | Proventi',
      title: '个人数据处理说明',
      subtitle: '根据《欧盟第 2016/679 号条例》（GDPR）第 13 条和第 14 条',
      contents: '本页内容',
      body: privacyPolicyBodyZh,
    },
    terms: {
      documentTitle: '服务条款 — PRO20',
      title: '服务条款',
      subtitle: '这些条款即将更新。',
      body: 'PRO20 服务条款正在准备中。请稍后再查看，或在此期间如需副本请联系我们。',
    },
  },
};
